import React, { useState, useEffect, useRef } from 'react';
import CreatableSelect from 'react-select/creatable';
import stateCityData from '../../data/state_city.json';
import './RPAWizardModal.css';
import { auth } from '../../firebase/config';
import { API_ENDPOINTS, apiClient } from '../../config/api';

// ── Step icons (Feather-style inline SVG) ─────────────────────────────────────
const IcoHome     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const IcoUsers    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IcoDollar   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
const IcoCard     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>;
const IcoBanknote = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/><path d="M6 12h.01M18 12h.01"/></svg>;
const IcoShield   = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
const IcoList     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>;
const IcoScale    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="3" x2="12" y2="21"/><path d="M3 7l9-4 9 4"/><path d="M5 12l-2 7h8z"/><path d="M19 12l2 7h-8z"/></svg>;
const IcoFile     = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
const IcoCheck    = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>;

const IcoPen = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>;

const STEPS = [
  { id: 1,  title: 'Offer & Property',  icon: <IcoHome /> },
  { id: 2,  title: 'Agency',            icon: <IcoUsers /> },
  { id: 3,  title: 'Price & EMD',       icon: <IcoDollar /> },
  { id: 4,  title: 'Financing',         icon: <IcoCard /> },
  { id: 5,  title: 'Seller Payment',    icon: <IcoBanknote /> },
  { id: 6,  title: 'Contingencies',     icon: <IcoShield /> },
  { id: 7,  title: 'Items Included',    icon: <IcoList /> },
  { id: 8,  title: 'Costs & Terms',     icon: <IcoScale /> },
  { id: 9,  title: 'Addenda',           icon: <IcoFile /> },
  { id: 10, title: 'Offer & Brokers',   icon: <IcoPen /> },
  { id: 11, title: 'Review & Sign',     icon: <IcoCheck /> },
];

// ── Initial form state — keys ARE DocuSign tabLabels (identity mapping via rpa-mapping.json)
// _ui.* keys are UI-only state; they are silently skipped by the backend mapping layer.
const INITIAL = {

  // ── Section 1: Offer ──────────────────────────────────────────────────────
  'room.offer.presentedofferdate':       '',
  'room.offer.contractDate':             '',
  'room.details.buyer1.name':            '',
  'room.details.buyer2.name':            '',
  'room.details.seller1.name':           '',
  'room.details.seller2.name':           '',
  'room.property.location.address1':     '',
  'room.property.location.address2':     '',
  'room.property.location.city':         '',
  'room.property.location.state':        'CA',
  'room.property.location.county':       '',
  'room.property.location.postalCode':   '',
  'room.property.ParcelID':              '',

  // ── Section 2: Agency — Seller side ──────────────────────────────────────
  'room.details.listingAgent1.company':            '',
  'room.details.sellerBrokerage.LicenseNum':       '',
  'additionalFields.SellerBrokerageType':           'Seller',
  'room.details.listingAgent1.name':               '',
  'room.details.designatedsellerAgent.licenseNum': '',
  'additionalFields.AgentType':                    "Seller's Agent",

  // ── Section 2: Agency — Buyer side ───────────────────────────────────────
  'room.details.buyerAgent1.company':             '',
  'room.details.buyerBrokerage.licenseNum':        '',
  'additionalFields.BuyerBrokerageType':           'Buyer',
  'room.details.buyerAgent1.name':                '',
  'room.details.designatedbuyerAgent.licenseNum': '',
  'additionalFields.AgentType2':                  "the Buyer's Agent",

  // ── Section 2: Additional disclosures ────────────────────────────────────
  'additionalFields.MultipleRepresentationWithDesignatedAgency': false,
  'additionalFields.SellerorBuyerRadio':                         'Seller',

  // ── Section 3 Row A: Purchase price ──────────────────────────────────────
  'room.offer.contractAmount':            '',
  'additionalFields.MethodofPaymentCash': false,

  // ── Section 3 Row B: Close of Escrow ─────────────────────────────────────
  'room.offer.ClosingPeriodFromDateOfAcceptance': '30',
  'room.offer.COEDate':                           '',

  // ── Section 3 Row C: Expiration of offer ─────────────────────────────────
  'room.offer.offerExpireDate':       '',
  'room.offer.OfferExpireTime':       '17:00',
  'additionalFields.OfferExpireTime': 'PM',

  // ── Section 3 Row D: Initial deposit ─────────────────────────────────────
  'room.offer.earnestMoneyAmount':            '',
  'room.offer.earnestmoneypercentofpurchase': '',
  'room.offer.earnestmoneyDueDays':           '3',

  // ── Section 3 Row E1: First loan ─────────────────────────────────────────
  'room.offer.firstMortgageLoanAmount':         '',
  'room.offer.loanPercentofPurchasePrice':      '',
  'room.offer.LoanApplicationPeriod':           '17',
  'room.offer.MaxInitialAdjustableLoanPercent': '',
  'room.offer.MaxPointsBuyerPays':              '',
  '_ui.firstLoanRateType':                      'fixed',
  'additionalFields.AdjustableRateFinancing':   false,
  '_ui.firstLoanType':                          'conventional',
  'additionalFields.FHALoans':                  false,
  'additionalFields.VALoans':                   false,
  'additionalFields.SellerFinancingOptions':    false,
  'additionalFields.OtherLoan':                 false,
  'room.offer.OtherLoanType':                   '',

  // ── Section 3 Row E2: Additional financed amount ─────────────────────────
  'room.offer.AmountFinanced':                          '',
  'room.offer.loanPercentofPurchasePrice2':             '',
  'room.offer.NumberOfDaysAfterAcceptancePeriod':       '',
  'room.offer.secondmortgageinterestrateperAnnum':      '',
  'room.offer.MaxPointsBuyerPays2':                     '',
  '_ui.addLoanRateType':                                'fixed',
  'additionalFields.AdjustableRateFinancing2':          false,
  '_ui.addLoanType':                                    'conventional',
  'additionalFields.SellerFinancingOptions2':           false,
  'additionalFields.OtherLoan2':                        false,
  'room.offer.OtherLoanType2':                          '',

  // ── Section 3 Row E3: Occupancy type ─────────────────────────────────────
  'additionalFields.OccupancyType': '',

  // ── Section 3 Row F: Down payment & total ────────────────────────────────
  'room.offer.BalanceOfPurchasePrice':             '',
  'room.offer.PurchasePriceAllocationTotalAmount': '',

  // ── Step 2 — Section G: Seller Payment ───────────────────────────────────
  'additionalFields.CreditReport':                  false,
  'room.offer.SellerEstimatedCreditToBuyer':        '',
  'room.offer.OtherCreditDocuments':                '',
  'room.offer.OtherFinancingTerms':                 '',
  'additionalFields.BuyerBrokerageFeePaidbySeller': false,
  'room.additionalInformation.buyerSideCommission': '',
  'room.offer.salecommissionAmount':                '',
  'additionalFields.CooperativeBrokerCompensation': false,
  'room.offer.additionalsalecommissionAmount':      '',

  // ── Step 2 — Section H: Verification ─────────────────────────────────────
  'room.offer.UnencomberedFundsEvidenceDays':   '3',
  'additionalFields.OtherNumberOfDays':          false,
  '_ui.h1Timing':                                'attached',
  'room.offer.UnencomberedFundsEvidenceDays2':  '3',
  'additionalFields.OtherNumberOfDays2':         false,
  '_ui.h2Timing':                                'attached',
  'additionalFields.OtherNumberOfDays3':         false,
  '_ui.h3Timing':                                'attached',
  'additionalFields.BuyerFinancingPreapproval':  false,
  'additionalFields.BuyerFinancingPreapproval2': false,
  'additionalFields.BuyerFinancingPreapproval3': false,
  '_ui.h3Approval':                              '',

  // ── Step 2 — Section J: Final Verification ───────────────────────────────
  'room.offer.InventoryConditionFormDeliveryPeriod': '5',

  // ── Step 2 — Section K: Assignment ───────────────────────────────────────
  'room.offer.AssignablePeriod': '17',

  // ── Step 2 — Section L: Contingencies ────────────────────────────────────
  'additionalFields.RemoveContingency':              false,
  'room.offer.FinancingContingencyPeriod':           '17',
  'additionalFields.FinancingContingencyWaived':     false,
  'room.offer.AppraisalContingencyPeriod':           '17',
  'additionalFields.AppraisalContingency2':          false,
  'room.offer.appraisalAmount':                      '',
  'additionalFields.Appraisal':                      false,
  'room.offer.NumberOfDaysAfterAcceptancePeriod2':   '17',
  'room.offer.NumberOfDaysAfterAcceptancePeriod3':   '17',
  'room.offer.DocumentReviewContingencyDeadline':    '17',
  'room.offer.NumberOfDaysAfterAcceptancePeriod4':   '17',
  'room.offer.NumberOfDaysAfterAcceptancePeriod5':   '17',
  'room.offer.LeaseReviewContingencyPeriod':         '17',
  'room.offer.LeaseReviewContingencyPeriod2':        '17',
  'additionalFields.BuyerPropertyContingency':       false,

  // ── Step 2 — Section M: Possession ───────────────────────────────────────
  '_ui.m1Possession':                                 'recordation',
  'additionalFields.PossessionAtClosing':             false,
  'additionalFields.PossessionOther':                 false,
  'room.offer.PossessionTime':                        '',
  'additionalFields.PossessionTime':                  'PM',
  '_ui.m2Timing':                                     'coe',
  'additionalFields.OccupiedByOwner':                 false,
  'room.property.LengthofOccupancy':                  '',
  'additionalFields.OccupiedByOwner2':                false,
  'room.property.LengthofOccupancy2':                 '',
  'additionalFields.TenantOccupiedPropertyAddendum':  false,

  // ── Step 2 — Section N: Documents / Fees ─────────────────────────────────
  'room.offer.NumberOfDaysAfterAcceptancePeriod6': '7',
  'room.offer.NumberOfDaysAfterAcceptancePeriod7': '5',
  'room.offer.NumberOfDaysAfterAcceptancePeriod8': '3',
  'room.offer.NumberOfDaysAfterAcceptancePeriod9': '7',

  // ── Step 3 — Section P: Items Included / Excluded ────────────────────────
  'additionalFields.Stove':             false,
  'additionalFields.Refrigerator':      false,
  'additionalFields.WineRefrigerator':  false,
  'additionalFields.Washer':            false,
  'additionalFields.Dryer':             false,
  'additionalFields.Dishwasher':        false,
  'additionalFields.Microwave':         false,
  'additionalFields.DoorBell':          false,
  'additionalFields.SecuritySystem':    false,
  'additionalFields.SecuritySystem2':   false,
  'additionalFields.SmartHomeFeatures': false,
  'additionalFields.AboveGroundPool':   false,
  'additionalFields.ElectricCarCharger':false,
  // Custom included slots (checkbox + text)
  'additionalFields.AdditionalItemsIncluded18': false,
  'additionalFields.AdditionalItemsIncluded5':  false,
  'room.property.details.fixtureInclusion2':    '',
  'additionalFields.AdditionalItemsIncluded2':  false,
  'room.property.details.fixtureInclusion4':    '',
  'additionalFields.AdditionalItemsIncluded3':  false,
  'room.property.details.fixtureInclusion':     '',
  'additionalFields.AdditionalItemsIncluded6':  false,
  'room.property.details.fixtureInclusion3':    '',
  'additionalFields.AdditionalItemsIncluded':   false,
  'room.property.details.fixtureInclusion5':    '',
  'additionalFields.AdditionalItemsIncluded4':  false,
  'additionalFields.AdditionalItemsIncluded7':  false,
  // P(2) Excluded items
  'additionalFields.ItemsExcluded':  false,
  'room.property.details.fixtureExclusion':  '',
  'additionalFields.ItemsExcluded2': false,
  'room.property.details.fixtureExclusion2': '',
  'additionalFields.ItemsExcluded3': false,
  'room.property.details.fixtureExclusion3': '',

  // ── Step 3 — Section Q: Allocation of Costs ──────────────────────────────
  // Q(1) Natural Hazard Zone
  'additionalFields.EnvironmentalInspectionReport':  false,
  'additionalFields.EnvironmentalInspectionReport2': false,
  'room.offer.OtherFeesPaidByDescription':           '',
  'additionalFields.EnvironmentalInspectionReport3': false,
  'room.offer.OtherFeesPaidByDescription2':          '',
  'room.offer.OtherFeesPaidByDescription3':          '',
  // Q(2) Wildfire
  'additionalFields.DisclosureReport':               false,
  'room.offer.OtherFeesPaidByDescription4':          '',
  'room.offer.OtherFeesPaidByDescription5':          '',
  'room.offer.OtherFeesPaidByDescription6':          '',
  // Q(3A) & Q(3B) Custom reports
  'room.offer.OtherReports':                         '',
  'room.offer.OtherFeesPaidByDescription7':          '',
  'room.offer.OtherReports2':                        '',
  'room.offer.OtherFeesPaidByDescription8':          '',
  // Q(4) Smoke alarms
  'room.offer.OtherFeesPaidByDescription9':          '',
  // Q(5) Govt inspections
  'room.offer.OtherFeesPaidByDescription10':         '',
  // Q(6) Govt corrective actions
  'room.offer.OtherFeesPaidByDescription11':         '',
  // Q(7) Escrow fee
  'additionalFields.OtherFees':                      false,
  'room.offer.EscrowFees':                           '',
  'room.offer.EscrowFeesPaidBy':                     '',
  'room.offer.OtherFeesPaidByDescription12':         '',
  // Q(8) Owner title insurance
  'room.offer.OtherFeesPaidByDescription13':         '',
  // Q(10) County transfer tax
  'room.offer.OtherTransferFeeDescription':          '',
  // Q(11) City transfer tax
  'room.offer.OtherTransferFeeDescription2':         '',
  // Q(14) HOA transfer fees
  'room.property.HomeownerAssociationTransferFee':   '',
  // Q(15) Private transfer fees
  'room.offer.PrivateTransferFeeObligation':         '',
  'additionalFields.WellSepticSystemEvaluation':     false,
  // Q(17) Fees or costs
  'room.offer.BrokerServiceOtherFee':                '',
  'room.offer.OtherFeesPaidByDescription14':         '',
  // Custom Fee B (not an official Q-item)
  'room.offer.BrokerServiceOtherFee2':               '',
  'room.offer.OtherFeesPaidByDescription16':         '',
  // Q(18) Home warranty
  'room.service.HomeWarrantyTypeOfCoverage':          '',
  'room.service.HomeWarrantyTypeOfCoverage2':         '',
  'room.offer.OtherFeesPaidByDescription15':         '',
  'room.offer.SellerEstimatedHomeWarrantyCost':       '',
  'additionalFields.DeclineHomeWarrantyProgram':      false,
  'room.service.homeWarrantyProvider':                '',
  'room.service.homeWarrantyProvider2':               '',
  // ── Step 3 — Section Q: "Paid by" radio groups (DocuSign Buyer/Seller/Both radios) ─
  'additionalFields.PayableByBuyerOrSeller':                            '',
  'additionalFields.PayableByBuyerOrSeller2':                           '',
  'additionalFields.PayableByBuyerOrSeller3':                           '',
  'additionalFields.PayableByBuyerOrSeller4':                           '',
  'additionalFields.PayableByBuyerOrSeller5':                           '',
  'additionalFields.PayableByBuyerOrSeller6':                           '',
  'additionalFields.PayableByBuyerOrSeller7':                           '',
  'additionalFields.PayableByBuyerOrSeller8':                           '',
  'additionalFields.PayCostOfPaymentEscrow':                            '',
  'additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer':         '',
  'additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer2':        '',
  'additionalFields.TransferTaxesPaidByBuyerOrSeller':                  '',
  'additionalFields.TransferTaxesPaidByBuyerOrSeller2':                 '',
  'additionalFields.TransferFeesPaidforBy':                             '',
  'additionalFields.TransferFeesPaidforBy2':                            '',

  // ── Step 3 — Section R: Other Terms ──────────────────────────────────────
  'room.offer.OtherTerms':  '',
  'room.offer.OtherTerms2': '',
  'room.offer.OtherTerms3': '',
  'room.offer.OtherTerms4': '',
  'room.offer.OtherTerms5': '',

  // ── Step 4 — Section 4A: Property Type Addenda ───────────────────────────
  'additionalFields.TenantOccupiedPropertyAddendum2': false,  // TOPA — 4A checklist (distinct from Step 6 M(3) "TOPA attached")
  'additionalFields.OtherAddenda':    false,  // PAPA — Probate Agreement Purchase Addendum
  'additionalFields.OtherAddenda2':   false,  // MHPA — Manufactured Home Purchase Addendum
  'additionalFields.OtherAddenda3':   false,  // TICPA — Tenancy in Common Purchase Addendum
  'additionalFields.OtherAddenda4':   false,  // COOPPA — Stock Cooperative Purchase Addendum
  'additionalFields.OtherAddenda5':   false,  // MUPA — Mixed Use Purchase Addendum
  'additionalFields.OtherAddenda6':   false,  // Other property type addendum (checkbox)
  'room.offer.AddendumsAttached':     '',     // Other property type addendum (text)
  // ── Step 4 — Section 4B: Other Addenda ──────────────────────────────────
  'additionalFields.OtherAddenda7':   false,  // ADM — Addendum # (checkbox)
  'room.offer.addendumNumbersCARFormADM': '', // ADM — Addendum # (number)
  'additionalFields.OtherAddenda12':  false,  // SSA — Short Sale Addendum
  'additionalFields.OtherAddenda8':   false,  // BUO — Back Up Offer Addendum
  'additionalFields.OtherAddenda13':  false,  // CCA — Court Confirmation Addendum
  'additionalFields.OtherAddenda9':   false,  // SWPI-C — Septic, Well, Propane Tank & Property Boundaries
  'additionalFields.OtherAddenda10':  false,  // BXA — Buyer Intent to Exchange Addendum
  'additionalFields.OtherAddenda14':  false,  // SXA — Seller Intent to Exchange Addendum
  // AD — Disclosure Regarding Real Estate Agency Relationships: fixed, always included, no key needed
  'additionalFields.OtherAddenda11':  false,  // Custom addendum 1 (checkbox)
  'room.offer.AddendumsAttached2':    '',     // Custom addendum 1 (text)
  'additionalFields.OtherAddenda15':  false,  // Custom addendum 2 (checkbox)
  'room.offer.AddendumsAttached3':    '',     // Custom addendum 2 (text)
  // ── Step 4 — Section 4C: Advisories (reference only) ────────────────────
  // BIA, WFA, FHDA, BHIA — fixed, always included, no key needed
  'additionalField.OtherAdvisories':  false,  // WFDA — Wildfire Disaster Advisory
  'additionalField.OtherAdvisories5': false,  // SBSA — Statewide Buyer and Seller Advisory
  'additionalField.OtherAdvisories2': false,  // TA — Trust Advisory
  'additionalField.OtherAdvisories6': false,  // PA — Probate Advisory
  'additionalField.OtherAdvisories3': false,  // REO — REO Advisory
  'additionalField.OtherAdvisories4': false,  // Other advisory 1 (checkbox)
  'room.offer.OtherAdvisories':       '',     // Other advisory text 1
  'additionalField.OtherAdvisories7': false,  // Other advisory 2 (checkbox)
  'room.offer.OtherAdvisories2':      '',     // Other advisory text 2
  'additionalField.OtherAdvisories8': false,  // Other advisory 3 (checkbox)
  'room.offer.OtherAdvisories3':      '',     // Other advisory text 3

  // ── Step 5 — Para 32: Offer / Entity Buyers ───────────────────────────────
  '_ui.entityBuyerEnabled':                   false,
  'additionalFields.EntityBuyers':             false,
  'room.offer.PropertyCaseNumber':             '',
  'room.offer.PropertyCaseNumber2':            '',
  'room.details.AuthorizedSignerName':           '',
  'room.details.AuthorizedSignerName2':           '',
  // ── Para 32D — Buyer Signature(s) ─────────────────────────────────────────
  'additionalFields.ClientRepresentative':             false,
  'room.details.BuyerEntityTitle':                 '',
  'additionalFields.ClientRepresentative2':             false,
  'room.details.BuyerEntityTitle2':                 '',
  'additionalFields.AdditionalSignature':      false,
  // ── Step 4 — Para 33: Acceptance / Entity Sellers ────────────────────────
  'additionalFields.CounterOffer':        false,
  'additionalFields.BackUpContractAddendum':         false,
  '_ui.entitySellerEnabled':                   false,
  'additionalFields.EntitySellers':             false,
  'room.offer.PropertyCaseNumber3':            '',
  'room.offer.PropertyCaseNumber4':                     '',
  'room.details.AuthorizedSignerName3':           '',
  'room.details.AuthorizedSignerName4':           '',
  // ── Para 33D — Seller Signature(s) ────────────────────────────────────────
  'additionalFields.ClientRepresentative3':            false,
  'room.details.SellerEntityTitle':                '',
  'additionalFields.ClientRepresentative4':            false,
  'room.details.SellerEntityTitle2':                '',
  'additionalFields.AdditionalSignature2':     false,

  // ── Step 6 — Real Estate Brokers Section: Buyer's Brokerage (§4A) ─────────
  'room.details.buyerAgent1.phone':                             '',
  'room.details.buyerAgent1.email':                             '',
  'room.details.designatedbuyerAgent2.licenseNum':              '',
  'room.details.buyerBrokerage.address':                        '',
  'TODO_BuyerBrokerageAddress2':                                '',
  'room.details.buyerBrokerage.city':                           '',
  'room.details.buyerBrokerage.state':                          'CA',
  'room.details.buyerBrokerage.zip':                            '',
  'room.details.buyerBrokerage.email':                          '',
  'room.details.buyerBrokerage.phone':                          '',
  'additionalFields.MultipleRepresentationWithDesignatedAgency2':                           false,
  'additionalFields.MultipleRepresentationWithDesignatedAgency3':                          false,
  'room.details.DocumentDeliveryMethodLabel':                        '',
  'additionalFields.EmailDeliveryOfDocumentMethod':                         false,
  // ── Step 6 — Real Estate Brokers Section: Seller's Brokerage (§4B) ────────
  'room.details.designatedsellerAgent2.licenseNum':             '',
  'room.details.sellerBrokerage.address':                       '',
  'TODO_SellerBrokerageAddress2':                               '',
  'room.details.sellerBrokerage.city':                          '',
  'room.details.sellerBrokerage.state':                         'CA',
  'room.details.sellerBrokerage.zip':                           '',
  'room.details.sellerBrokerage.email':                         '',
  'room.details.sellerBrokerage.phone':                         '',
  'additionalFields.MultipleRepresentationWithDesignatedAgency4':                          false,
  'additionalFields.MultipleRepresentationWithDesignatedAgency5':                         false,
  'room.details.DocumentDeliveryMethodLabel2':                       '',
  'additionalFields.EmailDeliveryOfDocumentMethod2':                        false,

  // ── Step 6 — Escrow Holder Acknowledgment ────────────────────────────────
  'additionalFields.depositEarnestMoney':         false,
  'room.offer.finalearnestmoneyAmount':                      '',
  'room.offer.CounterofferNumber':                      '',
  'room.offer.CounterofferNumber639':                      '',
  'room.offer.EscrowAgentNotifedByName':                          '',
  'room.offer.bindingDate':                     '',
  'room.offer.entityHoldingEarnestMoney':                         '',
  'room.details.escrowCompany.number':                             '',
  'room.details.escrowCompany.address':                            '',
  'room.details.escrowCompany.address2':                           '',
  'room.details.escrowCompany.city':                               '',
  'room.details.escrowCompany.state':                              'CA',
  'room.details.escrowCompany.postalCode':                         '',
  'room.details.escrowOfficer.cellPhone':                       '',
  'room.details.escrowOfficer.fax':                         '',
  'room.details.escrowOfficer.email':                       '',
  'room.details.escrowOfficer.LicenseNumber':                      '',
  'additionalFields.FinancialProtectionDepartment': false,
  'additionalFields.InsuranceDepartment':           false,
  'additionalFields.RealEstateDepartment':          false,
};

const DUMMY = {
  // ── Section 1: Offer ─────────────────────────────────────────────────────
  'room.offer.presentedofferdate':       '2026-06-12',
  'room.offer.contractDate':             '2026-06-12',
  'room.details.buyer1.name':            'John A. Smith',
  'room.details.buyer2.name':            'Jane B. Smith',
  'room.property.location.address1':     '123 Sunset Boulevard',
  'room.property.location.address2':     'Unit 4B',
  'room.property.location.city':         'Los Angeles',
  'room.property.location.state':        'CA',
  'room.property.location.county':       'Los Angeles',
  'room.property.location.postalCode':   '90028',
  'room.property.ParcelID':              '5535-022-019',

  // ── Section 2: Agency — Seller side ─────────────────────────────────────
  'room.details.listingAgent1.company':            'Coldwell Banker Realty',
  'room.details.sellerBrokerage.LicenseNum':       '01234567',
  'additionalFields.SellerBrokerageType':           'Both the Buyer and Seller',
  'room.details.listingAgent1.name':               'Robert J. Davis',
  'room.details.designatedsellerAgent.licenseNum': '02345678',
  'additionalFields.AgentType':                    "Both the Buyer's and Seller's Agent",

  // ── Section 2: Agency — Buyer side ──────────────────────────────────────
  'room.details.buyerAgent1.company':             'Keller Williams Realty',
  'room.details.buyerBrokerage.licenseNum':        '03456789',
  'additionalFields.BuyerBrokerageType':           'Buyer',
  'room.details.buyerAgent1.name':                'Sarah M. Johnson',
  'room.details.designatedbuyerAgent.licenseNum': '04567890',
  'additionalFields.AgentType2':                  "the Buyer's Agent",

  // ── Section 2: Additional disclosures ───────────────────────────────────
  // checked → reveals SellerorBuyerRadio
  'additionalFields.MultipleRepresentationWithDesignatedAgency': true,
  'additionalFields.SellerorBuyerRadio':                         'Seller',

  // ── Section 3 Row A: Purchase price ─────────────────────────────────────
  'room.offer.contractAmount':            '850000',
  'additionalFields.MethodofPaymentCash': true,

  // ── Section 3 Row B: Close of Escrow ────────────────────────────────────
  'room.offer.ClosingPeriodFromDateOfAcceptance': '30',
  'room.offer.COEDate':                           '2026-07-14',

  // ── Section 3 Row C: Expiration ─────────────────────────────────────────
  'room.offer.offerExpireDate':       '2026-06-15',
  'room.offer.OfferExpireTime':       '17:00',
  'additionalFields.OfferExpireTime': 'PM',

  // ── Section 3 Row D: Initial deposit ────────────────────────────────────
  'room.offer.earnestMoneyAmount':            '25000',
  'room.offer.earnestmoneypercentofpurchase': '2.94',
  'room.offer.earnestmoneyDueDays':           '3',

  // ── Section 3 Row E1: First loan ────────────────────────────────────────
  // rate type 'adjustable' keeps MaxInitialAdjustableLoanPercent visible
  // loan type 'other' reveals OtherLoanType text field
  'room.offer.firstMortgageLoanAmount':         '680000',
  'room.offer.loanPercentofPurchasePrice':      '80',
  'room.offer.LoanApplicationPeriod':           '17',
  'room.offer.MaxInitialAdjustableLoanPercent': '8.5',
  'room.offer.MaxPointsBuyerPays':              '1',
  '_ui.firstLoanRateType':                      'adjustable',
  'additionalFields.AdjustableRateFinancing':   true,
  '_ui.firstLoanType':                          'other',
  'additionalFields.FHALoans':                  false,
  'additionalFields.VALoans':                   false,
  'additionalFields.SellerFinancingOptions':    false,
  'additionalFields.OtherLoan':                 true,
  'room.offer.OtherLoanType':                   'Bridge loan — 12-month term',

  // ── Section 3 Row E2: Additional financed amount ────────────────────────
  // loan type 'other' reveals OtherLoanType2 text field
  'room.offer.AmountFinanced':                     '50000',
  'room.offer.loanPercentofPurchasePrice2':         '5.88',
  'room.offer.NumberOfDaysAfterAcceptancePeriod':   '17',
  'room.offer.secondmortgageinterestrateperAnnum':  '7.0',
  'room.offer.MaxPointsBuyerPays2':                 '1',
  '_ui.addLoanRateType':                            'adjustable',
  'additionalFields.AdjustableRateFinancing2':      true,
  '_ui.addLoanType':                                'other',
  'additionalFields.SellerFinancingOptions2':       false,
  'additionalFields.OtherLoan2':                    true,
  'room.offer.OtherLoanType2':                      'Seller carry-back — 2nd TD',

  // ── Section 3 Row E3: Occupancy type ────────────────────────────────────
  'additionalFields.OccupancyType': 'Secondary',

  // ── Section 3 Row F: Down payment & total ───────────────────────────────
  'room.offer.BalanceOfPurchasePrice':             '120000',
  'room.offer.PurchasePriceAllocationTotalAmount': '850000',

  // ── Step 2 — Section G: Seller Payment ──────────────────────────────────
  // CreditReport checked → reveals SellerEstimatedCreditToBuyer
  'additionalFields.CreditReport':                  true,
  'room.offer.SellerEstimatedCreditToBuyer':        '5000',
  'room.offer.OtherCreditDocuments':                'Gift letter from buyers parents',
  'room.offer.OtherFinancingTerms':                 'Subject to buyer obtaining rate lock within 5 days',
  // BuyerBrokerageFeePaidbySeller checked → reveals commission + CooperativeBrokerCompensation
  'additionalFields.BuyerBrokerageFeePaidbySeller': true,
  'room.additionalInformation.buyerSideCommission': '2.5',
  'room.offer.salecommissionAmount':                '21250',
  // CooperativeBrokerCompensation checked → reveals additionalsalecommissionAmount
  'additionalFields.CooperativeBrokerCompensation': true,
  'room.offer.additionalsalecommissionAmount':      '500',

  // ── Step 2 — Section H: Verification ────────────────────────────────────
  // h1Timing 'days' → reveals UnencomberedFundsEvidenceDays
  '_ui.h1Timing':                                'days',
  'additionalFields.OtherNumberOfDays':          true,
  'room.offer.UnencomberedFundsEvidenceDays':    '5',
  // h2Timing 'days' → reveals UnencomberedFundsEvidenceDays2
  '_ui.h2Timing':                                'days',
  'additionalFields.OtherNumberOfDays2':         true,
  'room.offer.UnencomberedFundsEvidenceDays2':   '5',
  // h3Timing 'days' → reveals LoanApplicationPeriod day field
  '_ui.h3Timing':                                'days',
  'additionalFields.OtherNumberOfDays3':         true,
  'additionalFields.BuyerFinancingPreapproval':  false,
  'additionalFields.BuyerFinancingPreapproval2': false,
  'additionalFields.BuyerFinancingPreapproval3': true,
  '_ui.h3Approval':                              'fully_underwritten',

  // ── Step 2 — Section J: Final Verification ──────────────────────────────
  'room.offer.InventoryConditionFormDeliveryPeriod': '5',

  // ── Step 2 — Section K: Assignment ──────────────────────────────────────
  'room.offer.AssignablePeriod': '17',

  // ── Step 2 — Section L: Contingencies ───────────────────────────────────
  'additionalFields.RemoveContingency':              true,
  'room.offer.FinancingContingencyPeriod':           '21',
  'additionalFields.FinancingContingencyWaived':     true,
  'room.offer.AppraisalContingencyPeriod':           '17',
  'additionalFields.AppraisalContingency2':          true,
  'room.offer.appraisalAmount':                      '850000',
  'additionalFields.Appraisal':                      true,
  'room.offer.NumberOfDaysAfterAcceptancePeriod2':   '17',
  'room.offer.NumberOfDaysAfterAcceptancePeriod3':   '17',
  'room.offer.DocumentReviewContingencyDeadline':    '17',
  'room.offer.NumberOfDaysAfterAcceptancePeriod4':   '17',
  'room.offer.NumberOfDaysAfterAcceptancePeriod5':   '17',
  'room.offer.LeaseReviewContingencyPeriod':         '17',
  'room.offer.LeaseReviewContingencyPeriod2':        '17',
  'additionalFields.BuyerPropertyContingency':       true,

  // ── Step 2 — Section M: Possession ──────────────────────────────────────
  // m1Possession 'custom' → reveals PossessionTime + AM/PM
  '_ui.m1Possession':                                 'custom',
  'additionalFields.PossessionAtClosing':             false,
  'additionalFields.PossessionOther':                 true,
  'room.offer.PossessionTime':                        '17:00',
  'additionalFields.PossessionTime':                  'PM',
  // m2Timing 'short' → reveals LengthofOccupancy (≤29 days)
  '_ui.m2Timing':                                     'short',
  'additionalFields.OccupiedByOwner':                 true,
  'room.property.LengthofOccupancy':                  '5',
  'additionalFields.OccupiedByOwner2':                false,
  'room.property.LengthofOccupancy2':                 '',
  'additionalFields.TenantOccupiedPropertyAddendum':  true,

  // ── Step 2 — Section N: Documents / Fees ────────────────────────────────
  'room.offer.NumberOfDaysAfterAcceptancePeriod6': '7',
  'room.offer.NumberOfDaysAfterAcceptancePeriod7': '5',
  'room.offer.NumberOfDaysAfterAcceptancePeriod8': '3',
  'room.offer.NumberOfDaysAfterAcceptancePeriod9': '7',

  // ── Step 3 — Section P: Items Included ──────────────────────────────────
  'additionalFields.Stove':             true,
  'additionalFields.Refrigerator':      true,
  'additionalFields.WineRefrigerator':  true,
  'additionalFields.Washer':            true,
  'additionalFields.Dryer':             true,
  'additionalFields.Dishwasher':        true,
  'additionalFields.Microwave':         true,
  'additionalFields.DoorBell':          true,
  'additionalFields.SecuritySystem':    true,
  'additionalFields.SecuritySystem2':   true,
  'additionalFields.SmartHomeFeatures': true,
  'additionalFields.AboveGroundPool':   true,
  'additionalFields.ElectricCarCharger':true,
  // All custom included slots checked → all text boxes visible
  'additionalFields.AdditionalItemsIncluded18': true,
  'room.property.details.fixtureInclusion':     'Built-in BBQ grill with propane tank',
  'additionalFields.AdditionalItemsIncluded5':  true,
  'room.property.details.fixtureInclusion2':    'Mounted flat-screen TV in living room',
  'additionalFields.AdditionalItemsIncluded2':  true,
  'room.property.details.fixtureInclusion4':    'Backyard pergola and outdoor furniture set',
  'additionalFields.AdditionalItemsIncluded3':  true,
  'additionalFields.AdditionalItemsIncluded6':  true,
  'room.property.details.fixtureInclusion3':    'Pool equipment and accessories',
  'additionalFields.AdditionalItemsIncluded':   true,
  'room.property.details.fixtureInclusion5':    'Home theater projector and screen',
  'additionalFields.AdditionalItemsIncluded4':  true,
  'additionalFields.AdditionalItemsIncluded7':  true,
  // All excluded slots checked → all exclusion text boxes visible
  'additionalFields.ItemsExcluded':             true,
  'room.property.details.fixtureExclusion':     'Dining room chandelier (sentimental family heirloom)',
  'additionalFields.ItemsExcluded2':            true,
  'room.property.details.fixtureExclusion2':    'Antique bathroom mirror in master bath',
  'additionalFields.ItemsExcluded3':            true,
  'room.property.details.fixtureExclusion3':    'Custom workshop shelving in garage',

  // ── Step 3 — Section Q: Allocation of Costs ─────────────────────────────
  'additionalFields.EnvironmentalInspectionReport':  true,
  'additionalFields.EnvironmentalInspectionReport2': true,
  'room.offer.OtherFeesPaidByDescription':           'Seller pays 100%',
  'additionalFields.EnvironmentalInspectionReport3': true,
  'room.offer.OtherFeesPaidByDescription2':          'ABC Environmental Testing',
  'room.offer.OtherFeesPaidByDescription3':          'ABC Environmental Testing',
  'additionalFields.DisclosureReport':               true,
  'room.offer.OtherFeesPaidByDescription4':          'Buyer pays 100%',
  'room.offer.OtherFeesPaidByDescription5':          'California Wildfire Reports, LLC',
  'room.offer.OtherFeesPaidByDescription6':          'Seller pays',
  'room.offer.OtherReports':                         'Pest Inspection Report',
  'room.offer.OtherFeesPaidByDescription7':          'Buyer pays',
  'room.offer.OtherReports2':                        'Roof Inspection Report',
  'room.offer.OtherFeesPaidByDescription8':          'Buyer pays',
  'room.offer.OtherFeesPaidByDescription9':          'Seller',
  'room.offer.OtherFeesPaidByDescription10':         'Buyer',
  'room.offer.OtherFeesPaidByDescription11':         'Seller',
  'additionalFields.OtherFees':                      true,
  'room.offer.EscrowFees':                           'West Coast Escrow',
  'room.offer.EscrowFeesPaidBy':                      'Each party pays their own fees',
  'room.offer.OtherFeesPaidByDescription12':         'Each 50%',
  'room.offer.OtherFeesPaidByDescription13':         'Seller',
  'room.offer.OtherTransferFeeDescription':          'Buyer',
  'room.offer.OtherTransferFeeDescription2':         'Seller',
  'room.property.HomeownerAssociationTransferFee':   '350',
  'room.offer.PrivateTransferFeeObligation':         'None known',
  'additionalFields.WellSepticSystemEvaluation':     true,
  'room.offer.BrokerServiceOtherFee':                'Transaction Coordination Fee',
  'room.offer.OtherFeesPaidByDescription14':         'Buyer pays',
  'room.offer.BrokerServiceOtherFee2':               'Photography & Marketing Levy',
  'room.offer.OtherFeesPaidByDescription16':         'Seller',
  'room.service.HomeWarrantyTypeOfCoverage':          'Premium Home Warranty Plan',
  'room.service.HomeWarrantyTypeOfCoverage2':         'HVAC Extended Coverage',
  'room.offer.OtherFeesPaidByDescription15':         'Seller pays',
  'room.offer.SellerEstimatedHomeWarrantyCost':       '600',
  'additionalFields.DeclineHomeWarrantyProgram':      false,
  'room.service.homeWarrantyProvider':                'American Home Shield',
  'room.service.homeWarrantyProvider2':               '',
  // Section Q "paid by" radio groups
  'additionalFields.PayableByBuyerOrSeller':                            'Seller',
  'additionalFields.PayableByBuyerOrSeller2':                           'Seller',
  'additionalFields.PayableByBuyerOrSeller3':                           'Buyer',
  'additionalFields.PayableByBuyerOrSeller4':                           'Buyer',
  'additionalFields.PayableByBuyerOrSeller5':                           'Seller',
  'additionalFields.PayableByBuyerOrSeller6':                           'Seller',
  'additionalFields.PayableByBuyerOrSeller7':                           'Seller',
  'additionalFields.PayableByBuyerOrSeller8':                           'Seller',
  'additionalFields.PayCostOfPaymentEscrow':                            'Both',
  'additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer':         'Seller',
  'additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer2':        'Buyer',
  'additionalFields.TransferTaxesPaidByBuyerOrSeller':                  'Seller',
  'additionalFields.TransferTaxesPaidByBuyerOrSeller2':                 'Buyer',
  'additionalFields.TransferFeesPaidforBy':                             'Buyer',
  'additionalFields.TransferFeesPaidforBy2':                            'Seller',

  // ── Step 3 — Section R: Other Terms ─────────────────────────────────────
  'room.offer.OtherTerms':  'Seller to provide pest inspection report within 7 days of acceptance.',
  'room.offer.OtherTerms2': 'Buyer to assume existing solar panel lease agreement.',
  'room.offer.OtherTerms3': 'All appliances to be in working order at time of COE.',
  'room.offer.OtherTerms4': 'Seller to credit buyer $2,500 toward new carpet installation.',
  'room.offer.OtherTerms5': 'Buyer reserves the right to conduct a final walk-through 5 days prior to COE.',

  // ── Step 4 — Section 4A: Property Type Addenda ───────────────────────────
  'additionalFields.TenantOccupiedPropertyAddendum2': true,   // TOPA (4A checklist)
  'additionalFields.OtherAddenda':    true,   // PAPA
  'additionalFields.OtherAddenda2':   false,
  'additionalFields.OtherAddenda3':   false,
  'additionalFields.OtherAddenda4':   false,
  'additionalFields.OtherAddenda5':   false,
  'additionalFields.OtherAddenda6':   true,
  'room.offer.AddendumsAttached':     'Green Building Addendum',
  // ── Step 4 — Section 4B: Other Addenda ──────────────────────────────────
  'additionalFields.OtherAddenda7':   true,
  'room.offer.addendumNumbersCARFormADM': '3',
  'additionalFields.OtherAddenda12':  false,  // SSA
  'additionalFields.OtherAddenda8':   true,   // BUO
  'additionalFields.OtherAddenda13':  false,  // CCA
  'additionalFields.OtherAddenda9':   false,  // SWPI-C
  'additionalFields.OtherAddenda10':  false,  // BXA
  'additionalFields.OtherAddenda14':  false,  // SXA
  'additionalFields.OtherAddenda11':  true,
  'room.offer.AddendumsAttached2':    'Solar Panel Lease Addendum',
  'additionalFields.OtherAddenda15':  false,
  'room.offer.AddendumsAttached3':    '',
  // ── Step 4 — Section 4C: Advisories ─────────────────────────────────────
  // BIA, WFA, FHDA, BHIA — fixed, always included, no key needed
  'additionalField.OtherAdvisories':  true,   // WFDA
  'additionalField.OtherAdvisories5': true,   // SBSA
  'additionalField.OtherAdvisories2': false,  // TA
  'additionalField.OtherAdvisories6': false,  // PA
  'additionalField.OtherAdvisories3': true,   // REO
  'additionalField.OtherAdvisories4': true,   // Other advisory 1 (checkbox)
  'room.offer.OtherAdvisories':       'REO Advisory 1',
  'additionalField.OtherAdvisories7': true,   // Other advisory 2 (checkbox)
  'room.offer.OtherAdvisories2':      'Test Advisory 2',
  'additionalField.OtherAdvisories8': false,  // Other advisory 3 (checkbox)
  'room.offer.OtherAdvisories3':      '',

  // ── Step 5 — Para 32 & 33 ────────────────────────────────────────────────
  // entityBuyerEnabled checked → reveals full entity buyer panel
  '_ui.entityBuyerEnabled':                   true,
  'additionalFields.EntityBuyers':             true,
  'room.offer.PropertyCaseNumber':             'Smith Family Revocable Living Trust dated January 15, 2020',
  'room.offer.PropertyCaseNumber2':            'Case #PROB-2026-LA-00421',
  'room.details.AuthorizedSignerName':           'John A. Smith, Co-Trustee',
  'room.details.AuthorizedSignerName2':           'Jane B. Smith, Co-Trustee',
  'additionalFields.ClientRepresentative':             true,
  'room.details.BuyerEntityTitle':                 'Co-Trustee',
  'additionalFields.ClientRepresentative2':             true,
  'room.details.BuyerEntityTitle2':                 'Co-Trustee',
  'additionalFields.AdditionalSignature':      false,
  // Counter offer and backup offer checked
  'additionalFields.CounterOffer':        true,
  'additionalFields.BackUpContractAddendum':         true,
  // entitySellerEnabled checked → reveals full entity seller panel
  '_ui.entitySellerEnabled':                   true,
  'additionalFields.EntitySellers':             true,
  'room.offer.PropertyCaseNumber3':            'Horizon Real Estate Holdings LLC, a California LLC',
  'room.offer.PropertyCaseNumber4':            'CORP-2026-CA-88732',
  'room.details.AuthorizedSignerName3':           'Michael T. Harrison, Managing Member',
  'room.details.AuthorizedSignerName4':           'Linda K. Harrison, Co-Manager',
  'additionalFields.ClientRepresentative3':            true,
  'room.details.SellerEntityTitle':                'Managing Member',
  'additionalFields.ClientRepresentative4':            true,
  'room.details.SellerEntityTitle2':                'Co-Manager',
  'additionalFields.AdditionalSignature2':     false,

  // ── Step 5 — Real Estate Brokers Section: Buyer's Brokerage (§4A) ─────────
  'room.details.buyerAgent1.phone':                             '(310) 555-0199',
  'room.details.buyerAgent1.email':                             'sarah.johnson@kwrealty.com',
  'room.details.designatedbuyerAgent2.licenseNum':              '05678901',
  'room.details.buyerBrokerage.address':                        '9255 Sunset Blvd Ste 1100',
  'TODO_BuyerBrokerageAddress2':                                '',
  'room.details.buyerBrokerage.city':                           'Los Angeles',
  'room.details.buyerBrokerage.state':                          'CA',
  'room.details.buyerBrokerage.zip':                            '90069',
  'room.details.buyerBrokerage.email':                          'info@kwrealty-la.com',
  'room.details.buyerBrokerage.phone':                          '(310) 555-0100',
  'additionalFields.MultipleRepresentationWithDesignatedAgency2':                           true,
  'additionalFields.MultipleRepresentationWithDesignatedAgency3':                          true,
  'room.details.DocumentDeliveryMethodLabel':                        'secure-docs@kwrealty.com',
  'additionalFields.EmailDeliveryOfDocumentMethod':                         true,
  // ── Step 5 — Real Estate Brokers Section: Seller's Brokerage (§4B) ────────
  'room.details.designatedsellerAgent2.licenseNum':             '06789012',
  'room.details.sellerBrokerage.address':                       '5600 Wilshire Blvd Ste 2100',
  'TODO_SellerBrokerageAddress2':                                '',
  'room.details.sellerBrokerage.city':                          'Los Angeles',
  'room.details.sellerBrokerage.state':                         'CA',
  'room.details.sellerBrokerage.zip':                           '90036',
  'room.details.sellerBrokerage.email':                         'info@coldwellbanker-la.com',
  'room.details.sellerBrokerage.phone':                         '(323) 555-0400',
  'additionalFields.MultipleRepresentationWithDesignatedAgency4':                          true,
  'additionalFields.MultipleRepresentationWithDesignatedAgency5':                         true,
  'room.details.DocumentDeliveryMethodLabel2':                       'docs@coldwellbanker-la.com',
  'additionalFields.EmailDeliveryOfDocumentMethod2':                        true,

  // ── Step 6 — Escrow Holder Acknowledgment ────────────────────────────────
  'additionalFields.depositEarnestMoney':         true,
  'room.offer.finalearnestmoneyAmount':                      '$25,000.00',
  'room.offer.CounterofferNumber':                      'SCO #1',
  'room.offer.CounterofferNumber639':                      'SCO #2',
  'room.offer.EscrowAgentNotifedByName':                          'Sarah M. Johnson',
  'room.offer.bindingDate':                     '2026-06-13',
  'room.offer.entityHoldingEarnestMoney':                         'Pacific Coast Escrow',
  'room.details.escrowCompany.number':                             'PCE-2026-88421',
  'room.details.escrowCompany.address':                            '1234 Wilshire Blvd',
  'room.details.escrowCompany.address2':                           'Suite 500',
  'room.details.escrowCompany.city':                               'Los Angeles',
  'room.details.escrowCompany.state':                              'CA',
  'room.details.escrowCompany.postalCode':                         '90017',
  'room.details.escrowOfficer.cellPhone':                       '(213) 555-7890',
  'room.details.escrowOfficer.fax':                         '(213) 555-7891',
  'room.details.escrowOfficer.email':                       'escrow@pacificcoast.com',
  'room.details.escrowOfficer.LicenseNumber':                      'ESC-4567890',
  'additionalFields.FinancialProtectionDepartment': false,
  'additionalFields.InsuranceDepartment':           false,
  'additionalFields.RealEstateDepartment':          true,
};

export default function PartialRPAWizardModal({
  isOpen,
  onClose,
  onComplete,
  onProgressUpdate,
  transaction,
  draftData,
  participants = [],
  interestedProperty,
  envelopeId,
}) {
  const [form, setForm] = useState({ ...INITIAL });
  const [step, setStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [confirmed, setConfirmed] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Prevents the draftData useEffect from re-running when onProgressUpdate
  // updates draftData while the modal is open (which would overwrite user's in-flight edits).
  const draftInitializedRef = useRef(false);

  const getFirebaseToken = () => new Promise((resolve, reject) => {
    if (auth.currentUser) {
      auth.currentUser.getIdToken(true).then(resolve).catch(reject);
    } else {
      const unsub = auth.onAuthStateChanged
        ? auth.onAuthStateChanged(u => { unsub(); u ? u.getIdToken(true).then(resolve).catch(reject) : reject(new Error('Not authenticated')); })
        : (() => { reject(new Error('Auth unavailable')); })();
      setTimeout(() => reject(new Error('Auth timeout')), 10000);
    }
  });

  // Fetch county from ATTOM address-lookup API when it is missing
  const fetchCountyFromAttom = async (address, city, state, zip) => {
    if (!address) return '';
    try {
      const token = await getFirebaseToken();
      const address2 = [city, state, zip].filter(Boolean).join(', ');
      const params = { address1: address };
      if (address2) params.address2 = address2;
      const res = await apiClient.get(API_ENDPOINTS.attomAddressLookup, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      const d = Array.isArray(res.data) ? res.data[0] : res.data;
      return d?.county || d?.countyName || d?.countyFIPS || '';
    } catch {
      return '';
    }
  };

  // Map an interested-property API object → RPA form keys (DocuSign tab labels).
  // Only emits keys that have a value, so it never blanks out existing form data.
  const mapInterestedPropertyToForm = (p) => {
    if (!p || typeof p !== 'object') return {};
    const out = {};
    const put = (key, val) => { if (val !== null && val !== undefined && val !== '') out[key] = String(val); };
    put('room.property.location.address1',  p.propertyAddress);
    put('room.property.location.city',      p.propertyCity);
    put('room.property.location.state',     p.propertyState);
    put('room.property.location.postalCode', p.propertyZip);
    put('room.property.location.county',    p.county);
    put('room.property.ParcelID',           p.taxId);
    put('room.offer.contractAmount',        p.listPrice);
    // API returns a single sellerName → Seller 1 only (no sellerName2). Seller 2 stays manual.
    put('room.details.seller1.name',        p.sellerName);
    put('room.details.listingAgent1.name',    p.listingAgentName);
    put('room.details.listingAgent1.company', p.listingOfficeName);
    put('room.details.listingAgent1.email',   p.listingAgentEmail);
    put('room.details.listingAgent1.phone',   p.listingAgentPhone);
    return out;
  };

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Pre-fill from transaction + participants when no draft
  useEffect(() => {
    if (!isOpen || !transaction || draftData) return;

    const getName = (p) => p ? (p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()) : '';
    const byRole = (...roles) => participants.filter(p => {
      const r = (p.role || p.participantRole || p.type || '').toUpperCase().replace(/[\s-]+/g, '_');
      return roles.some(role => r === role);
    });

    const buyers  = byRole('BUYER');
    const sellers = byRole('SELLER');
    const agent   = byRole('BUYER_AGENT', 'BUYERS_AGENT', 'BUYING_AGENT')[0] || null;

    let currentUser = {};
    try { currentUser = JSON.parse(localStorage.getItem('user') || '{}'); } catch { /* ignore */ }
    const currentUserName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();

    const txnAddress = transaction.address || transaction.propertyAddress || '';
    const txnCity    = transaction.city    || '';
    const txnState   = transaction.state   || 'CA';
    const txnZip     = transaction.zipCode || transaction.zip || '';

    setStep(1);
    setDraftId(null);
    setCompletedSteps([]);
    setForm(prev => ({
      ...prev,
      'room.property.location.address1':              txnAddress,
      'room.property.location.city':                   txnCity,
      'room.property.location.state':                  txnState,
      'room.property.location.postalCode':             txnZip,
      'room.offer.contractAmount':                     transaction.listPrice || transaction.price || prev['room.offer.contractAmount'],
      'room.details.buyer1.name':                      buyers[0] ? getName(buyers[0]) : (transaction.buyerName || prev['room.details.buyer1.name']),
      'room.details.buyer2.name':                      buyers[1] ? getName(buyers[1]) : prev['room.details.buyer2.name'],
      'room.details.listingAgent1.name':               sellers[0] ? getName(sellers[0]) : (transaction.sellerName || prev['room.details.listingAgent1.name']),
      'room.details.buyerAgent1.name':                 agent ? getName(agent) : (currentUserName || prev['room.details.buyerAgent1.name']),
      'room.details.designatedbuyerAgent.licenseNum':  currentUser.licenseNumber || currentUser.agentNumber || prev['room.details.designatedbuyerAgent.licenseNum'],
      'room.details.buyerAgent1.company':              currentUser.brokerageName || transaction.brokerageName || prev['room.details.buyerAgent1.company'],
      // Interested-property values take precedence over transaction.* fallbacks (richer source).
      ...mapInterestedPropertyToForm(interestedProperty),
    }));

    // Fetch county from ATTOM when address is available
    const ipMap = mapInterestedPropertyToForm(interestedProperty);
    const coeAddress = ipMap['room.property.location.address1'] || txnAddress;
    const coeCity    = ipMap['room.property.location.city']      || txnCity;
    const coeState   = ipMap['room.property.location.state']     || txnState;
    const coeZip     = ipMap['room.property.location.postalCode'] || txnZip;
    if (coeAddress && !ipMap['room.property.location.county']) {
      fetchCountyFromAttom(coeAddress, coeCity, coeState, coeZip)
        .then(county => { if (county) setForm(prev => ({ ...prev, 'room.property.location.county': county })); });
    }
  }, [isOpen, transaction, draftData, interestedProperty]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fill from draftData (resume existing draft)
  useEffect(() => {
    if (!isOpen) { draftInitializedRef.current = false; return; }
    if (!draftData || draftInitializedRef.current) return;
    draftInitializedRef.current = true;

    setDraftId(draftData.id || null);
    setStep(draftData.currentStep || 1);
    setCompletedSteps(Array.isArray(draftData.completedSteps) ? draftData.completedSteps : []);

    // Single source of truth: the backend's canonical formData map. This avoids the old
    // bug where merging per-step copies let a stale Review-step (step 11) snapshot overwrite
    // freshly edited fields. Legacy drafts (saved before formData existed) fall back to merging
    // step data, but SKIP NOT_STARTED steps so the stale Review snapshot can't win.
    const steps = draftData.steps || {};
    let merged = {};
    if (draftData.formData && typeof draftData.formData === 'object' && Object.keys(draftData.formData).length) {
      merged = { ...draftData.formData };
    } else {
      Object.values(steps).forEach(stepObj => {
        if (stepObj?.data && typeof stepObj.data === 'object' && stepObj.stepStatus !== 'NOT_STARTED') {
          Object.assign(merged, stepObj.data);
        }
      });
    }

    // ── Translate backend auto-fill simple keys → DocuSign tab labels ──────────
    // Backend buildStep1Data/2Data/3Data stores data with simple keys (address, city,
    // buyers[], offerPrice, etc.). On first open those keys are never in the form,
    // so we translate them here. User-saved data (DocuSign labels) overrides the
    // translation via the spread order: { ...backendToDocuSign, ...merged }.
    const backendToDocuSign = {};
    const joinName = (o) => `${o?.firstName || ''} ${o?.lastName || ''}`.trim();

    // Step 1 → Property
    if (merged.address)   backendToDocuSign['room.property.location.address1']   = merged.address;
    if (merged.city)      backendToDocuSign['room.property.location.city']        = merged.city;
    if (merged.state)     backendToDocuSign['room.property.location.state']       = merged.state;
    if (merged.zip)       backendToDocuSign['room.property.location.postalCode']  = merged.zip;
    if (merged.county)    backendToDocuSign['room.property.location.county']      = merged.county;
    if (merged.apn)       backendToDocuSign['room.property.ParcelID']             = merged.apn;
    if (merged.listPrice) backendToDocuSign['room.offer.contractAmount']          = merged.listPrice;

    // Step 2 → Agency
    const buyersArr = Array.isArray(merged.buyers) ? merged.buyers : [];
    if (buyersArr[0]) { const n = joinName(buyersArr[0]); if (n) backendToDocuSign['room.details.buyer1.name'] = n; }
    if (buyersArr[1]) { const n = joinName(buyersArr[1]); if (n) backendToDocuSign['room.details.buyer2.name'] = n; }
    if (merged.buyerAgentName)    backendToDocuSign['room.details.buyerAgent1.name']                = merged.buyerAgentName;
    if (merged.buyerAgentLicense) backendToDocuSign['room.details.designatedbuyerAgent.licenseNum'] = merged.buyerAgentLicense;
    if (merged.buyerBrokerage)    backendToDocuSign['room.details.buyerAgent1.company']              = merged.buyerBrokerage;
    if (merged.listingAgentName)  backendToDocuSign['room.details.listingAgent1.name']               = merged.listingAgentName;
    if (merged.listingOfficeName) backendToDocuSign['room.details.listingAgent1.company']            = merged.listingOfficeName;
    if (merged.listingAgentPhone) backendToDocuSign['room.details.listingAgent1.phone']              = merged.listingAgentPhone;
    if (merged.listingAgentEmail) backendToDocuSign['room.details.listingAgent1.email']              = merged.listingAgentEmail;

    // Step 3 → Price
    if (merged.offerPrice) backendToDocuSign['room.offer.contractAmount'] = merged.offerPrice;

    // Priority (low → high): interested-property auto-fill < backend auto-fill < user-saved (DocuSign labels).
    // interestedProperty fills empty property/listing fields on first launch; saved data always wins on resume.
    const fullMerged = { ...mapInterestedPropertyToForm(interestedProperty), ...backendToDocuSign, ...merged };

    // ── Participant / profile fallbacks ────────────────────────────────────────
    const getName = (p) => p ? (p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim()) : '';
    const byRole = (...roles) => participants.filter(p => {
      const r = (p.role || p.participantRole || p.type || '').toUpperCase().replace(/[\s-]+/g, '_');
      return roles.some(role => r === role);
    });
    const buyers  = byRole('BUYER');
    const sellers = byRole('SELLER');
    const agent   = byRole('BUYER_AGENT', 'BUYERS_AGENT', 'BUYING_AGENT')[0] || null;

    let currentUser = {};
    try { currentUser = JSON.parse(localStorage.getItem('user') || '{}'); } catch { /* ignore */ }
    const currentUserName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim();

    // Use fullMerged (includes translated simple keys) for address resolution
    const resolvedAddress = fullMerged['room.property.location.address1']  || transaction?.address || transaction?.propertyAddress || '';
    const resolvedCity    = fullMerged['room.property.location.city']       || transaction?.city   || '';
    const resolvedState   = fullMerged['room.property.location.state']      || transaction?.state  || 'CA';
    const resolvedZip     = fullMerged['room.property.location.postalCode'] || transaction?.zipCode || transaction?.zip || '';
    const resolvedCounty  = fullMerged['room.property.location.county']     || '';

    // Fetch county from ATTOM when it is missing from the draft
    if (resolvedAddress && !resolvedCounty) {
      fetchCountyFromAttom(resolvedAddress, resolvedCity, resolvedState, resolvedZip)
        .then(county => { if (county) setForm(prev => ({ ...prev, 'room.property.location.county': county })); });
    }

    setForm(prev => ({
      ...prev,
      // Restore all saved fields — DocuSign labels AND translated backend auto-fill
      ...fullMerged,
      // Explicit overrides with participant / profile fallbacks for key identity fields
      'room.property.location.address1':
        fullMerged['room.property.location.address1'] || transaction?.address || transaction?.propertyAddress || prev['room.property.location.address1'],
      'room.property.location.city':
        fullMerged['room.property.location.city']  || transaction?.city  || prev['room.property.location.city'],
      'room.property.location.state':
        fullMerged['room.property.location.state'] || transaction?.state || prev['room.property.location.state'],
      'room.property.location.postalCode':
        fullMerged['room.property.location.postalCode'] || transaction?.zipCode || transaction?.zip || prev['room.property.location.postalCode'],
      'room.details.buyer1.name':
        fullMerged['room.details.buyer1.name'] ||
        (buyers[0] ? getName(buyers[0]) : (transaction?.buyerName || prev['room.details.buyer1.name'])),
      'room.details.buyer2.name':
        fullMerged['room.details.buyer2.name'] ||
        (buyers[1] ? getName(buyers[1]) : prev['room.details.buyer2.name']),
      'room.details.listingAgent1.name':
        fullMerged['room.details.listingAgent1.name'] ||
        (sellers[0] ? getName(sellers[0]) : (transaction?.sellerName || prev['room.details.listingAgent1.name'])),
      'room.details.buyerAgent1.name':
        fullMerged['room.details.buyerAgent1.name'] ||
        (agent ? getName(agent) : (currentUserName || prev['room.details.buyerAgent1.name'])),
      'room.details.designatedbuyerAgent.licenseNum':
        fullMerged['room.details.designatedbuyerAgent.licenseNum'] ||
        currentUser.licenseNumber || currentUser.agentNumber ||
        prev['room.details.designatedbuyerAgent.licenseNum'],
      'room.details.buyerAgent1.company':
        fullMerged['room.details.buyerAgent1.company'] ||
        currentUser.brokerageName || transaction?.brokerageName ||
        prev['room.details.buyerAgent1.company'],
    }));
  }, [isOpen, draftData, interestedProperty]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // ── Step 1 handlers ────────────────────────────────────────────────────────
  const setFirstRateType = (v) => setForm(prev => ({
    ...prev, '_ui.firstLoanRateType': v,
    'additionalFields.AdjustableRateFinancing': v === 'adjustable',
  }));
  const setAddRateType = (v) => setForm(prev => ({
    ...prev, '_ui.addLoanRateType': v,
    'additionalFields.AdjustableRateFinancing2': v === 'adjustable',
  }));
  const setFirstLoanType = (v) => setForm(prev => ({
    ...prev, '_ui.firstLoanType': v,
    'additionalFields.FHALoans':              v === 'fha',
    'additionalFields.VALoans':               v === 'va',
    'additionalFields.SellerFinancingOptions': v === 'seller_financing',
    'additionalFields.OtherLoan':             v === 'other',
  }));
  const setAddLoanType = (v) => setForm(prev => ({
    ...prev, '_ui.addLoanType': v,
    'additionalFields.SellerFinancingOptions2': v === 'seller_financing',
    'additionalFields.OtherLoan2':              v === 'other',
  }));

  // ── Step 2 handlers ────────────────────────────────────────────────────────
  const setH1Timing = (v) => setForm(prev => ({
    ...prev, '_ui.h1Timing': v,
    'additionalFields.OtherNumberOfDays': v === 'days',
  }));
  const setH2Timing = (v) => setForm(prev => ({
    ...prev, '_ui.h2Timing': v,
    'additionalFields.OtherNumberOfDays2': v === 'days',
  }));
  const setH3Timing = (v) => setForm(prev => ({
    ...prev, '_ui.h3Timing': v,
    'additionalFields.OtherNumberOfDays3': v === 'days',
  }));
  const setH3Approval = (v) => setForm(prev => ({
    ...prev, '_ui.h3Approval': v,
    'additionalFields.BuyerFinancingPreapproval':  v === 'prequalification',
    'additionalFields.BuyerFinancingPreapproval2': v === 'preapproval',
    'additionalFields.BuyerFinancingPreapproval3': v === 'fully_underwritten',
  }));
  const setM1Possession = (v) => setForm(prev => ({
    ...prev, '_ui.m1Possession': v,
    'additionalFields.PossessionAtClosing': v === 'recordation',
    'additionalFields.PossessionOther':     v === 'custom',
  }));
  const setM2Timing = (v) => setForm(prev => ({
    ...prev, '_ui.m2Timing': v,
    'additionalFields.OccupiedByOwner':  v === 'short',
    'additionalFields.OccupiedByOwner2': v === 'long',
  }));

  const handleContinue = async () => {
    if (!draftId || !transaction?.id) {
      const next = step + 1;
      const updated = completedSteps.includes(step) ? completedSteps : [...completedSteps, step];
      setCompletedSteps(updated);
      setStep(next);
      onProgressUpdate && onProgressUpdate(updated, next);
      return;
    }
    setSaveError(null);
    setIsSaving(true);
    try {
      const token = await getFirebaseToken();
      await apiClient.put(
        API_ENDPOINTS.rpaWizardSaveStep(transaction.id, draftId, step, true),
        { data: form },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = completedSteps.includes(step) ? completedSteps : [...completedSteps, step];
      setCompletedSteps(updated);
      const next = step + 1;
      onProgressUpdate && onProgressUpdate(updated, next);
      setStep(next);
    } catch (err) {
      const serverMsg = err?.response?.data?.message;
      setSaveError(serverMsg || `Failed to save Step ${step}. Please try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  // Fire-and-forget save of the step being left, so in-progress edits aren't
  // silently lost when navigating backward or jumping via the step bar.
  const saveCurrentStepFireAndForget = () => {
    if (draftId && transaction?.id) {
      getFirebaseToken().then(token => {
        apiClient.put(
          API_ENDPOINTS.rpaWizardSaveStep(transaction.id, draftId, step, false),
          { data: form },
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => {});
      }).catch(() => {});
    }
  };

  const handleBack = () => {
    setSaveError(null);
    saveCurrentStepFireAndForget();
    const prev = step - 1;
    setStep(prev);
    onProgressUpdate && onProgressUpdate(completedSteps, prev);
  };

  const handleSubmit = async () => {
    if (!confirmed || !draftId || !transaction?.id) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      const token = await getFirebaseToken();
      await apiClient.post(
        API_ENDPOINTS.rpaWizardSubmit(transaction.id, draftId),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updated = completedSteps.includes(step) ? completedSteps : [...completedSteps, step];
      setCompletedSteps(updated);
      onProgressUpdate && onProgressUpdate(updated, step);
      onComplete ? onComplete(form) : onClose();
    } catch (err) {
      const serverMsg = err?.response?.data?.message;
      setSaveError(serverMsg || 'Failed to submit. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (draftId && transaction?.id) {
      getFirebaseToken().then(token => {
        apiClient.put(
          API_ENDPOINTS.rpaWizardSaveStep(transaction.id, draftId, step, false),
          { data: form },
          { headers: { Authorization: `Bearer ${token}` } }
        ).catch(() => {});
      }).catch(() => {});
    }
    onProgressUpdate && onProgressUpdate(completedSteps, step);
    onClose && onClose();
  };

  return (
    <div className="rpa-overlay">
      <div className="rpa-modal">

        {/* ── Header ── */}
        <div className="rpa-header">
          <div className="rpa-header-left">
            <div className="rpa-header-title">{envelopeId ? 'Update Partial RPA' : 'Offer Creation'}</div>
            <div className="rpa-header-sub">C.A.R. Form RPA Rev. 12/25</div>
          </div>
          <div className="rpa-header-right">
<button className="rpa-close-btn" onClick={handleClose} aria-label="Close">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>

        {/* ── Progress track ── */}
        <div className="rpa-progress-track">
          <div className="rpa-progress-bar" style={{ width: `${Math.round(((step - 1) / 10) * 100)}%` }} />
        </div>

        {/* ── Body ── */}
        <div className="rpa-body">

          {/* ── Step bar ── */}
          <nav className="rpa-steps-bar" aria-label="Wizard steps">
            {STEPS.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`rpa-step${step === s.id ? ' active' : step > s.id ? ' completed' : ' todo'}`}
                onClick={() => { saveCurrentStepFireAndForget(); setStep(s.id); }}
                aria-current={step === s.id ? 'step' : undefined}
              >
                <div className="rpa-step-circle">{s.icon}</div>
                <div className="rpa-step-meta">
                  <span className="rpa-step-label">STEP {s.id}</span>
                  <span className="rpa-step-name">{s.title}</span>
                </div>
                {step > s.id && <span className="rpa-step-check">✓</span>}
              </button>
            ))}
          </nav>

          {/* ── Content ── */}
          <div className="rpa-content">
            <div className="rpa-step-content">
              <h2 className="rpa-step-heading">{STEPS[step - 1].title}</h2>

            {/* ════════════════ STEP 1 — OFFER & PROPERTY ════════════════ */}
            {step === 1 && (
              <>

                {/* ════ SECTION 1 — OFFER ════ */}
                <SectionCard num="1" title="Offer" note="Parties & Property identification">

                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Date Prepared" hint="room.offer.presentedofferdate">
                      <input className="rpa-input" type="date"
                        value={form['room.offer.presentedofferdate']}
                        onChange={e => set('room.offer.presentedofferdate', e.target.value)} />
                    </Fld>
                    <Fld label="Contract Date" hint="room.offer.contractDate">
                      <input className="rpa-input" type="date"
                        value={form['room.offer.contractDate']}
                        onChange={e => set('room.offer.contractDate', e.target.value)} />
                    </Fld>
                  </div>

                  <div className="rpa-field-row rpa-cols-2" style={{ marginTop: 14 }}>
                    <Fld label="Buyer 1 Name" hint="room.details.buyer1.name">
                      <input className="rpa-input" type="text" placeholder="Full legal name"
                        value={form['room.details.buyer1.name']}
                        onChange={e => set('room.details.buyer1.name', e.target.value)} />
                    </Fld>
                    <Fld label="Buyer 2 Name" hint="room.details.buyer2.name">
                      <input className="rpa-input" type="text" placeholder="Full legal name (if any)"
                        value={form['room.details.buyer2.name']}
                        onChange={e => set('room.details.buyer2.name', e.target.value)} />
                    </Fld>
                  </div>

                  <div className="rpa-field-row rpa-cols-2" style={{ marginTop: 14 }}>
                    <Fld label="Seller 1 Name" hint="room.details.seller1.name">
                      <input className="rpa-input" type="text" placeholder="Full legal name"
                        value={form['room.details.seller1.name']}
                        onChange={e => set('room.details.seller1.name', e.target.value)} />
                    </Fld>
                    <Fld label="Seller 2 Name" hint="room.details.seller2.name">
                      <input className="rpa-input" type="text" placeholder="Full legal name (if any)"
                        value={form['room.details.seller2.name']}
                        onChange={e => set('room.details.seller2.name', e.target.value)} />
                    </Fld>
                  </div>

                  <div className="rpa-field-row" style={{ marginTop: 14 }}>
                    <Fld label="Property Address (Line 1)" hint="room.property.location.address1">
                      <input className="rpa-input" type="text" placeholder="Street address"
                        value={form['room.property.location.address1']}
                        onChange={e => set('room.property.location.address1', e.target.value)} />
                    </Fld>
                  </div>

                  <div className="rpa-field-row" style={{ marginTop: 8 }}>
                    <Fld label="Address Line 2 (Apt, Unit, etc.)" hint="room.property.location.address2">
                      <input className="rpa-input" type="text" placeholder="Apt / Suite / Unit (optional)"
                        value={form['room.property.location.address2']}
                        onChange={e => set('room.property.location.address2', e.target.value)} />
                    </Fld>
                  </div>

                  <div className="rpa-field-row rpa-cols-4" style={{ marginTop: 14 }}>
                    <Fld label="State">
                      <StateSelect stateKey="room.property.location.state" cityKey="room.property.location.city" form={form} set={set} />
                    </Fld>
                    <Fld label="City">
                      <WizardCitySelect cityKey="room.property.location.city" stateKey="room.property.location.state" form={form} set={set} />
                    </Fld>
                    <Fld label="County">
                      <input className="rpa-input" type="text" placeholder="County"
                        value={form['room.property.location.county']}
                        onChange={e => set('room.property.location.county', e.target.value)} />
                    </Fld>
                    <Fld label="Zip Code">
                      <input className="rpa-input" type="text" placeholder="12345"
                        value={form['room.property.location.postalCode']}
                        onChange={e => set('room.property.location.postalCode', e.target.value)} />
                    </Fld>
                  </div>

                  <div className="rpa-field-row" style={{ marginTop: 14 }}>
                    <Fld label="Assessor's Parcel No(s)" hint="room.property.ParcelID">
                      <input className="rpa-input" type="text" placeholder="e.g. 123-456-789"
                        value={form['room.property.ParcelID']}
                        onChange={e => set('room.property.ParcelID', e.target.value)} />
                    </Fld>
                  </div>

                </SectionCard>

              </>
            )}

            {/* ════════════════ STEP 2 — AGENCY ════════════════ */}
            {step === 2 && (
              <>

                {/* ════ SECTION 2 — AGENCY ════ */}
                <SectionCard num="2" title="Agency" note="Brokerage & agent confirmation">
                  <p className="rpa-field-hint" style={{ marginBottom: 14 }}>
                    Parties acknowledge receipt of C.A.R. Form AD. The following agency relationships are confirmed for this transaction.
                  </p>

                  {/* Seller side */}
                  <AgencyParty title="Seller's Representation">
                    <div className="rpa-field-row rpa-cols-2" style={{ marginBottom: 12 }}>
                      <Fld label="Seller's Brokerage Firm" hint="room.details.listingAgent1.company">
                        <input className="rpa-input" type="text" placeholder="Brokerage firm name"
                          value={form['room.details.listingAgent1.company']}
                          onChange={e => set('room.details.listingAgent1.company', e.target.value)} />
                      </Fld>
                      <Fld label="License Number" hint="room.details.sellerBrokerage.LicenseNum">
                        <input className="rpa-input" type="text" placeholder="DRE Lic. #"
                          value={form['room.details.sellerBrokerage.LicenseNum']}
                          onChange={e => set('room.details.sellerBrokerage.LicenseNum', e.target.value)} />
                      </Fld>
                    </div>
                    <Fld label="Brokerage Role" hint="additionalFields.SellerBrokerageType (radio)">
                      <RadioGroup
                        value={form['additionalFields.SellerBrokerageType']}
                        onChange={v => set('additionalFields.SellerBrokerageType', v)}
                        options={[
                          { value: 'Seller', label: 'Represents the Seller only' },
                          { value: 'Both the Buyer and Seller', label: 'Dual Agent — both Buyer and Seller' },
                        ]}
                      />
                    </Fld>
                    <Hr />
                    <div className="rpa-field-row rpa-cols-2" style={{ marginBottom: 12 }}>
                      <Fld label="Seller's Agent" hint="room.details.listingAgent1.name">
                        <input className="rpa-input" type="text" placeholder="Agent name"
                          value={form['room.details.listingAgent1.name']}
                          onChange={e => set('room.details.listingAgent1.name', e.target.value)} />
                      </Fld>
                      <Fld label="License Number" hint="room.details.designatedsellerAgent.licenseNum">
                        <input className="rpa-input" type="text" placeholder="DRE Lic. #"
                          value={form['room.details.designatedsellerAgent.licenseNum']}
                          onChange={e => set('room.details.designatedsellerAgent.licenseNum', e.target.value)} />
                      </Fld>
                    </div>
                    <Fld label="Agent Role" hint="additionalFields.AgentType (radio)">
                      <RadioGroup
                        value={form['additionalFields.AgentType']}
                        onChange={v => set('additionalFields.AgentType', v)}
                        options={[
                          { value: "Seller's Agent", label: "Seller's Agent (salesperson or broker-associate)" },
                          { value: "Both the Buyer's and Seller's Agent", label: "Dual Agent — both Buyer's and Seller's Agent" },
                        ]}
                      />
                    </Fld>
                  </AgencyParty>

                  {/* Buyer side */}
                  <AgencyParty title="Buyer's Representation">
                    <div className="rpa-field-row rpa-cols-2" style={{ marginBottom: 12 }}>
                      <Fld label="Buyer's Brokerage Firm" hint="room.details.buyerAgent1.company">
                        <input className="rpa-input" type="text" placeholder="Brokerage firm name"
                          value={form['room.details.buyerAgent1.company']}
                          onChange={e => set('room.details.buyerAgent1.company', e.target.value)} />
                      </Fld>
                      <Fld label="License Number" hint="room.details.buyerBrokerage.licenseNum">
                        <input className="rpa-input" type="text" placeholder="DRE Lic. #"
                          value={form['room.details.buyerBrokerage.licenseNum']}
                          onChange={e => set('room.details.buyerBrokerage.licenseNum', e.target.value)} />
                      </Fld>
                    </div>
                    <Fld label="Brokerage Role" hint="additionalFields.BuyerBrokerageType (radio)">
                      <RadioGroup
                        value={form['additionalFields.BuyerBrokerageType']}
                        onChange={v => set('additionalFields.BuyerBrokerageType', v)}
                        options={[
                          { value: 'Buyer', label: 'Represents the Buyer only' },
                          { value: 'Both the Buyer and Seller', label: 'Dual Agent — both Buyer and Seller' },
                        ]}
                      />
                    </Fld>
                    <Hr />
                    <div className="rpa-field-row rpa-cols-2" style={{ marginBottom: 12 }}>
                      <Fld label="Buyer's Agent" hint="room.details.buyerAgent1.name">
                        <input className="rpa-input" type="text" placeholder="Agent name"
                          value={form['room.details.buyerAgent1.name']}
                          onChange={e => set('room.details.buyerAgent1.name', e.target.value)} />
                      </Fld>
                      <Fld label="License Number" hint="room.details.designatedbuyerAgent.licenseNum">
                        <input className="rpa-input" type="text" placeholder="DRE Lic. #"
                          value={form['room.details.designatedbuyerAgent.licenseNum']}
                          onChange={e => set('room.details.designatedbuyerAgent.licenseNum', e.target.value)} />
                      </Fld>
                    </div>
                    <Fld label="Agent Role" hint="additionalFields.AgentType2 (radio)">
                      <RadioGroup
                        value={form['additionalFields.AgentType2']}
                        onChange={v => set('additionalFields.AgentType2', v)}
                        options={[
                          { value: "the Buyer's Agent", label: "Buyer's Agent (salesperson or broker-associate)" },
                          { value: "Both the Buyer's and Seller's Agent", label: "Dual Agent — both Buyer's and Seller's Agent" },
                        ]}
                      />
                    </Fld>
                  </AgencyParty>

                  {/* Compensation + Additional disclosures */}
                  <Fld label="Buyer Broker Compensation Paid By" hint="additionalFields.SellerorBuyerRadio (radio)">
                    <RadioGroup
                      value={form['additionalFields.SellerorBuyerRadio']}
                      onChange={v => set('additionalFields.SellerorBuyerRadio', v)}
                      options={[
                        { value: 'Seller', label: "Seller pays Buyer's Brokerage compensation" },
                        { value: 'Buyer',  label: "Buyer pays Buyer's Brokerage compensation" },
                      ]}
                      inline
                    />
                  </Fld>

                  <Fld label="Additional Disclosures">
                    <CheckRow
                      checked={form['additionalFields.MultipleRepresentationWithDesignatedAgency']}
                      onChange={v => set('additionalFields.MultipleRepresentationWithDesignatedAgency', v)}
                      label="More than one Brokerage represents — See C.A.R. Form ABA"
                      hint="additionalFields.MultipleRepresentationWithDesignatedAgency"
                    />
                    {form['additionalFields.MultipleRepresentationWithDesignatedAgency'] && (
                      <div style={{ marginLeft: 24, marginTop: 8 }}>
                        <Fld hint="additionalFields.SellerorBuyerRadio (radio)">
                          <RadioGroup
                            value={form['additionalFields.SellerorBuyerRadio']}
                            onChange={v => set('additionalFields.SellerorBuyerRadio', v)}
                            options={[
                              { value: 'Seller', label: 'Seller' },
                              { value: 'Buyer',  label: 'Buyer'  },
                            ]}
                            inline
                          />
                        </Fld>
                      </div>
                    )}
                  </Fld>

                </SectionCard>

              </>
            )}

            {/* ════════════════ STEP 3 — PRICE & EMD ════════════════ */}
            {step === 3 && (
              <>

                {/* ════ SECTION 3 — PRICE & EMD ════ */}
                <SectionCard num="3" title="Price & EMD" note="Rows A–D · Purchase price, Close of Escrow, Expiration & Initial deposit">

                  {/* Row A */}
                  <DivLabel>Row A &nbsp;·&nbsp; Purchase price</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Purchase Price" hint="room.offer.contractAmount">
                      <input className="rpa-input" type="text" placeholder="0.00"
                        value={form['room.offer.contractAmount']}
                        onChange={e => {
                          const v = e.target.value;
                          setForm(prev => ({ ...prev, 'room.offer.contractAmount': v, 'room.offer.PurchasePriceAllocationTotalAmount': v }));
                        }} />
                    </Fld>
                    <div style={{ paddingTop: 22 }}>
                      <CheckRow
                        checked={form['additionalFields.MethodofPaymentCash']}
                        onChange={v => set('additionalFields.MethodofPaymentCash', v)}
                        label="All-cash offer — no financing contingency"
                        hint="additionalFields.MethodofPaymentCash"
                      />
                    </div>
                  </div>

                  {/* Row B */}
                  <DivLabel>Row B &nbsp;·&nbsp; Close of Escrow (COE)</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Days after acceptance" hint="room.offer.ClosingPeriodFromDateOfAcceptance">
                      <input className="rpa-input" type="number" placeholder="e.g. 30" min="1" max="365"
                        value={form['room.offer.ClosingPeriodFromDateOfAcceptance']}
                        onChange={e => { set('room.offer.ClosingPeriodFromDateOfAcceptance', e.target.value); if (e.target.value) set('room.offer.COEDate', ''); }} />
                    </Fld>
                    <Fld label="Or specific COE date" hint="room.offer.COEDate">
                      <input className="rpa-input" type="date"
                        value={form['room.offer.COEDate']}
                        onChange={e => { set('room.offer.COEDate', e.target.value); if (e.target.value) set('room.offer.ClosingPeriodFromDateOfAcceptance', ''); }} />
                    </Fld>
                  </div>

                  {/* Row C */}
                  <DivLabel>Row C &nbsp;·&nbsp; Expiration of offer</DivLabel>
                  <div className="rpa-field-row rpa-cols-3">
                    <Fld label="Custom expiry date" hint="room.offer.offerExpireDate">
                      <input className="rpa-input" type="date"
                        value={form['room.offer.offerExpireDate']}
                        onChange={e => set('room.offer.offerExpireDate', e.target.value)} />
                    </Fld>
                    <Fld label="Expiry time" hint="room.offer.OfferExpireTime">
                      <input className="rpa-input" type="time"
                        value={form['room.offer.OfferExpireTime']}
                        onChange={e => set('room.offer.OfferExpireTime', e.target.value)} />
                    </Fld>
                    <Fld label="AM / PM" hint="additionalFields.OfferExpireTime (radio)">
                      <RadioGroup
                        value={form['additionalFields.OfferExpireTime']}
                        onChange={v => set('additionalFields.OfferExpireTime', v)}
                        options={[{ value: 'AM', label: 'AM' }, { value: 'PM', label: 'PM' }]}
                        inline
                      />
                    </Fld>
                  </div>

                  {/* Row D */}
                  <DivLabel>Row D &nbsp;·&nbsp; Initial deposit</DivLabel>
                  <div className="rpa-field-row rpa-cols-3">
                    <Fld label="Initial deposit amount" hint="room.offer.earnestMoneyAmount">
                      <input className="rpa-input" type="text" placeholder="0.00"
                        value={form['room.offer.earnestMoneyAmount']}
                        onChange={e => set('room.offer.earnestMoneyAmount', e.target.value)} />
                    </Fld>
                    <Fld label="% of purchase price" hint="room.offer.earnestmoneypercentofpurchase">
                      <input className="rpa-input" type="number" placeholder="e.g. 3" min="0" max="100" step="0.1"
                        value={form['room.offer.earnestmoneypercentofpurchase']}
                        onChange={e => set('room.offer.earnestmoneypercentofpurchase', e.target.value)} />
                    </Fld>
                    <Fld label="Business days to deliver" hint="room.offer.earnestmoneyDueDays">
                      <input className="rpa-input" type="number" placeholder="3" min="1"
                        value={form['room.offer.earnestmoneyDueDays']}
                        onChange={e => set('room.offer.earnestmoneyDueDays', e.target.value)} />
                    </Fld>
                  </div>

                </SectionCard>

              </>
            )}

            {/* ════════════════ STEP 4 — FINANCING ════════════════ */}
            {step === 4 && (
              <>

                <SectionCard num="3" title="Financing" note="Rows E–F · Loan details, occupancy type & down payment">

                  {/* Row E1 */}
                  <DivLabel>Row E1 &nbsp;·&nbsp; First loan</DivLabel>
                  <div className="rpa-field-row rpa-cols-3">
                    <Fld label="First loan amount" hint="room.offer.firstMortgageLoanAmount">
                      <input className="rpa-input" type="text" placeholder="0.00"
                        value={form['room.offer.firstMortgageLoanAmount']}
                        onChange={e => set('room.offer.firstMortgageLoanAmount', e.target.value)} />
                    </Fld>
                    <Fld label="% of purchase price" hint="room.offer.loanPercentofPurchasePrice">
                      <input className="rpa-input" type="number" placeholder="e.g. 80" min="0" max="100" step="0.1"
                        value={form['room.offer.loanPercentofPurchasePrice']}
                        onChange={e => set('room.offer.loanPercentofPurchasePrice', e.target.value)} />
                    </Fld>
                    <Fld label="Days after acceptance" hint="room.offer.LoanApplicationPeriod">
                      <input className="rpa-input" type="number" placeholder="17" min="1"
                        value={form['room.offer.LoanApplicationPeriod']}
                        onChange={e => set('room.offer.LoanApplicationPeriod', e.target.value)} />
                    </Fld>
                  </div>
                  <div className="rpa-field-row rpa-cols-3" style={{ marginTop: 12 }}>
                    <Fld label="Interest rate not to exceed (%)" hint="room.offer.MaxInitialAdjustableLoanPercent">
                      <input className="rpa-input" type="number" placeholder="e.g. 7.5" min="0" max="30" step="0.1"
                        value={form['room.offer.MaxInitialAdjustableLoanPercent']}
                        onChange={e => set('room.offer.MaxInitialAdjustableLoanPercent', e.target.value)} />
                    </Fld>
                    <Fld label="Points (max buyer pays)" hint="room.offer.MaxPointsBuyerPays">
                      <input className="rpa-input" type="number" placeholder="e.g. 1" min="0" max="10" step="0.25"
                        value={form['room.offer.MaxPointsBuyerPays']}
                        onChange={e => set('room.offer.MaxPointsBuyerPays', e.target.value)} />
                    </Fld>
                    <Fld label="Rate type" hint="additionalFields.AdjustableRateFinancing (checkbox)">
                      <RadioGroup
                        value={form['_ui.firstLoanRateType']}
                        onChange={setFirstRateType}
                        options={[{ value: 'fixed', label: 'Fixed' }, { value: 'adjustable', label: 'Initial adjustable' }]}
                        inline
                      />
                    </Fld>
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <Fld label="Loan type (Row E1)" hint="additionalFields.FHALoans / VALoans / SellerFinancingOptions / OtherLoan">
                      <RadioGroup
                        value={form['_ui.firstLoanType']}
                        onChange={setFirstLoanType}
                        options={[
                          { value: 'conventional',      label: 'Conventional' },
                          { value: 'fha',               label: 'FHA (attach FVAC / HID)' },
                          { value: 'va',                label: 'VA (attach FVAC)' },
                          { value: 'seller_financing',  label: 'Seller Financing (attach SFA)' },
                          { value: 'other',             label: 'Other' },
                        ]}
                      />
                    </Fld>
                  </div>
                  {form['_ui.firstLoanType'] === 'other' && (
                    <div style={{ marginTop: 10 }}>
                      <Fld label="Other loan type — specify" hint="room.offer.OtherLoanType">
                        <input className="rpa-input" type="text" placeholder="Describe other financing type"
                          value={form['room.offer.OtherLoanType']}
                          onChange={e => set('room.offer.OtherLoanType', e.target.value)} />
                      </Fld>
                    </div>
                  )}

                  {/* Row E2 */}
                  <DivLabel>Row E2 &nbsp;·&nbsp; Additional financed amount</DivLabel>
                  <div className="rpa-field-row rpa-cols-3">
                    <Fld label="Additional loan amount" hint="room.offer.AmountFinanced">
                      <input className="rpa-input" type="text" placeholder="0.00"
                        value={form['room.offer.AmountFinanced']}
                        onChange={e => set('room.offer.AmountFinanced', e.target.value)} />
                    </Fld>
                    <Fld label="% of purchase price" hint="room.offer.loanPercentofPurchasePrice2">
                      <input className="rpa-input" type="number" placeholder="e.g. 10" min="0" max="100" step="0.1"
                        value={form['room.offer.loanPercentofPurchasePrice2']}
                        onChange={e => set('room.offer.loanPercentofPurchasePrice2', e.target.value)} />
                    </Fld>
                    <Fld label="Days after acceptance" hint="room.offer.NumberOfDaysAfterAcceptancePeriod">
                      <input className="rpa-input" type="number" placeholder="17" min="1"
                        value={form['room.offer.NumberOfDaysAfterAcceptancePeriod']}
                        onChange={e => set('room.offer.NumberOfDaysAfterAcceptancePeriod', e.target.value)} />
                    </Fld>
                  </div>
                  <div className="rpa-field-row rpa-cols-2" style={{ marginTop: 12 }}>
                    <Fld label="Interest rate not to exceed (%)" hint="room.offer.secondmortgageinterestrateperAnnum">
                      <input className="rpa-input" type="number" placeholder="e.g. 8.0" min="0" max="30" step="0.1"
                        value={form['room.offer.secondmortgageinterestrateperAnnum']}
                        onChange={e => set('room.offer.secondmortgageinterestrateperAnnum', e.target.value)} />
                    </Fld>
                    <Fld label="Points (max buyer pays)" hint="room.offer.MaxPointsBuyerPays2">
                      <input className="rpa-input" type="number" placeholder="e.g. 1" min="0" max="10" step="0.25"
                        value={form['room.offer.MaxPointsBuyerPays2']}
                        onChange={e => set('room.offer.MaxPointsBuyerPays2', e.target.value)} />
                    </Fld>
                  </div>
                  <div className="rpa-field-row rpa-cols-2" style={{ marginTop: 12 }}>
                    <Fld label="Rate type" hint="additionalFields.AdjustableRateFinancing2 (checkbox)">
                      <RadioGroup
                        value={form['_ui.addLoanRateType']}
                        onChange={setAddRateType}
                        options={[{ value: 'fixed', label: 'Fixed' }, { value: 'adjustable', label: 'Initial adjustable' }]}
                        inline
                      />
                    </Fld>
                    <Fld label="Loan type (Row E2)" hint="additionalFields.SellerFinancingOptions2 / OtherLoan2">
                      <RadioGroup
                        value={form['_ui.addLoanType']}
                        onChange={setAddLoanType}
                        options={[
                          { value: 'conventional',     label: 'Conventional' },
                          { value: 'seller_financing', label: 'Seller Financing (attach SFA)' },
                          { value: 'other',            label: 'Other' },
                        ]}
                        inline
                      />
                    </Fld>
                  </div>
                  {form['_ui.addLoanType'] === 'other' && (
                    <div style={{ marginTop: 10 }}>
                      <Fld label="Other loan type — specify" hint="room.offer.OtherLoanType2">
                        <input className="rpa-input" type="text" placeholder="Describe other financing type"
                          value={form['room.offer.OtherLoanType2']}
                          onChange={e => set('room.offer.OtherLoanType2', e.target.value)} />
                      </Fld>
                    </div>
                  )}

                  {/* Row E3 */}
                  <DivLabel>Row E3 &nbsp;·&nbsp; Occupancy type</DivLabel>
                  <Fld hint="additionalFields.OccupancyType (radio)">
                    <RadioGroup
                      value={form['additionalFields.OccupancyType']}
                      onChange={v => set('additionalFields.OccupancyType', v)}
                      options={[
                        { value: '',           label: 'Primary residence' },
                        { value: 'Secondary',  label: 'Secondary residence' },
                        { value: 'Investment', label: 'Investment property' },
                      ]}
                      inline
                    />
                  </Fld>

                  {/* Row F */}
                  <DivLabel>Row F &nbsp;·&nbsp; Down payment &amp; total</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Balance of down payment" hint="room.offer.BalanceOfPurchasePrice">
                      <input className="rpa-input" type="text" placeholder="0.00"
                        value={form['room.offer.BalanceOfPurchasePrice']}
                        onChange={e => set('room.offer.BalanceOfPurchasePrice', e.target.value)} />
                    </Fld>
                    <Fld label="Purchase price total" hint="room.offer.PurchasePriceAllocationTotalAmount">
                      <input className="rpa-input" type="text" placeholder="0.00" style={{ fontWeight: 600 }}
                        value={form['room.offer.PurchasePriceAllocationTotalAmount']}
                        onChange={e => set('room.offer.PurchasePriceAllocationTotalAmount', e.target.value)} />
                    </Fld>
                  </div>

                </SectionCard>

              </>
            )}

            {/* ════════════════ STEP 5 — SELLER PAYMENT & VERIFICATION ════════════════ */}
            {step === 5 && (
              <>

                {/* ════ SECTION G — SELLER PAYMENT ════ */}
                <SectionCard num="G" title="Seller Payment to Cover Buyer Expenses" note="Para 5E · 18A">

                  <DivLabel>G(1) &nbsp;·&nbsp; Seller Credit to Buyer — Para 5E</DivLabel>
                  <CheckRow
                    checked={form['additionalFields.CreditReport']}
                    onChange={v => set('additionalFields.CreditReport', v)}
                    label="Seller Credit to Buyer — for closing costs"
                    hint="additionalFields.CreditReport"
                  />
                  {form['additionalFields.CreditReport'] && (
                    <div style={{ marginTop: 10 }}>
                      <Fld label="Seller credit amount ($)" hint="room.offer.SellerEstimatedCreditToBuyer">
                        <input className="rpa-input" type="text" placeholder="0.00"
                          value={form['room.offer.SellerEstimatedCreditToBuyer']}
                          onChange={e => set('room.offer.SellerEstimatedCreditToBuyer', e.target.value)} />
                      </Fld>
                    </div>
                  )}

                  <DivLabel>G(2) &nbsp;·&nbsp; Additional Seller Credit Terms</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Additional credit terms" hint="room.offer.OtherCreditDocuments">
                      <input className="rpa-input" type="text" placeholder="Additional terms beyond G(1)"
                        value={form['room.offer.OtherCreditDocuments']}
                        onChange={e => set('room.offer.OtherCreditDocuments', e.target.value)} />
                    </Fld>
                    <Fld label="Other financing terms" hint="room.offer.OtherFinancingTerms">
                      <input className="rpa-input" type="text" placeholder="Other financing terms"
                        value={form['room.offer.OtherFinancingTerms']}
                        onChange={e => set('room.offer.OtherFinancingTerms', e.target.value)} />
                    </Fld>
                  </div>

                  <DivLabel>G(3) &nbsp;·&nbsp; Seller Payment to Compensate Buyer's Broker — Para 18A</DivLabel>
                  <CheckRow
                    checked={form['additionalFields.BuyerBrokerageFeePaidbySeller']}
                    onChange={v => set('additionalFields.BuyerBrokerageFeePaidbySeller', v)}
                    label="Seller agrees to pay Buyer's Broker out of the transaction proceeds"
                    hint="additionalFields.BuyerBrokerageFeePaidbySeller"
                  />
                  {form['additionalFields.BuyerBrokerageFeePaidbySeller'] && (
                    <div style={{ marginTop: 10 }}>
                      <div className="rpa-field-row rpa-cols-2">
                        <Fld label="% of final purchase price" hint="room.additionalInformation.buyerSideCommission">
                          <input className="rpa-input" type="number" placeholder="e.g. 2.5" min="0" max="10" step="0.1"
                            value={form['room.additionalInformation.buyerSideCommission']}
                            onChange={e => set('room.additionalInformation.buyerSideCommission', e.target.value)} />
                        </Fld>
                        <Fld label="AND if applicable ($)" hint="room.offer.salecommissionAmount">
                          <input className="rpa-input" type="text" placeholder="0.00"
                            value={form['room.offer.salecommissionAmount']}
                            onChange={e => set('room.offer.salecommissionAmount', e.target.value)} />
                        </Fld>
                      </div>
                      <div className="rpa-field-row rpa-cols-2" style={{ marginTop: 10 }}>
                        <div style={{ paddingTop: 22 }}>
                          <CheckRow
                            checked={form['additionalFields.CooperativeBrokerCompensation']}
                            onChange={v => set('additionalFields.CooperativeBrokerCompensation', v)}
                            label="OR — Use fixed alternate amount"
                            hint="additionalFields.CooperativeBrokerCompensation"
                          />
                        </div>
                        <Fld label="Fixed alternate amount ($)" hint="room.offer.additionalsalecommissionAmount">
                          <input className="rpa-input" type="text" placeholder="0.00"
                            value={form['room.offer.additionalsalecommissionAmount']}
                            onChange={e => set('room.offer.additionalsalecommissionAmount', e.target.value)} />
                        </Fld>
                      </div>
                    </div>
                  )}

                </SectionCard>

                {/* ════ SECTION H — VERIFICATION ════ */}
                <SectionCard num="H" title="Verification of Funds & Financing" note="Para 5B · 6A · 6B">

                  <DivLabel>H(1) &nbsp;·&nbsp; Verification of All Cash — Para 5B</DivLabel>
                  <Fld label="Delivery timing" hint="additionalFields.OtherNumberOfDays">
                    <RadioGroup
                      value={form['_ui.h1Timing']}
                      onChange={setH1Timing}
                      options={[
                        { value: 'attached', label: 'Attached to the offer' },
                        { value: 'days',     label: 'Days after Acceptance' },
                      ]}
                      inline
                    />
                  </Fld>
                  {form['_ui.h1Timing'] === 'days' && (
                    <div style={{ marginTop: 8, maxWidth: 200 }}>
                      <Fld label="Days" hint="room.offer.UnencomberedFundsEvidenceDays">
                        <input className="rpa-input" type="number" placeholder="3" min="1"
                          value={form['room.offer.UnencomberedFundsEvidenceDays']}
                          onChange={e => set('room.offer.UnencomberedFundsEvidenceDays', e.target.value)} />
                      </Fld>
                    </div>
                  )}

                  <Hr />

                  <DivLabel>H(2) &nbsp;·&nbsp; Verification of Down Payment and Closing Costs — Para 6A</DivLabel>
                  <Fld label="Delivery timing" hint="additionalFields.OtherNumberOfDays2">
                    <RadioGroup
                      value={form['_ui.h2Timing']}
                      onChange={setH2Timing}
                      options={[
                        { value: 'attached', label: 'Attached to the offer' },
                        { value: 'days',     label: 'Days after Acceptance' },
                      ]}
                      inline
                    />
                  </Fld>
                  {form['_ui.h2Timing'] === 'days' && (
                    <div style={{ marginTop: 8, maxWidth: 200 }}>
                      <Fld label="Days" hint="room.offer.UnencomberedFundsEvidenceDays2">
                        <input className="rpa-input" type="number" placeholder="3" min="1"
                          value={form['room.offer.UnencomberedFundsEvidenceDays2']}
                          onChange={e => set('room.offer.UnencomberedFundsEvidenceDays2', e.target.value)} />
                      </Fld>
                    </div>
                  )}

                  <Hr />

                  <DivLabel>H(3) &nbsp;·&nbsp; Verification of Loan Application — Para 6B</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <div>
                      <Fld label="Delivery timing" hint="additionalFields.OtherNumberOfDays3">
                        <RadioGroup
                          value={form['_ui.h3Timing']}
                          onChange={setH3Timing}
                          options={[
                            { value: 'attached', label: 'Attached to the offer' },
                            { value: 'days',     label: 'Days after Acceptance' },
                          ]}
                        />
                      </Fld>
                      {form['_ui.h3Timing'] === 'days' && (
                        <div style={{ marginTop: 8 }}>
                          <Fld label="Days" hint="room.offer.LoanApplicationPeriod">
                            <input className="rpa-input" type="number" placeholder="17" min="1"
                              value={form['room.offer.LoanApplicationPeriod']}
                              onChange={e => set('room.offer.LoanApplicationPeriod', e.target.value)} />
                          </Fld>
                        </div>
                      )}
                    </div>
                    <Fld label="Approval type" hint="additionalFields.BuyerFinancingPreapproval 1/2/3">
                      <RadioGroup
                        value={form['_ui.h3Approval']}
                        onChange={setH3Approval}
                        options={[
                          { value: 'prequalification',   label: 'Prequalification' },
                          { value: 'preapproval',        label: 'Preapproval' },
                          { value: 'fully_underwritten', label: 'Fully underwritten preapproval' },
                        ]}
                      />
                    </Fld>
                  </div>

                </SectionCard>

              </>
            )}

            {/* ════════════════ STEP 6 — CONTINGENCIES & POSSESSION ════════════════ */}
            {step === 6 && (
              <>

                {/* ════ SECTION J — FINAL VERIFICATION ════ */}
                <SectionCard num="J" title="Final Verification of Condition" note="Para 16">
                  <Fld label="Days prior to Close of Escrow" hint="room.offer.InventoryConditionFormDeliveryPeriod">
                    <input className="rpa-input" type="number" placeholder="5" min="1" style={{ maxWidth: 180 }}
                      value={form['room.offer.InventoryConditionFormDeliveryPeriod']}
                      onChange={e => set('room.offer.InventoryConditionFormDeliveryPeriod', e.target.value)} />
                  </Fld>
                </SectionCard>

                {/* ════ SECTION K — ASSIGNMENT ════ */}
                <SectionCard num="K" title="Assignment Request" note="Para 23">
                  <Fld label="Days after Acceptance" hint="room.offer.AssignablePeriod">
                    <input className="rpa-input" type="number" placeholder="17" min="1" style={{ maxWidth: 180 }}
                      value={form['room.offer.AssignablePeriod']}
                      onChange={e => set('room.offer.AssignablePeriod', e.target.value)} />
                  </Fld>
                </SectionCard>

                {/* ════ SECTION L — CONTINGENCIES ════ */}
                <SectionCard num="L" title="Contingencies" note="Para 8A–8K">

                  <Fld label="">
                    <CheckRow
                      checked={form['additionalFields.RemoveContingency']}
                      onChange={v => set('additionalFields.RemoveContingency', v)}
                      label="C.A.R. Form CR-B attached — removes applicable contingencies as checked therein"
                      hint="additionalFields.RemoveContingency"
                    />
                  </Fld>

                  <DivLabel>L(1) &nbsp;·&nbsp; Loan Contingency — Para 8A</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Days after Acceptance" hint="room.offer.FinancingContingencyPeriod">
                      <input className="rpa-input" type="number" placeholder="17" min="1"
                        value={form['room.offer.FinancingContingencyPeriod']}
                        onChange={e => set('room.offer.FinancingContingencyPeriod', e.target.value)} />
                    </Fld>
                    <div style={{ paddingTop: 22 }}>
                      <CheckRow
                        checked={form['additionalFields.FinancingContingencyWaived']}
                        onChange={v => set('additionalFields.FinancingContingencyWaived', v)}
                        label="No loan contingency — Buyer waives"
                        hint="additionalFields.FinancingContingencyWaived"
                      />
                    </div>
                  </div>

                  <DivLabel>L(2) &nbsp;·&nbsp; Appraisal Contingency — Para 8B</DivLabel>
                  <div className="rpa-field-row rpa-cols-3">
                    <Fld label="Days after Acceptance" hint="room.offer.AppraisalContingencyPeriod">
                      <input className="rpa-input" type="number" placeholder="17" min="1"
                        value={form['room.offer.AppraisalContingencyPeriod']}
                        onChange={e => set('room.offer.AppraisalContingencyPeriod', e.target.value)} />
                    </Fld>
                    <Fld label="Custom appraisal value ($)" hint="room.offer.appraisalAmount + additionalFields.Appraisal">
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input type="checkbox" checked={form['additionalFields.Appraisal']}
                          onChange={e => set('additionalFields.Appraisal', e.target.checked)}
                          style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }} />
                        <input className="rpa-input" type="text" placeholder="0.00"
                          value={form['room.offer.appraisalAmount']}
                          onChange={e => set('room.offer.appraisalAmount', e.target.value)} />
                      </div>
                    </Fld>
                    <div style={{ paddingTop: 22 }}>
                      <CheckRow
                        checked={form['additionalFields.AppraisalContingency2']}
                        onChange={v => set('additionalFields.AppraisalContingency2', v)}
                        label="No appraisal contingency"
                        hint="additionalFields.AppraisalContingency2"
                      />
                    </div>
                  </div>

                  <DivLabel>L(3) &nbsp;·&nbsp; Investigation of Property — Para 8C, 12</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Days after Acceptance (contingency)" hint="room.offer.NumberOfDaysAfterAcceptancePeriod2">
                      <input className="rpa-input" type="number" placeholder="17" min="1"
                        value={form['room.offer.NumberOfDaysAfterAcceptancePeriod2']}
                        onChange={e => set('room.offer.NumberOfDaysAfterAcceptancePeriod2', e.target.value)} />
                    </Fld>
                    <Fld label="Informational access days (not a contingency)" hint="room.offer.NumberOfDaysAfterAcceptancePeriod3">
                      <input className="rpa-input" type="number" placeholder="17" min="1"
                        value={form['room.offer.NumberOfDaysAfterAcceptancePeriod3']}
                        onChange={e => set('room.offer.NumberOfDaysAfterAcceptancePeriod3', e.target.value)} />
                    </Fld>
                  </div>

                  <DivLabel>L(4) &nbsp;·&nbsp; Insurance Contingency — Para 8D</DivLabel>
                  <Fld label="Days after Acceptance" hint="room.offer.DocumentReviewContingencyDeadline">
                    <input className="rpa-input" type="number" placeholder="17" min="1" style={{ maxWidth: 180 }}
                      value={form['room.offer.DocumentReviewContingencyDeadline']}
                      onChange={e => set('room.offer.DocumentReviewContingencyDeadline', e.target.value)} />
                  </Fld>

                  <DivLabel>L(5) &nbsp;·&nbsp; Review of Seller Documents — Para 8E, 14A</DivLabel>
                  <Fld label="Days after Acceptance" hint="room.offer.NumberOfDaysAfterAcceptancePeriod4">
                    <input className="rpa-input" type="number" placeholder="17" min="1" style={{ maxWidth: 180 }}
                      value={form['room.offer.NumberOfDaysAfterAcceptancePeriod4']}
                      onChange={e => set('room.offer.NumberOfDaysAfterAcceptancePeriod4', e.target.value)} />
                  </Fld>

                  <DivLabel>L(6) &nbsp;·&nbsp; Preliminary ("Title") Report — Para 8F, 13A</DivLabel>
                  <Fld label="Days after Acceptance" hint="room.offer.NumberOfDaysAfterAcceptancePeriod5">
                    <input className="rpa-input" type="number" placeholder="17" min="1" style={{ maxWidth: 180 }}
                      value={form['room.offer.NumberOfDaysAfterAcceptancePeriod5']}
                      onChange={e => set('room.offer.NumberOfDaysAfterAcceptancePeriod5', e.target.value)} />
                  </Fld>

                  <DivLabel>L(7) &nbsp;·&nbsp; Common Interest Disclosures — Para 8G, 11M</DivLabel>
                  <Fld label="Days after Acceptance" hint="room.offer.LeaseReviewContingencyPeriod">
                    <input className="rpa-input" type="number" placeholder="17" min="1" style={{ maxWidth: 180 }}
                      value={form['room.offer.LeaseReviewContingencyPeriod']}
                      onChange={e => set('room.offer.LeaseReviewContingencyPeriod', e.target.value)} />
                  </Fld>

                  <DivLabel>L(8) &nbsp;·&nbsp; Review of Leased or Liened Items — Para 8H, 9B(6)</DivLabel>
                  <Fld label="Days after Acceptance" hint="room.offer.LeaseReviewContingencyPeriod2">
                    <input className="rpa-input" type="number" placeholder="17" min="1" style={{ maxWidth: 180 }}
                      value={form['room.offer.LeaseReviewContingencyPeriod2']}
                      onChange={e => set('room.offer.LeaseReviewContingencyPeriod2', e.target.value)} />
                  </Fld>

                  <DivLabel>L(9) &nbsp;·&nbsp; Sale of Buyer's Property — Para 8K</DivLabel>
                  <CheckRow
                    checked={form['additionalFields.BuyerPropertyContingency']}
                    onChange={v => set('additionalFields.BuyerPropertyContingency', v)}
                    label="Sale of Buyer's property IS a contingency — C.A.R. Form COP attached"
                    hint="additionalFields.BuyerPropertyContingency"
                  />

                </SectionCard>

                {/* ════ SECTION M — POSSESSION ════ */}
                <SectionCard num="M" title="Possession" note="Para 3M · 4A · 7A · 7D">

                  <DivLabel>M(1) &nbsp;·&nbsp; Time of Possession</DivLabel>
                  <Fld label="When possession is delivered">
                    <RadioGroup
                      value={form['_ui.m1Possession']}
                      onChange={setM1Possession}
                      options={[
                        { value: 'recordation', label: 'Upon notice of recordation' },
                        { value: '6pm',         label: '6:00 PM on the date in M(2) or TOPA' },
                        { value: 'custom',      label: 'Custom time on date in M(2) or TOPA' },
                      ]}
                    />
                  </Fld>
                  {form['_ui.m1Possession'] === 'custom' && (
                    <div className="rpa-field-row rpa-cols-2" style={{ marginTop: 10 }}>
                      <Fld label="Custom time" hint="room.offer.PossessionTime">
                        <input className="rpa-input" type="time"
                          value={form['room.offer.PossessionTime']}
                          onChange={e => set('room.offer.PossessionTime', e.target.value)} />
                      </Fld>
                      <Fld label="AM / PM" hint="additionalFields.PossessionTime (radio)">
                        <RadioGroup
                          value={form['additionalFields.PossessionTime']}
                          onChange={v => set('additionalFields.PossessionTime', v)}
                          options={[{ value: 'AM', label: 'AM' }, { value: 'PM', label: 'PM' }]}
                          inline
                        />
                      </Fld>
                    </div>
                  )}

                  <Hr />

                  <DivLabel>M(2) &nbsp;·&nbsp; Seller Occupied or Vacant Units — Para 7D</DivLabel>
                  <Fld label="Seller vacates on">
                    <RadioGroup
                      value={form['_ui.m2Timing']}
                      onChange={setM2Timing}
                      options={[
                        { value: 'coe',   label: 'COE date (Close of Escrow)' },
                        { value: 'short', label: '≤ 29 days after COE — attach C.A.R. Form SIP' },
                        { value: 'long',  label: '≥ 30 days after COE — attach C.A.R. Form RLAS' },
                      ]}
                    />
                  </Fld>
                  {form['_ui.m2Timing'] === 'short' && (
                    <div style={{ marginTop: 8, maxWidth: 200 }}>
                      <Fld label="Days after COE (max 29)" hint="room.property.LengthofOccupancy">
                        <input className="rpa-input" type="number" placeholder="days" min="1" max="29"
                          value={form['room.property.LengthofOccupancy']}
                          onChange={e => set('room.property.LengthofOccupancy', e.target.value)} />
                      </Fld>
                    </div>
                  )}
                  {form['_ui.m2Timing'] === 'long' && (
                    <div style={{ marginTop: 8, maxWidth: 200 }}>
                      <Fld label="Days after COE (min 30)" hint="room.property.LengthofOccupancy2">
                        <input className="rpa-input" type="number" placeholder="days" min="30"
                          value={form['room.property.LengthofOccupancy2']}
                          onChange={e => set('room.property.LengthofOccupancy2', e.target.value)} />
                      </Fld>
                    </div>
                  )}

                  <Hr />

                  <DivLabel>M(3) &nbsp;·&nbsp; Occupied Units by Tenants — Para 4A, 7A</DivLabel>
                  <CheckRow
                    checked={form['additionalFields.TenantOccupiedPropertyAddendum']}
                    onChange={v => set('additionalFields.TenantOccupiedPropertyAddendum', v)}
                    label="Tenant Occupied Property Addendum (C.A.R. Form TOPA) attached"
                    hint="additionalFields.TenantOccupiedPropertyAddendum"
                  />

                </SectionCard>

                {/* ════ SECTION N — DOCUMENTS / FEES / COMPLIANCE ════ */}
                <SectionCard num="N" title="Documents / Fees / Compliance" note="Para 10B · 11M · 14A · 19B · 24">
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="N(1) — Seller Delivery of Documents (days after Acceptance)" hint="room.offer.NumberOfDaysAfterAcceptancePeriod6">
                      <input className="rpa-input" type="number" placeholder="7" min="1"
                        value={form['room.offer.NumberOfDaysAfterAcceptancePeriod6']}
                        onChange={e => set('room.offer.NumberOfDaysAfterAcceptancePeriod6', e.target.value)} />
                    </Fld>
                    <Fld label="N(2) — Escrow Holder Provisions (days after Delivery)" hint="room.offer.NumberOfDaysAfterAcceptancePeriod7">
                      <input className="rpa-input" type="number" placeholder="5" min="1"
                        value={form['room.offer.NumberOfDaysAfterAcceptancePeriod7']}
                        onChange={e => set('room.offer.NumberOfDaysAfterAcceptancePeriod7', e.target.value)} />
                    </Fld>
                    <Fld label="N(3) — HOA Document Fees (days after Acceptance)" hint="room.offer.NumberOfDaysAfterAcceptancePeriod8">
                      <input className="rpa-input" type="number" placeholder="3" min="1"
                        value={form['room.offer.NumberOfDaysAfterAcceptancePeriod8']}
                        onChange={e => set('room.offer.NumberOfDaysAfterAcceptancePeriod8', e.target.value)} />
                    </Fld>
                    <Fld label="N(4) — Smoke Alarm / CO Detector / Bracing (days after Acceptance)" hint="room.offer.NumberOfDaysAfterAcceptancePeriod9">
                      <input className="rpa-input" type="number" placeholder="7" min="1"
                        value={form['room.offer.NumberOfDaysAfterAcceptancePeriod9']}
                        onChange={e => set('room.offer.NumberOfDaysAfterAcceptancePeriod9', e.target.value)} />
                    </Fld>
                  </div>
                  <div style={{ fontSize: 11, color: '#999', marginTop: 10, fontStyle: 'italic' }}>
                    N(5) — Evidence of Representative Authority: fixed at 3 days (not customizable per Para 24).
                  </div>
                </SectionCard>

              </>
            )}

            {/* ════════════════ STEP 7 — ITEMS INCLUDED / EXCLUDED ════════════════ */}
            {step === 7 && (
              <>

                {/* ════ SECTION P — ITEMS INCLUDED / EXCLUDED ════ */}
                <SectionCard num="P" title="Items Included and Excluded in Sale" note="Para 9">
                  <p className="rpa-field-hint" style={{ marginBottom: 14, fontStyle: 'italic', color: '#666' }}>
                    All checked items are included in the purchase price and transferred without Seller warranty. Items in MLS or marketing materials are NOT included unless specified here.
                  </p>

                  <DivLabel>P(1) &nbsp;·&nbsp; Items included in sale</DivLabel>

                  <div style={{ marginBottom: 12 }}>
                    <div className="rpa-field-label" style={{ marginBottom: 8 }}>Appliances</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 7 }}>
                      {[
                        ['additionalFields.Stove',           'Stove(s) / oven(s) / combos'],
                        ['additionalFields.Refrigerator',    'Refrigerator(s)'],
                        ['additionalFields.WineRefrigerator','Wine refrigerator(s)'],
                        ['additionalFields.Washer',          'Washer(s)'],
                        ['additionalFields.Dryer',           'Dryer(s)'],
                        ['additionalFields.Dishwasher',      'Dishwasher(s)'],
                        ['additionalFields.Microwave',       'Microwave(s)'],
                      ].map(([key, label]) => (
                        <CheckRow key={key} checked={form[key]} onChange={v => set(key, v)} label={label} hint={key} />
                      ))}
                    </div>
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <div className="rpa-field-label" style={{ marginBottom: 8 }}>Additional included item</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input type="checkbox" checked={form['additionalFields.AdditionalItemsIncluded18']}
                        onChange={e => set('additionalFields.AdditionalItemsIncluded18', e.target.checked)}
                        style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }} />
                      <input className="rpa-input" type="text" placeholder="Additional item included…"
                        value={form['room.property.details.fixtureInclusion'] || ''}
                        onChange={e => set('room.property.details.fixtureInclusion', e.target.value)} />
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <div className="rpa-field-label" style={{ marginBottom: 8 }}>Security &amp; smart home</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 7, marginBottom: 8 }}>
                      {[
                        ['additionalFields.DoorBell',          'Video doorbell(s)'],
                        ['additionalFields.SecuritySystem2',   'Security camera equipment'],
                        ['additionalFields.SecuritySystem',    'Security system(s) / alarm(s)'],
                        ['additionalFields.SmartHomeFeatures', 'Smart home control devices'],
                      ].map(([key, label]) => (
                        <CheckRow key={key} checked={form[key]} onChange={v => set(key, v)} label={label} hint={key} />
                      ))}
                    </div>
                    {[
                      ['additionalFields.AdditionalItemsIncluded5', 'room.property.details.fixtureInclusion2', 'Additional security / smart home item…'],
                      ['additionalFields.AdditionalItemsIncluded2', 'room.property.details.fixtureInclusion4', 'Additional security / smart home item…'],
                    ].map(([cbKey, txtKey, ph]) => (
                      <div key={cbKey} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                        <input type="checkbox" checked={form[cbKey]} onChange={e => set(cbKey, e.target.checked)}
                          style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }} />
                        <input className="rpa-input" type="text" placeholder={ph}
                          value={form[txtKey]} onChange={e => set(txtKey, e.target.value)} />
                      </div>
                    ))}
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <div className="rpa-field-label" style={{ marginBottom: 8 }}>Outdoor &amp; other</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 7, marginBottom: 8 }}>
                      {[
                        ['additionalFields.AboveGroundPool',    'Above-ground pool(s)'],
                        ['additionalFields.ElectricCarCharger', 'Electric car charging systems'],
                      ].map(([key, label]) => (
                        <CheckRow key={key} checked={form[key]} onChange={v => set(key, v)} label={label} hint={key} />
                      ))}
                    </div>
                    {[
                      ['additionalFields.AdditionalItemsIncluded3', 'room.property.details.fixtureInclusion3', 'Additional outdoor / other item…'],
                      ['additionalFields.AdditionalItemsIncluded6', 'room.property.details.fixtureInclusion5', 'Additional outdoor / other item…'],
                      ['additionalFields.AdditionalItemsIncluded',  'room.property.details.fixtureInclusion2', 'Additional item…'],
                    ].map(([cbKey, txtKey, ph]) => (
                      <div key={cbKey} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                        <input type="checkbox" checked={form[cbKey]} onChange={e => set(cbKey, e.target.checked)}
                          style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }} />
                        <input className="rpa-input" type="text" placeholder={ph}
                          value={form[txtKey]} onChange={e => set(txtKey, e.target.value)} />
                      </div>
                    ))}
                  </div>

                  <DivLabel>P(2) &nbsp;·&nbsp; Excluded items</DivLabel>
                  {[
                    ['additionalFields.ItemsExcluded',  'room.property.details.fixtureExclusion',  'Excluded item 1…'],
                    ['additionalFields.ItemsExcluded2', 'room.property.details.fixtureExclusion2', 'Excluded item 2…'],
                    ['additionalFields.ItemsExcluded3', 'room.property.details.fixtureExclusion3', 'Excluded item 3…'],
                  ].map(([cbKey, txtKey, ph]) => (
                    <div key={cbKey} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                      <input type="checkbox" checked={form[cbKey]} onChange={e => set(cbKey, e.target.checked)}
                        style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }} />
                      <input className="rpa-input" type="text" placeholder={ph}
                        value={form[txtKey]} onChange={e => set(txtKey, e.target.value)} />
                    </div>
                  ))}
                </SectionCard>

              </>
            )}

            {/* ════════════════ STEP 8 — ALLOCATION OF COSTS & OTHER TERMS ════════════════ */}
            {step === 8 && (
              <>

                {/* ════ SECTION Q — ALLOCATION OF COSTS ════ */}
                <SectionCard num="Q" title="Allocation of Costs" note="Para 10A · 10B · 10C · 11A · 13 · 19B">
                  <p className="rpa-field-hint" style={{ marginBottom: 14, fontStyle: 'italic', color: '#666' }}>
                    Enter the payer (Buyer / Seller / Both) and any additional details for each item. Rows Q(9), Q(12), Q(13) are fixed by contract.
                  </p>

                  <DivLabel>Q(1) &nbsp;·&nbsp; Natural Hazard Zone Disclosure Report — Para 10A, 11A</DivLabel>
                  <Fld label="Paid by" hint="additionalFields.PayableByBuyerOrSeller (radio)">
                    <RadioGroup value={form['additionalFields.PayableByBuyerOrSeller']}
                      onChange={v => set('additionalFields.PayableByBuyerOrSeller', v)}
                      options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                  </Fld>
                  <Fld label="Payer / split description" hint="room.offer.OtherFeesPaidByDescription">
                    <input className="rpa-input" type="text" placeholder="Payer / split description — e.g., Seller pays 100%"
                      value={form['room.offer.OtherFeesPaidByDescription']}
                      onChange={e => set('room.offer.OtherFeesPaidByDescription', e.target.value)} />
                  </Fld>
                  <div className="rpa-field-row" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                    <CheckRow checked={form['additionalFields.EnvironmentalInspectionReport3']}
                      onChange={v => set('additionalFields.EnvironmentalInspectionReport3', v)}
                      label="Provided by" hint="additionalFields.EnvironmentalInspectionReport3" />
                    <input className="rpa-input" type="text" placeholder="Provider name" style={{ flex: 1, minWidth: 160 }}
                      value={form['room.offer.OtherFeesPaidByDescription3']}
                      onChange={e => set('room.offer.OtherFeesPaidByDescription3', e.target.value)} />
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <CheckRow checked={form['additionalFields.EnvironmentalInspectionReport']}
                      onChange={v => set('additionalFields.EnvironmentalInspectionReport', v)}
                      label="Environmental" hint="additionalFields.EnvironmentalInspectionReport" />
                  </div>
                  <div className="rpa-field-row" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                    <CheckRow checked={form['additionalFields.EnvironmentalInspectionReport2']}
                      onChange={v => set('additionalFields.EnvironmentalInspectionReport2', v)}
                      label="Other" hint="additionalFields.EnvironmentalInspectionReport2" />
                    <input className="rpa-input" type="text" placeholder="e.g., report provider" style={{ flex: 1, minWidth: 160 }}
                      value={form['room.offer.OtherFeesPaidByDescription2']}
                      onChange={e => set('room.offer.OtherFeesPaidByDescription2', e.target.value)} />
                  </div>

                  <DivLabel>Q(2) &nbsp;·&nbsp; Optional Wildfire Disclosure Report</DivLabel>
                  <div style={{ marginBottom: 8 }}>
                    <CheckRow checked={form['additionalFields.DisclosureReport']}
                      onChange={v => set('additionalFields.DisclosureReport', v)}
                      label="Optional Wildfire Disclosure Report" hint="additionalFields.DisclosureReport" />
                  </div>
                  <Fld label="Paid by" hint="additionalFields.PayableByBuyerOrSeller2 (radio)">
                    <RadioGroup value={form['additionalFields.PayableByBuyerOrSeller2']}
                      onChange={v => set('additionalFields.PayableByBuyerOrSeller2', v)}
                      options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                  </Fld>
                  <Fld label="Payer / split description" hint="room.offer.OtherFeesPaidByDescription4">
                    <input className="rpa-input" type="text" placeholder="Buyer pays 100%"
                      value={form['room.offer.OtherFeesPaidByDescription4']}
                      onChange={e => set('room.offer.OtherFeesPaidByDescription4', e.target.value)} />
                  </Fld>
                  <Fld label="Provided by" hint="room.offer.OtherFeesPaidByDescription5">
                    <input className="rpa-input" type="text" placeholder="Provider name"
                      value={form['room.offer.OtherFeesPaidByDescription5']}
                      onChange={e => set('room.offer.OtherFeesPaidByDescription5', e.target.value)} />
                  </Fld>
                  <Fld label="Additional detail" hint="room.offer.OtherFeesPaidByDescription6">
                    <input className="rpa-input" type="text" placeholder="Additional detail — e.g., Seller pays"
                      value={form['room.offer.OtherFeesPaidByDescription6']}
                      onChange={e => set('room.offer.OtherFeesPaidByDescription6', e.target.value)} />
                  </Fld>

                  <DivLabel>Q(3) &nbsp;·&nbsp; Other Inspections &amp; Reports (A &amp; B)</DivLabel>
                  <div className="rpa-field-row" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 600, color: '#555a66' }}>(A)</span>
                    <input className="rpa-input" type="text" placeholder="e.g. Pest inspection report" style={{ flex: 1, minWidth: 160 }}
                      value={form['room.offer.OtherReports']}
                      onChange={e => set('room.offer.OtherReports', e.target.value)} />
                    <RadioGroup value={form['additionalFields.PayableByBuyerOrSeller3']}
                      onChange={v => set('additionalFields.PayableByBuyerOrSeller3', v)}
                      options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                    <input className="rpa-input" type="text" placeholder="Payer / split description" style={{ flex: 1, minWidth: 160 }}
                      value={form['room.offer.OtherFeesPaidByDescription7']}
                      onChange={e => set('room.offer.OtherFeesPaidByDescription7', e.target.value)} />
                  </div>
                  <div className="rpa-field-row" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                    <span style={{ fontWeight: 600, color: '#555a66' }}>(B)</span>
                    <input className="rpa-input" type="text" placeholder="e.g. Roof inspection report" style={{ flex: 1, minWidth: 160 }}
                      value={form['room.offer.OtherReports2']}
                      onChange={e => set('room.offer.OtherReports2', e.target.value)} />
                    <RadioGroup value={form['additionalFields.PayableByBuyerOrSeller4']}
                      onChange={v => set('additionalFields.PayableByBuyerOrSeller4', v)}
                      options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                    <input className="rpa-input" type="text" placeholder="Payer / split description" style={{ flex: 1, minWidth: 160 }}
                      value={form['room.offer.OtherFeesPaidByDescription8']}
                      onChange={e => set('room.offer.OtherFeesPaidByDescription8', e.target.value)} />
                  </div>

                  <DivLabel>Q(4) &nbsp;·&nbsp; Smoke Alarms, CO Detectors, Water Heater Bracing — Para 10B(1)</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Paid by" hint="additionalFields.PayableByBuyerOrSeller5 (radio)">
                      <RadioGroup value={form['additionalFields.PayableByBuyerOrSeller5']}
                        onChange={v => set('additionalFields.PayableByBuyerOrSeller5', v)}
                        options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                    </Fld>
                    <Fld label="Payer detail" hint="room.offer.OtherFeesPaidByDescription9">
                      <input className="rpa-input" type="text" placeholder="Buyer / Seller / Both"
                        value={form['room.offer.OtherFeesPaidByDescription9']}
                        onChange={e => set('room.offer.OtherFeesPaidByDescription9', e.target.value)} />
                    </Fld>
                  </div>

                  <DivLabel>Q(5) &nbsp;·&nbsp; Govt Required Point-of-Sale Inspections &amp; Reports — Para 10A, 10B(2)</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Paid by" hint="additionalFields.PayableByBuyerOrSeller6 (radio)">
                      <RadioGroup value={form['additionalFields.PayableByBuyerOrSeller6']}
                        onChange={v => set('additionalFields.PayableByBuyerOrSeller6', v)}
                        options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                    </Fld>
                    <Fld label="Payer detail" hint="room.offer.OtherFeesPaidByDescription10">
                      <input className="rpa-input" type="text" placeholder="Buyer / Seller / Both"
                        value={form['room.offer.OtherFeesPaidByDescription10']}
                        onChange={e => set('room.offer.OtherFeesPaidByDescription10', e.target.value)} />
                    </Fld>
                  </div>

                  <DivLabel>Q(6) &nbsp;·&nbsp; Govt Required Corrective / Remedial Actions — Para 10B(2)</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Paid by" hint="additionalFields.PayCostOfPaymentEscrow (radio)">
                      <RadioGroup value={form['additionalFields.PayCostOfPaymentEscrow']}
                        onChange={v => set('additionalFields.PayCostOfPaymentEscrow', v)}
                        options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                    </Fld>
                    <Fld label="Payer detail" hint="room.offer.OtherFeesPaidByDescription11">
                      <input className="rpa-input" type="text" placeholder="Buyer / Seller / Both"
                        value={form['room.offer.OtherFeesPaidByDescription11']}
                        onChange={e => set('room.offer.OtherFeesPaidByDescription11', e.target.value)} />
                    </Fld>
                  </div>

                  <DivLabel>Q(7) &nbsp;·&nbsp; Escrow Fee — Para 19B</DivLabel>
                  <Fld label="Paid by" hint="additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer (radio)">
                    <RadioGroup value={form['additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer']}
                      onChange={v => set('additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer', v)}
                      options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                  </Fld>
                  <Fld label="Payer / split description" hint="room.offer.EscrowFees">
                    <input className="rpa-input" type="text" placeholder="e.g., West Coast Escrow"
                      value={form['room.offer.EscrowFees']}
                      onChange={e => set('room.offer.EscrowFees', e.target.value)} />
                  </Fld>
                  <div style={{ marginTop: 8 }}>
                    <CheckRow checked={form['additionalFields.OtherFees']}
                      onChange={v => set('additionalFields.OtherFees', v)}
                      label="Each to pay their own fees"
                      hint="additionalFields.OtherFees" />
                  </div>
                  <Fld label="Escrow Holder" hint="room.offer.EscrowFeesPaidBy">
                    <input className="rpa-input" type="text" placeholder="Escrow holder name / detail"
                      value={form['room.offer.EscrowFeesPaidBy']}
                      onChange={e => set('room.offer.EscrowFeesPaidBy', e.target.value)} />
                  </Fld>

                  <DivLabel>Q(8) &nbsp;·&nbsp; Owner Title Insurance Policy — Para 13</DivLabel>
                  <Fld label="Paid by" hint="additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer2 (radio)">
                    <RadioGroup value={form['additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer2']}
                      onChange={v => set('additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer2', v)}
                      options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                  </Fld>
                  <Fld label="Payer / split description" hint="room.offer.OtherFeesPaidByDescription12">
                    <input className="rpa-input" type="text" placeholder="Payer / split description"
                      value={form['room.offer.OtherFeesPaidByDescription12']}
                      onChange={e => set('room.offer.OtherFeesPaidByDescription12', e.target.value)} />
                  </Fld>
                  <Fld label="Title Co. (if different from Escrow Holder)" hint="room.offer.OtherFeesPaidByDescription13">
                    <input className="rpa-input" type="text" placeholder="Title company name"
                      value={form['room.offer.OtherFeesPaidByDescription13']}
                      onChange={e => set('room.offer.OtherFeesPaidByDescription13', e.target.value)} />
                  </Fld>

                  <div className="rpa-field-hint" style={{ background: '#f5f5f0', border: '1px solid #e8e8ed', borderRadius: 6, padding: '8px 12px', marginTop: 12, marginBottom: 4 }}>
                    <strong>Q(9)</strong> — Buyer's lender title insurance: <em>Buyer pays — fixed by contract.</em>
                    &nbsp;&nbsp;<strong>Q(12)</strong> — HOA disclosure prep fee: <em>Seller — fixed.</em>
                    &nbsp;&nbsp;<strong>Q(13)</strong> — HOA certification fee: <em>Buyer — fixed.</em>
                  </div>

                  <DivLabel>Q(10) &nbsp;·&nbsp; County Transfer Tax / Fees</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Paid by" hint="additionalFields.TransferTaxesPaidByBuyerOrSeller (radio)">
                      <RadioGroup value={form['additionalFields.TransferTaxesPaidByBuyerOrSeller']}
                        onChange={v => set('additionalFields.TransferTaxesPaidByBuyerOrSeller', v)}
                        options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                    </Fld>
                    <Fld label="Payer detail" hint="room.offer.OtherTransferFeeDescription">
                      <input className="rpa-input" type="text" placeholder="Buyer / Seller / Both"
                        value={form['room.offer.OtherTransferFeeDescription']}
                        onChange={e => set('room.offer.OtherTransferFeeDescription', e.target.value)} />
                    </Fld>
                  </div>

                  <DivLabel>Q(11) &nbsp;·&nbsp; City Transfer Tax / Fees</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Paid by" hint="additionalFields.TransferTaxesPaidByBuyerOrSeller2 (radio)">
                      <RadioGroup value={form['additionalFields.TransferTaxesPaidByBuyerOrSeller2']}
                        onChange={v => set('additionalFields.TransferTaxesPaidByBuyerOrSeller2', v)}
                        options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                    </Fld>
                    <Fld label="Payer detail" hint="room.offer.OtherTransferFeeDescription2">
                      <input className="rpa-input" type="text" placeholder="Buyer / Seller / Both"
                        value={form['room.offer.OtherTransferFeeDescription2']}
                        onChange={e => set('room.offer.OtherTransferFeeDescription2', e.target.value)} />
                    </Fld>
                  </div>

                  <DivLabel>Q(14) &nbsp;·&nbsp; HOA Transfer Fees</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Paid by" hint="additionalFields.TransferFeesPaidforBy (radio)">
                      <RadioGroup value={form['additionalFields.TransferFeesPaidforBy']}
                        onChange={v => set('additionalFields.TransferFeesPaidforBy', v)}
                        options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                    </Fld>
                    <Fld label="HOA transfer fee details" hint="room.property.HomeownerAssociationTransferFee">
                      <input className="rpa-input" type="text" placeholder="Payer / custom terms"
                        value={form['room.property.HomeownerAssociationTransferFee']}
                        onChange={e => set('room.property.HomeownerAssociationTransferFee', e.target.value)} />
                    </Fld>
                  </div>

                  <DivLabel>Q(15) &nbsp;·&nbsp; Private Transfer Fees</DivLabel>
                  <div className="rpa-field-row" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span className="rpa-field-hint" style={{ whiteSpace: 'nowrap' }}>Seller, or if checked:</span>
                    <RadioGroup value={form['additionalFields.TransferFeesPaidforBy2']}
                      onChange={v => set('additionalFields.TransferFeesPaidforBy2', v)}
                      options={[{value:'Buyer',label:'Buyer'},{value:'Both',label:'Both'}]} inline />
                  </div>
                  <Fld label="Payer / split description" hint="room.offer.PrivateTransferFeeObligation">
                    <input className="rpa-input" type="text" placeholder="e.g., Seller pays all private transfer fees"
                      value={form['room.offer.PrivateTransferFeeObligation']}
                      onChange={e => set('room.offer.PrivateTransferFeeObligation', e.target.value)} />
                  </Fld>

                  <DivLabel>Q(16) &nbsp;·&nbsp; Septic, Well, Propane Tank &amp; Property Boundaries — Para 11O</DivLabel>
                  <CheckRow checked={form['additionalFields.WellSepticSystemEvaluation']}
                    onChange={v => set('additionalFields.WellSepticSystemEvaluation', v)}
                    label="Septic, Well, Propane Tank, and/or Property Boundaries (Survey)"
                    hint="additionalFields.WellSepticSystemEvaluation" />
                  <div className="rpa-field-hint" style={{ background: '#f5f5f0', border: '1px dashed #e8e8ed', borderRadius: 6, padding: '8px 12px', marginTop: 8 }}>
                    See Septic, Well, Propane Tank, and Property Boundaries Inspection and Allocation of Costs Addendum (C.A.R. Form SWPI-C).
                  </div>

                  <DivLabel>Q(17) &nbsp;·&nbsp; Fees or Costs</DivLabel>
                  <div className="rpa-field-row rpa-cols-3">
                    <Fld label="Fees or costs" hint="room.offer.BrokerServiceOtherFee">
                      <input className="rpa-input" type="text" placeholder="e.g. HOA move-in inspection fee"
                        value={form['room.offer.BrokerServiceOtherFee']}
                        onChange={e => set('room.offer.BrokerServiceOtherFee', e.target.value)} />
                    </Fld>
                    <Fld label="Paid by" hint="additionalFields.PayableByBuyerOrSeller7 (radio)">
                      <RadioGroup value={form['additionalFields.PayableByBuyerOrSeller7']}
                        onChange={v => set('additionalFields.PayableByBuyerOrSeller7', v)}
                        options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                    </Fld>
                    <Fld label="Payer detail" hint="room.offer.OtherFeesPaidByDescription14">
                      <input className="rpa-input" type="text" placeholder="Buyer / Seller / Both"
                        value={form['room.offer.OtherFeesPaidByDescription14']}
                        onChange={e => set('room.offer.OtherFeesPaidByDescription14', e.target.value)} />
                    </Fld>
                  </div>

                  <DivLabel>Custom Fee (B)</DivLabel>
                  <div className="rpa-field-row rpa-cols-2">
                    <Fld label="Fee / cost name" hint="room.offer.BrokerServiceOtherFee2">
                      <input className="rpa-input" type="text" placeholder="e.g. City retrofit compliance fee"
                        value={form['room.offer.BrokerServiceOtherFee2']}
                        onChange={e => set('room.offer.BrokerServiceOtherFee2', e.target.value)} />
                    </Fld>
                    <Fld label="Payer / split" hint="room.offer.OtherFeesPaidByDescription16">
                      <input className="rpa-input" type="text" placeholder="Buyer / Seller / Both"
                        value={form['room.offer.OtherFeesPaidByDescription16']}
                        onChange={e => set('room.offer.OtherFeesPaidByDescription16', e.target.value)} />
                    </Fld>
                  </div>

                  <DivLabel>Q(18) &nbsp;·&nbsp; Home Warranty Plan — Para 10C</DivLabel>
                  <Fld label="Home warranty plan chosen by Buyer. Coverage includes, but is not limited to:" hint="room.service.HomeWarrantyTypeOfCoverage / 2">
                    <input className="rpa-input" type="text" placeholder="Coverage line 1" style={{ marginBottom: 8 }}
                      value={form['room.service.HomeWarrantyTypeOfCoverage']}
                      onChange={e => set('room.service.HomeWarrantyTypeOfCoverage', e.target.value)} />
                    <input className="rpa-input" type="text" placeholder="Coverage line 2"
                      value={form['room.service.HomeWarrantyTypeOfCoverage2']}
                      onChange={e => set('room.service.HomeWarrantyTypeOfCoverage2', e.target.value)} />
                  </Fld>
                  <Fld label="Paid by" hint="additionalFields.PayableByBuyerOrSeller8 (radio)">
                    <RadioGroup value={form['additionalFields.PayableByBuyerOrSeller8']}
                      onChange={v => set('additionalFields.PayableByBuyerOrSeller8', v)}
                      options={[{value:'Buyer',label:'Buyer'},{value:'Seller',label:'Seller'},{value:'Both',label:'Both'}]} inline />
                  </Fld>
                  <Fld label="Payer / split description" hint="room.offer.OtherFeesPaidByDescription15">
                    <input className="rpa-input" type="text" placeholder="Payer / split description"
                      value={form['room.offer.OtherFeesPaidByDescription15']}
                      onChange={e => set('room.offer.OtherFeesPaidByDescription15', e.target.value)} />
                  </Fld>
                  <div style={{ marginTop: 8 }}>
                    <CheckRow
                      checked={form['additionalFields.DeclineHomeWarrantyProgram']}
                      onChange={v => set('additionalFields.DeclineHomeWarrantyProgram', v)}
                      label="Buyer waives home warranty plan"
                      hint="additionalFields.DeclineHomeWarrantyProgram"
                    />
                  </div>
                  <Fld label="If Seller or Both checked, Seller's cost not to exceed $" hint="room.offer.SellerEstimatedHomeWarrantyCost">
                    <input className="rpa-input" type="text" placeholder="0.00"
                      value={form['room.offer.SellerEstimatedHomeWarrantyCost']}
                      onChange={e => set('room.offer.SellerEstimatedHomeWarrantyCost', e.target.value)} />
                  </Fld>
                  <Fld label="Issued by" hint="room.service.homeWarrantyProvider">
                    <input className="rpa-input" type="text" placeholder="Warranty company name"
                      value={form['room.service.homeWarrantyProvider']}
                      onChange={e => set('room.service.homeWarrantyProvider', e.target.value)} />
                  </Fld>
                  <Fld label="Issued by (continued)" hint="room.service.homeWarrantyProvider2">
                    <input className="rpa-input" type="text" placeholder="Additional detail"
                      value={form['room.service.homeWarrantyProvider2']}
                      onChange={e => set('room.service.homeWarrantyProvider2', e.target.value)} />
                  </Fld>

                </SectionCard>

                {/* ════ SECTION R — OTHER TERMS ════ */}
                <SectionCard num="R" title="Other Terms" note="Free-text — additional contractual terms">
                  <Fld label="Additional contractual terms (up to 4 lines)" hint="room.offer.OtherTerms 1–4">
                    {['room.offer.OtherTerms','room.offer.OtherTerms2','room.offer.OtherTerms3','room.offer.OtherTerms4'].map((key, i) => (
                      <input key={key} className="rpa-input" type="text"
                        placeholder={`Line ${i + 1} — additional terms, conditions or agreements…`}
                        value={form[key]} onChange={e => set(key, e.target.value)}
                        style={{ marginBottom: 8 }} />
                    ))}
                  </Fld>
                </SectionCard>

              </>
            )}

            {/* ════════════════ STEP 9 — ADDENDA & ADVISORIES ════════════════ */}
            {step === 9 && (
              <>

                {/* ════ SECTION 4A — PROPERTY TYPE ADDENDA ════ */}
                <SectionCard num="4A" title="Property Type Addenda" note="Para 4A · attached addenda become part of this Agreement">
                  <p className="rpa-field-hint" style={{ marginBottom: 14, fontStyle: 'italic', color: '#666' }}>
                    Check all property type addenda that apply. Each checked addendum is incorporated into this Agreement.
                  </p>
                  <CheckRow
                    checked={form['additionalFields.TenantOccupiedPropertyAddendum2']}
                    onChange={v => set('additionalFields.TenantOccupiedPropertyAddendum2', v)}
                    label="Tenant Occupied Property Addendum (C.A.R. Form TOPA)"
                    hint="additionalFields.TenantOccupiedPropertyAddendum2"
                  />
                  <CheckRow
                    checked={form['additionalFields.OtherAddenda']}
                    onChange={v => set('additionalFields.OtherAddenda', v)}
                    label="Probate Agreement Purchase Addendum (C.A.R. Form PA-PA)"
                    hint="additionalFields.OtherAddenda"
                  />
                  <CheckRow
                    checked={form['additionalFields.OtherAddenda2']}
                    onChange={v => set('additionalFields.OtherAddenda2', v)}
                    label="Manufactured Home Purchase Addendum (C.A.R. Form MH-PA)"
                    hint="additionalFields.OtherAddenda2"
                  />
                  <CheckRow
                    checked={form['additionalFields.OtherAddenda3']}
                    onChange={v => set('additionalFields.OtherAddenda3', v)}
                    label="Tenancy in Common Purchase Addendum (C.A.R. Form TIC-PA)"
                    hint="additionalFields.OtherAddenda3"
                  />
                  <CheckRow
                    checked={form['additionalFields.OtherAddenda4']}
                    onChange={v => set('additionalFields.OtherAddenda4', v)}
                    label="Stock Cooperative Purchase Addendum (C.A.R. Form COOP-PA)"
                    hint="additionalFields.OtherAddenda4"
                  />
                  <CheckRow
                    checked={form['additionalFields.OtherAddenda5']}
                    onChange={v => set('additionalFields.OtherAddenda5', v)}
                    label="Mixed Use Purchase Addendum (C.A.R. Form MU-PA)"
                    hint="additionalFields.OtherAddenda5"
                  />
                  <DivLabel>Other property type addendum</DivLabel>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <input type="checkbox" checked={form['additionalFields.OtherAddenda6']}
                      onChange={e => set('additionalFields.OtherAddenda6', e.target.checked)}
                      style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }} />
                    <input className="rpa-input" type="text" placeholder="Specify other property type addendum…"
                      value={form['room.offer.AddendumsAttached']}
                      onChange={e => set('room.offer.AddendumsAttached', e.target.value)} />
                  </div>
                </SectionCard>

                {/* ════ SECTION 4B — OTHER ADDENDA ════ */}
                <SectionCard num="4B" title="Other Addenda" note="Para 4B · attached addenda become part of this Agreement">

                  <DivLabel>Addendum # (C.A.R. Form ADM)</DivLabel>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <input type="checkbox" checked={form['additionalFields.OtherAddenda7']}
                      onChange={e => set('additionalFields.OtherAddenda7', e.target.checked)}
                      style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }} />
                    <span style={{ fontSize: 13, whiteSpace: 'nowrap', color: '#374151' }}>Addendum #</span>
                    <input className="rpa-input" type="text" placeholder="Number" style={{ maxWidth: 120 }}
                      value={form['room.offer.addendumNumbersCARFormADM']}
                      onChange={e => {
                        set('room.offer.addendumNumbersCARFormADM', e.target.value);
                        if (e.target.value) set('additionalFields.OtherAddenda7', true);
                      }} />
                    <span style={{ fontSize: 13, whiteSpace: 'nowrap', color: '#374151' }}>(C.A.R. Form ADM)</span>
                  </div>

                  <CheckRow
                    checked={form['additionalFields.OtherAddenda12']}
                    onChange={v => set('additionalFields.OtherAddenda12', v)}
                    label="Short Sale Addendum (C.A.R. Form SSA)"
                    hint="additionalFields.OtherAddenda12"
                  />
                  <CheckRow
                    checked={form['additionalFields.OtherAddenda8']}
                    onChange={v => set('additionalFields.OtherAddenda8', v)}
                    label="Back Up Offer Addendum (C.A.R. Form BUO)"
                    hint="additionalFields.OtherAddenda8"
                  />
                  <CheckRow
                    checked={form['additionalFields.OtherAddenda13']}
                    onChange={v => set('additionalFields.OtherAddenda13', v)}
                    label="Court Confirmation Addendum (C.A.R. Form CCA)"
                    hint="additionalFields.OtherAddenda13"
                  />
                  <CheckRow
                    checked={form['additionalFields.OtherAddenda9']}
                    onChange={v => set('additionalFields.OtherAddenda9', v)}
                    label="Septic, Well, Propane Tank, and Property Boundaries Allocation of Costs Addendum (C.A.R. Form SWPI-C)"
                    hint="additionalFields.OtherAddenda9"
                  />
                  <CheckRow
                    checked={form['additionalFields.OtherAddenda10']}
                    onChange={v => set('additionalFields.OtherAddenda10', v)}
                    label="Buyer Intent to Exchange Addendum (C.A.R. Form BXA)"
                    hint="additionalFields.OtherAddenda10"
                  />
                  <CheckRow
                    checked={form['additionalFields.OtherAddenda14']}
                    onChange={v => set('additionalFields.OtherAddenda14', v)}
                    label="Seller Intent to Exchange Addendum (C.A.R. Form SXA)"
                    hint="additionalFields.OtherAddenda14"
                  />
                  <div className="rpa-field-hint" style={{ background: '#f5f5f0', border: '1px solid #e8e8ed', borderRadius: 6, padding: '8px 12px', marginTop: 8, marginBottom: 8 }}>
                    <strong>✓</strong> Disclosure Regarding Real Estate Agency Relationships (C.A.R. Form AD) — <em>fixed, always included.</em>
                  </div>

                  <DivLabel>Custom addenda</DivLabel>
                  {[
                    ['additionalFields.OtherAddenda11', 'room.offer.AddendumsAttached2', 'Other addendum — specify…'],
                    ['additionalFields.OtherAddenda15', 'room.offer.AddendumsAttached3', 'Other addendum — specify…'],
                  ].map(([cbKey, txtKey, ph]) => (
                    <div key={cbKey} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                      <input type="checkbox" checked={form[cbKey]} onChange={e => set(cbKey, e.target.checked)}
                        style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }} />
                      <input className="rpa-input" type="text" placeholder={ph}
                        value={form[txtKey]} onChange={e => set(txtKey, e.target.value)} />
                    </div>
                  ))}
                </SectionCard>

                {/* ════ SECTION 4C — ADVISORIES ════ */}
                <SectionCard num="4C" title="Buyer and Seller Advisories" note="Reference only — NOT incorporated into this Agreement">
                  <div style={{ background: '#f5f4fd', border: '1px solid #c5c1ee', borderRadius: 6, padding: '9px 12px', marginBottom: 14, fontSize: 12, color: '#4c3494', lineHeight: 1.5 }}>
                    All advisories below are for reference only and are <strong>not incorporated</strong> into the Agreement. Parties may also receive a privacy disclosure from their own Agent.
                  </div>
                  <div className="rpa-field-hint" style={{ background: '#f5f5f0', border: '1px solid #e8e8ed', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
                    <strong>✓</strong> Buyer's Investigation Advisory (C.A.R. Form BIA) · Wire Fraud Advisory (C.A.R. Form WFA) · Fair Housing and Discrimination Advisory (C.A.R. Form FHDA) · Buyer Homeowner Insurance Advisory (C.A.R. Form BHIA) — <em>fixed, always included.</em>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 7, marginBottom: 12 }}>
                    {[
                      ['additionalField.OtherAdvisories',  'Wildfire Disaster Advisory (C.A.R. Form WFDA)'],
                      ['additionalField.OtherAdvisories5', 'Statewide Buyer and Seller Advisory (C.A.R. Form SBSA)'],
                      ['additionalField.OtherAdvisories2', 'Trust Advisory (C.A.R. Form TA)'],
                      ['additionalField.OtherAdvisories6', 'Probate Advisory (C.A.R. Form PA)'],
                      ['additionalField.OtherAdvisories3', 'REO Advisory (C.A.R. Form REO)'],
                    ].map(([key, label]) => (
                      <CheckRow key={key} checked={form[key]} onChange={v => set(key, v)} label={label} hint={key} />
                    ))}
                  </div>
                  <DivLabel>Other advisories</DivLabel>
                  {[
                    ['additionalField.OtherAdvisories4', 'room.offer.OtherAdvisories',  'Other advisory — specify…'],
                    ['additionalField.OtherAdvisories7', 'room.offer.OtherAdvisories2', 'Other advisory — specify…'],
                    ['additionalField.OtherAdvisories8', 'room.offer.OtherAdvisories3', 'Other advisory — specify…'],
                  ].map(([cbKey, txtKey, ph]) => (
                    <div key={cbKey} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
                      <input type="checkbox" checked={form[cbKey]} onChange={e => set(cbKey, e.target.checked)}
                        style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }} />
                      <input className="rpa-input" type="text" placeholder={ph}
                        value={form[txtKey]} onChange={e => set(txtKey, e.target.value)} />
                    </div>
                  ))}
                </SectionCard>

              </>
            )}

            {/* ════════════════ STEP 10 — OFFER, ACCEPTANCE & BROKERS / ESCROW ════════════════ */}
            {step === 10 && (
              <>

                {/* ════ PARA 32 — OFFER ════ */}
                <SectionCard num="32" title="Offer" note="Para 32A · 32B · 32C · 32D">
                  <div className="rpa-legal-flow" style={{ fontSize: 12.5, color: '#3a3a42', lineHeight: 1.7 }}>

                    {/* A — Expiration */}
                    <p style={{ margin: '0 0 12px' }}>
                      <strong>A. EXPIRATION OF OFFER:</strong> This offer shall be deemed revoked and the deposit, if any, shall be returned to Buyer unless by the date and time specified in paragraph 3C, the offer is Signed by Seller and a Copy of the Signed offer is Delivered to Buyer or Buyer's Authorized Agent. <strong>Seller has no obligation to respond to an offer made.</strong>
                    </p>

                    {/* B — Entity Buyers */}
                    <p style={{ margin: '0 0 6px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <input type="checkbox" checked={form['_ui.entityBuyerEnabled']}
                        onChange={e => setForm(prev => ({ ...prev, '_ui.entityBuyerEnabled': e.target.checked, 'additionalFields.EntityBuyers': e.target.checked }))}
                        style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0, marginTop: 3 }}
                        title="additionalFields.EntityBuyers" />
                      <span><strong>B. ENTITY BUYERS:</strong> (Note: If this paragraph is completed, a Representative Capacity Signature Disclosure (C.A.R. Form RCSD) is not required for the Legally Authorized Signers designated below.)</span>
                    </p>

                    {form['_ui.entityBuyerEnabled'] && (
                      <div style={{ paddingLeft: 24 }}>
                        <p style={{ margin: '0 0 10px' }}>
                          <strong>(1) Non-Individual (entity) Buyers:</strong> One or more Buyers is a trust, corporation, LLC, probate estate, partnership, power of attorney or other entity.
                        </p>

                        <p style={{ margin: '0 0 4px' }}>
                          <strong>(2) Full entity name:</strong> The following is the full name of the entity (if a trust, enter the complete trust name; if under probate, enter full name of the estate, including case #):
                        </p>
                        <input className="rpa-input" type="text"
                          title="room.offer.PropertyCaseNumber"
                          style={{ width: '100%', marginBottom: 6 }}
                          value={form['room.offer.PropertyCaseNumber']}
                          onChange={e => set('room.offer.PropertyCaseNumber', e.target.value)} />
                        <input className="rpa-input" type="text"
                          title="room.offer.PropertyCaseNumber2"
                          style={{ width: '100%', marginBottom: 10 }}
                          value={form['room.offer.PropertyCaseNumber2']}
                          onChange={e => set('room.offer.PropertyCaseNumber2', e.target.value)} />

                        <p style={{ margin: '0 0 4px' }}><strong>(3) Contractual Identity of Buyer:</strong> For purposes of this Agreement, when the name described below is used, it shall be deemed to be the full entity name.</p>
                        <p style={{ margin: '0 0 4px', paddingLeft: 16 }}>(A) If a trust: The trustee(s) of the trust or a simplified trust name (ex. John Doe, co-trustee, Jane Doe, co-trustee or Doe Revocable Family Trust);</p>
                        <p style={{ margin: '0 0 10px', paddingLeft: 16 }}>(B) If Property is sold under the jurisdiction of a probate court: The name of the executor or administrator, or a simplified probate name (John Doe, executor, or Estate (or Conservatorship) of John Doe).</p>

                        <p style={{ margin: '0 0 4px' }}><strong>(4) Legally Authorized Signer:</strong></p>
                        <p style={{ margin: '0 0 6px', paddingLeft: 16 }}>(A) This Agreement is being Signed by a Legally Authorized Signer in a representative capacity and not for him/herself as an individual. See paragraph 24 for additional terms.</p>
                        <p style={{ margin: '0 0 10px', paddingLeft: 16, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                          <span>(B) The name(s) of the Legally Authorized Signer(s) is/are:</span>
                          <input className="rpa-input" type="text" title="room.details.AuthorizedSignerName"
                            style={{ display: 'inline-block', width: 200 }}
                            value={form['room.details.AuthorizedSignerName']}
                            onChange={e => set('room.details.AuthorizedSignerName', e.target.value)} />
                          <span>,</span>
                          <input className="rpa-input" type="text" title="room.details.AuthorizedSignerName2"
                            style={{ display: 'inline-block', width: 200 }}
                            value={form['room.details.AuthorizedSignerName2']}
                            onChange={e => set('room.details.AuthorizedSignerName2', e.target.value)} />
                        </p>
                      </div>
                    )}

                    {/* C — Acknowledgement */}
                    <p style={{ margin: '0 0 12px' }}>
                      <strong>C.</strong> The RPA has <strong>17 pages</strong>. Buyer acknowledges receipt of, and has read and understands, every page and all attachments that make up the Agreement.
                    </p>

                    {/* D — Buyer Signature(s) */}
                    <p style={{ margin: '0 0 8px' }}><strong>D. BUYER SIGNATURE(S):</strong></p>

                    {[
                      { nameKey: 'room.details.buyer1.name', authKey: 'room.details.AuthorizedSignerName', confirmKey: 'additionalFields.ClientRepresentative', titleKey: 'room.details.BuyerEntityTitle' },
                      { nameKey: 'room.details.buyer2.name', authKey: 'room.details.AuthorizedSignerName2', confirmKey: 'additionalFields.ClientRepresentative2', titleKey: 'room.details.BuyerEntityTitle2' },
                    ].map((row, i) => (
                      <div key={row.nameKey} style={{ paddingLeft: 16, marginBottom: 12, borderBottom: i === 0 ? '1px dashed #e5e5ea' : 'none', paddingBottom: i === 0 ? 12 : 0 }}>
                        <p style={{ margin: '0 0 4px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                          <span>Printed name of BUYER:</span>
                          <input className="rpa-input" type="text" title={row.nameKey}
                            style={{ display: 'inline-block', width: 220 }}
                            value={form[row.nameKey]}
                            onChange={e => set(row.nameKey, e.target.value)} />
                        </p>
                        <p style={{ margin: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                          <input type="checkbox" checked={form[row.confirmKey]}
                            onChange={e => set(row.confirmKey, e.target.checked)}
                            style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }}
                            title={row.confirmKey} />
                          <span>Printed Name of Legally Authorized Signer:</span>
                          <input className="rpa-input" type="text" title={row.authKey}
                            style={{ display: 'inline-block', width: 200 }}
                            value={form[row.authKey]}
                            onChange={e => set(row.authKey, e.target.value)} />
                          <span style={{ marginLeft: 12 }}>Title, if applicable,</span>
                          <input className="rpa-input" type="text" title={row.titleKey}
                            style={{ display: 'inline-block', width: 160 }}
                            value={form[row.titleKey]}
                            onChange={e => set(row.titleKey, e.target.value)} />
                        </p>
                      </div>
                    ))}

                    <p style={{ margin: 0, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <input type="checkbox" checked={form['additionalFields.AdditionalSignature']}
                        onChange={e => set('additionalFields.AdditionalSignature', e.target.checked)}
                        style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0, marginTop: 2 }}
                        title="additionalFields.AdditionalSignature" />
                      <span>IF MORE THAN TWO SIGNERS, USE Additional Signature Addendum (C.A.R. Form ASA).</span>
                    </p>

                  </div>
                </SectionCard>

                {/* ════ PARA 33 — ACCEPTANCE ════ */}
                <SectionCard num="33" title="Acceptance" note="Para 33A · 33B · 33C · 33D">
                  <div className="rpa-legal-flow" style={{ fontSize: 12.5, color: '#3a3a42', lineHeight: 1.7 }}>

                    {/* A — Acceptance of Offer */}
                    <p style={{ margin: '0 0 6px' }}>
                      <strong>A. ACCEPTANCE OF OFFER:</strong> Seller warrants that Seller is the owner of the Property or has the authority to execute this Agreement. Seller accepts the above offer and agrees to sell the Property on the above terms and conditions. Seller has read and acknowledges receipt of a Copy of this Agreement and authorizes Agent to Deliver a Signed Copy to Buyer.
                    </p>
                    <p style={{ margin: '0 0 8px' }}>
                      Seller's acceptance is subject to the attached Counter Offer or Back-Up Offer Addendum, or both, checked below.
                    </p>
                    <p style={{ margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={form['additionalFields.CounterOffer']}
                        onChange={e => set('additionalFields.CounterOffer', e.target.checked)}
                        style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }}
                        title="additionalFields.CounterOffer" />
                      <span>Seller Counter Offer (C.A.R. Form SCO or SMCO)</span>
                    </p>
                    <p style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="checkbox" checked={form['additionalFields.BackUpContractAddendum']}
                        onChange={e => set('additionalFields.BackUpContractAddendum', e.target.checked)}
                        style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }}
                        title="additionalFields.BackUpContractAddendum" />
                      <span>Back-Up Offer Addendum (C.A.R. Form BUO)</span>
                    </p>

                    {/* B — Entity Sellers */}
                    <p style={{ margin: '0 0 6px', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <input type="checkbox" checked={form['_ui.entitySellerEnabled']}
                        onChange={e => setForm(prev => ({ ...prev, '_ui.entitySellerEnabled': e.target.checked, 'additionalFields.EntitySellers': e.target.checked }))}
                        style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0, marginTop: 3 }}
                        title="additionalFields.EntitySellers" />
                      <span><strong>B. ENTITY SELLERS:</strong> (Note: If this paragraph is completed, a Representative Capacity Signature Disclosure (C.A.R. Form RCSD) is not required for the Legally Authorized Signers designated below.)</span>
                    </p>

                    {form['_ui.entitySellerEnabled'] && (
                      <div style={{ paddingLeft: 24 }}>
                        <p style={{ margin: '0 0 10px' }}>
                          <strong>(1) Non-Individual (entity) Sellers:</strong> One or more Sellers is a trust, corporation, LLC, probate estate, partnership, power of attorney or other entity.
                        </p>

                        <p style={{ margin: '0 0 4px' }}>
                          <strong>(2) Full entity name:</strong> The following is the full name of the entity (if a trust, enter the complete trust name; if under probate, enter full name of the estate, including case #):
                        </p>
                        <input className="rpa-input" type="text"
                          title="room.offer.PropertyCaseNumber3"
                          style={{ width: '100%', marginBottom: 6 }}
                          value={form['room.offer.PropertyCaseNumber3']}
                          onChange={e => set('room.offer.PropertyCaseNumber3', e.target.value)} />
                        <input className="rpa-input" type="text"
                          title="room.offer.PropertyCaseNumber4"
                          style={{ width: '100%', marginBottom: 10 }}
                          value={form['room.offer.PropertyCaseNumber4']}
                          onChange={e => set('room.offer.PropertyCaseNumber4', e.target.value)} />

                        <p style={{ margin: '0 0 4px' }}><strong>(3) Contractual Identity of Seller:</strong> For purposes of this Agreement, when the name described below is used, it shall be deemed to be the full entity name.</p>
                        <p style={{ margin: '0 0 4px', paddingLeft: 16 }}>(A) If a trust: The trustee(s) of the trust or a simplified trust name (ex. John Doe, co-trustee, Jane Doe, co-trustee or Doe Revocable Family Trust);</p>
                        <p style={{ margin: '0 0 10px', paddingLeft: 16 }}>(B) If Property is sold under the jurisdiction of a probate court: The name of the executor or administrator, or a simplified probate name (John Doe, executor, or Estate (or Conservatorship) of John Doe).</p>

                        <p style={{ margin: '0 0 4px' }}><strong>(4) Legally Authorized Signer:</strong></p>
                        <p style={{ margin: '0 0 6px', paddingLeft: 16 }}>(A) This Agreement is being Signed by a Legally Authorized Signer in a representative capacity and not for him/herself as an individual. See paragraph 24 for additional terms.</p>
                        <p style={{ margin: '0 0 10px', paddingLeft: 16, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                          <span>(B) The name(s) of the Legally Authorized Signer(s) is/are:</span>
                          <input className="rpa-input" type="text" title="room.details.AuthorizedSignerName3"
                            style={{ display: 'inline-block', width: 200 }}
                            value={form['room.details.AuthorizedSignerName3']}
                            onChange={e => set('room.details.AuthorizedSignerName3', e.target.value)} />
                          <span>,</span>
                          <input className="rpa-input" type="text" title="room.details.AuthorizedSignerName4"
                            style={{ display: 'inline-block', width: 200 }}
                            value={form['room.details.AuthorizedSignerName4']}
                            onChange={e => set('room.details.AuthorizedSignerName4', e.target.value)} />
                        </p>
                      </div>
                    )}

                    {/* C — Acknowledgement */}
                    <p style={{ margin: '0 0 12px' }}>
                      <strong>C.</strong> The RPA has <strong>17 pages</strong>. Seller acknowledges receipt of, and has read and understands, every page and all attachments that make up the Agreement.
                    </p>

                    {/* D — Seller Signature(s) */}
                    <p style={{ margin: '0 0 8px' }}><strong>D. SELLER SIGNATURE(S):</strong></p>

                    {[
                      { nameKey: 'room.details.seller1.name', authKey: 'room.details.AuthorizedSignerName3', confirmKey: 'additionalFields.ClientRepresentative3', titleKey: 'room.details.SellerEntityTitle' },
                      { nameKey: 'room.details.seller2.name', authKey: 'room.details.AuthorizedSignerName4', confirmKey: 'additionalFields.ClientRepresentative4', titleKey: 'room.details.SellerEntityTitle2' },
                    ].map((row, i) => (
                      <div key={row.nameKey} style={{ paddingLeft: 16, marginBottom: 12, borderBottom: i === 0 ? '1px dashed #e5e5ea' : 'none', paddingBottom: i === 0 ? 12 : 0 }}>
                        <p style={{ margin: '0 0 4px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                          <span>Printed name of SELLER:</span>
                          <input className="rpa-input" type="text" title={row.nameKey}
                            style={{ display: 'inline-block', width: 220 }}
                            value={form[row.nameKey]}
                            onChange={e => set(row.nameKey, e.target.value)} />
                        </p>
                        <p style={{ margin: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                          <input type="checkbox" checked={form[row.confirmKey]}
                            onChange={e => set(row.confirmKey, e.target.checked)}
                            style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }}
                            title={row.confirmKey} />
                          <span>Printed Name of Legally Authorized Signer:</span>
                          <input className="rpa-input" type="text" title={row.authKey}
                            style={{ display: 'inline-block', width: 200 }}
                            value={form[row.authKey]}
                            onChange={e => set(row.authKey, e.target.value)} />
                          <span style={{ marginLeft: 12 }}>Title, if applicable,</span>
                          <input className="rpa-input" type="text" title={row.titleKey}
                            style={{ display: 'inline-block', width: 160 }}
                            value={form[row.titleKey]}
                            onChange={e => set(row.titleKey, e.target.value)} />
                        </p>
                      </div>
                    ))}

                    <p style={{ margin: 0, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <input type="checkbox" checked={form['additionalFields.AdditionalSignature2']}
                        onChange={e => set('additionalFields.AdditionalSignature2', e.target.checked)}
                        style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0, marginTop: 2 }}
                        title="additionalFields.AdditionalSignature2" />
                      <span>IF MORE THAN TWO SIGNERS, USE Additional Signature Addendum (C.A.R. Form ASA).</span>
                    </p>

                  </div>
                </SectionCard>


                {/* ════ REAL ESTATE BROKERS SECTION ════ */}
                <SectionCard num="4" title="Real Estate Brokers Section" note="Buyer's & Seller's Agent signatures & contact">

                  <div className="rpa-legal-flow" style={{ fontSize: 12.5, color: '#3a3a42', lineHeight: 1.7, marginBottom: 16 }}>
                    <p style={{ margin: '0 0 4px' }}><strong>1.</strong> Real Estate Agents are not parties to the Agreement between Buyer and Seller.</p>
                    <p style={{ margin: '0 0 4px' }}><strong>2.</strong> Agency relationships are confirmed as stated in paragraph 2.</p>
                    <p style={{ margin: '0 0 4px' }}><strong>3. Presentation of Offer:</strong> Pursuant to the National Association of REALTORS® Standard of Practice 1-7, if Buyer's Agent makes a written request, Seller's Agent shall confirm in writing that this offer has been presented to Seller.</p>
                    <p style={{ margin: 0 }}><strong>4.</strong> Agents' Signatures and designated electronic delivery address:</p>
                  </div>

                  {[
                    {
                      side: 'A', label: "Buyer's", role: 'Buyer',
                      firmKey: 'room.details.buyerAgent1.company', firmDreKey: 'room.details.buyerBrokerage.licenseNum',
                      agent1DreKey: 'room.details.designatedbuyerAgent.licenseNum',
                      agent2DreKey: 'room.details.designatedbuyerAgent2.licenseNum',
                      addr1Key: 'room.details.buyerBrokerage.address', addr2Key: 'TODO_BuyerBrokerageAddress2',
                      cityKey: 'room.details.buyerBrokerage.city', stateKey: 'room.details.buyerBrokerage.state', zipKey: 'room.details.buyerBrokerage.zip',
                      emailKey: 'room.details.buyerBrokerage.email', phoneKey: 'room.details.buyerBrokerage.phone',
                      multiAgentKey: 'additionalFields.MultipleRepresentationWithDesignatedAgency2', multiBrokerKey: 'additionalFields.MultipleRepresentationWithDesignatedAgency3',
                      dedaTextKey: 'room.details.DocumentDeliveryMethodLabel', dedaCheckKey: 'additionalFields.EmailDeliveryOfDocumentMethod',
                      dedaNote: 'Email above or',
                    },
                    {
                      side: 'B', label: "Seller's", role: 'Seller',
                      firmKey: 'room.details.listingAgent1.company', firmDreKey: 'room.details.sellerBrokerage.LicenseNum',
                      agent1DreKey: 'room.details.designatedsellerAgent.licenseNum',
                      agent2DreKey: 'room.details.designatedsellerAgent2.licenseNum',
                      addr1Key: 'room.details.sellerBrokerage.address', addr2Key: 'TODO_SellerBrokerageAddress2',
                      cityKey: 'room.details.sellerBrokerage.city', stateKey: 'room.details.sellerBrokerage.state', zipKey: 'room.details.sellerBrokerage.zip',
                      emailKey: 'room.details.sellerBrokerage.email', phoneKey: 'room.details.sellerBrokerage.phone',
                      multiAgentKey: 'additionalFields.MultipleRepresentationWithDesignatedAgency4', multiBrokerKey: 'additionalFields.MultipleRepresentationWithDesignatedAgency5',
                      dedaTextKey: 'room.details.DocumentDeliveryMethodLabel2', dedaCheckKey: 'additionalFields.EmailDeliveryOfDocumentMethod2',
                      dedaNote: "(To be filled out by Seller's Agent): Email above or",
                    },
                  ].map(b => (
                    <div key={b.side} style={{ background: '#f0f4fa', border: '1px solid #d0ddf0', borderRadius: 8, padding: 14, marginBottom: 14 }}>
                      <DivLabel>{b.side}. {b.label} Brokerage Firm</DivLabel>

                      <div className="rpa-field-row rpa-cols-2" style={{ marginBottom: 8 }}>
                        <Fld label={`${b.label} Brokerage Firm`} hint={b.firmKey}>
                          <input className="rpa-input" type="text" placeholder="Full legal name of brokerage"
                            value={form[b.firmKey]}
                            onChange={e => set(b.firmKey, e.target.value)} />
                        </Fld>
                        <Fld label="Firm DRE Lic. #" hint={b.firmDreKey}>
                          <input className="rpa-input" type="text" placeholder="e.g. 01234567"
                            value={form[b.firmDreKey]}
                            onChange={e => set(b.firmDreKey, e.target.value)} />
                        </Fld>
                      </div>

                      <div className="rpa-field-row rpa-cols-2" style={{ marginBottom: 10 }}>
                        <Fld label="Agent 1 — DRE Lic. #" hint={b.agent1DreKey}>
                          <input className="rpa-input" type="text" placeholder="e.g. 01234567"
                            value={form[b.agent1DreKey]}
                            onChange={e => set(b.agent1DreKey, e.target.value)} />
                        </Fld>
                        <Fld label="Agent 2 — DRE Lic. # (if applicable)" hint={b.agent2DreKey}>
                          <input className="rpa-input" type="text" placeholder="e.g. 01234567"
                            value={form[b.agent2DreKey]}
                            onChange={e => set(b.agent2DreKey, e.target.value)} />
                        </Fld>
                      </div>

                      <CheckRow
                        checked={form[b.multiAgentKey]}
                        onChange={v => set(b.multiAgentKey, v)}
                        label={`More than one agent from the same firm represents ${b.role}. Additional Agent Acknowledgement (C.A.R. Form AAA) attached.`}
                        hint={b.multiAgentKey}
                      />
                      <CheckRow
                        checked={form[b.multiBrokerKey]}
                        onChange={v => set(b.multiBrokerKey, v)}
                        label={`More than one brokerage firm represents ${b.role}. Additional Broker Acknowledgement (C.A.R. Form ABA) attached.`}
                        hint={b.multiBrokerKey}
                      />

                      <p style={{ margin: '10px 0 4px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, fontSize: 12.5 }}>
                        <span><strong>Designated Electronic Delivery Address(es)</strong> {b.dedaNote}</span>
                        <input className="rpa-input" type="text" title={b.dedaTextKey}
                          style={{ display: 'inline-block', width: 220 }}
                          value={form[b.dedaTextKey]}
                          onChange={e => set(b.dedaTextKey, e.target.value)} />
                      </p>
                      <CheckRow
                        checked={form[b.dedaCheckKey]}
                        onChange={v => set(b.dedaCheckKey, v)}
                        label="Attached DEDA: If Parties elect to have an alternative Delivery method, such method may be indicated on C.A.R. Form DEDA."
                        hint={b.dedaCheckKey}
                      />
                    </div>
                  ))}

                </SectionCard>

                {/* ════ ESCROW HOLDER ACKNOWLEDGMENT ════ */}
                <SectionCard num="EH" title="Escrow Holder Acknowledgment" note="Escrow receipt · Holder details · License">
                  <div className="rpa-legal-flow" style={{ fontSize: 12.5, color: '#3a3a42', lineHeight: 1.9 }}>

                    <p style={{ margin: '0 0 6px' }}><strong>ESCROW HOLDER ACKNOWLEDGMENT:</strong></p>

                    <p style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <span>Escrow Holder acknowledges receipt of a Copy of this Agreement, (if checked</span>
                      <input type="checkbox" checked={form['additionalFields.depositEarnestMoney']}
                        onChange={e => set('additionalFields.depositEarnestMoney', e.target.checked)}
                        style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }}
                        title="additionalFields.depositEarnestMoney" />
                      <span>a deposit in the amount of $</span>
                      <input className="rpa-input" type="text" title="room.offer.finalearnestmoneyAmount"
                        style={{ display: 'inline-block', width: 110 }}
                        value={form['room.offer.finalearnestmoneyAmount']}
                        onChange={e => set('room.offer.finalearnestmoneyAmount', e.target.value)} />
                      <span>), Counter Offer numbers</span>
                      <span>and</span>
                      <input className="rpa-input" type="text" title="room.offer.CounterofferNumber"
                        style={{ display: 'inline-block', width: 90 }}
                        value={form['room.offer.CounterofferNumber']}
                        onChange={e => set('room.offer.CounterofferNumber', e.target.value)} />
                      <span>and agrees to act as Escrow Holder subject to <strong>paragraph 19</strong> of this Agreement, any supplemental escrow instructions and the terms of Escrow Holder's general provisions.</span>
                    </p>

                    <p style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <span>Escrow Holder is advised by</span>
                      <span>that the date of Acceptance of the Agreement is</span>
                      <input className="rpa-input" type="date" title="room.offer.bindingDate"
                        style={{ display: 'inline-block', width: 160 }}
                        value={form['room.offer.bindingDate']}
                        onChange={e => set('room.offer.bindingDate', e.target.value)} />
                    </p>

                    <p style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <span>Escrow Holder</span>
                      <input className="rpa-input" type="text" title="room.offer.entityHoldingEarnestMoney"
                        style={{ display: 'inline-block', width: 280 }}
                        value={form['room.offer.entityHoldingEarnestMoney']}
                        onChange={e => set('room.offer.entityHoldingEarnestMoney', e.target.value)} />
                      <span style={{ marginLeft: 12 }}>Escrow #</span>
                      <input className="rpa-input" type="text" title="room.details.escrowCompany.number"
                        style={{ display: 'inline-block', width: 160 }}
                        value={form['room.details.escrowCompany.number']}
                        onChange={e => set('room.details.escrowCompany.number', e.target.value)} />
                    </p>

                    <p style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <span>Address</span>
                      <input className="rpa-input" type="text" title="room.details.escrowCompany.address"
                        style={{ display: 'inline-block', width: 200 }}
                        value={form['room.details.escrowCompany.address']}
                        onChange={e => set('room.details.escrowCompany.address', e.target.value)} />
                      <input className="rpa-input" type="text" title="room.details.escrowCompany.address2"
                        style={{ display: 'inline-block', width: 140 }}
                        value={form['room.details.escrowCompany.address2']}
                        onChange={e => set('room.details.escrowCompany.address2', e.target.value)} />
                      <span style={{ marginLeft: 8 }}>City</span>
                      <WizardCitySelect cityKey="room.details.escrowCompany.city" stateKey="room.details.escrowCompany.state" form={form} set={set} />
                      <span>State</span>
                      <span style={{ padding: '4px 8px', border: '1px solid #d0ddf0', borderRadius: 5, background: '#f0f4fa' }}>CA</span>
                      <span>Zip</span>
                      <input className="rpa-input" type="text" title="room.details.escrowCompany.postalCode"
                        style={{ display: 'inline-block', width: 90 }}
                        value={form['room.details.escrowCompany.postalCode']}
                        onChange={e => set('room.details.escrowCompany.postalCode', e.target.value)} />
                    </p>

                    <p style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <span>Phone</span>
                      <input className="rpa-input" type="text" title="room.details.escrowOfficer.cellPhone"
                        style={{ display: 'inline-block', width: 150 }}
                        value={form['room.details.escrowOfficer.cellPhone']}
                        onChange={e => set('room.details.escrowOfficer.cellPhone', e.target.value)} />
                      <span>/ Fax</span>
                      <input className="rpa-input" type="text" title="room.details.escrowOfficer.fax"
                        style={{ display: 'inline-block', width: 150 }}
                        value={form['room.details.escrowOfficer.fax']}
                        onChange={e => set('room.details.escrowOfficer.fax', e.target.value)} />
                      <span>/ E-mail</span>
                      <input className="rpa-input" type="text" title="room.details.escrowOfficer.email"
                        style={{ display: 'inline-block', width: 220 }}
                        value={form['room.details.escrowOfficer.email']}
                        onChange={e => set('room.details.escrowOfficer.email', e.target.value)} />
                    </p>

                    <p style={{ margin: '0 0 10px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}>
                      <span>Escrow Holder has the following license number #</span>
                      <input className="rpa-input" type="text" title="room.details.escrowOfficer.LicenseNumber"
                        style={{ display: 'inline-block', width: 180 }}
                        value={form['room.details.escrowOfficer.LicenseNumber']}
                        onChange={e => set('room.details.escrowOfficer.LicenseNumber', e.target.value)} />
                    </p>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                      <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="checkbox" checked={form['additionalFields.FinancialProtectionDepartment']}
                          onChange={e => set('additionalFields.FinancialProtectionDepartment', e.target.checked)}
                          style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }}
                          title="additionalFields.FinancialProtectionDepartment" />
                        <span>Department of Financial Protection and Innovation.</span>
                      </p>
                      <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="checkbox" checked={form['additionalFields.InsuranceDepartment']}
                          onChange={e => set('additionalFields.InsuranceDepartment', e.target.checked)}
                          style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }}
                          title="additionalFields.InsuranceDepartment" />
                        <span>Department of Insurance.</span>
                      </p>
                      <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="checkbox" checked={form['additionalFields.RealEstateDepartment']}
                          onChange={e => set('additionalFields.RealEstateDepartment', e.target.checked)}
                          style={{ accentColor: '#5B47D6', width: 15, height: 15, flexShrink: 0 }}
                          title="additionalFields.RealEstateDepartment" />
                        <span>Department of Real Estate.</span>
                      </p>
                    </div>

                  </div>
                </SectionCard>

              </>
            )}

            {/* ════════════════ STEP 11 — REVIEW & SIGN ════════════════ */}
            {step === 11 && (() => {
              const fmtVal = (v) => v || '—';
              const fmtAmt = (v, prefix = '$') => v ? `${prefix}${Number(v).toLocaleString()}` : '—';
              const ReviewBlock = ({ icon, title, rows }) => (
                <div className="rpa-review-block">
                  <div className="rpa-review-block-header">{icon}{title}</div>
                  {rows.filter(r => r).map(([label, value], i) => (
                    <div key={i} className="rpa-review-row">
                      <span className="rpa-review-label">{label}</span>
                      <span className="rpa-review-value">{value || '—'}</span>
                    </div>
                  ))}
                </div>
              );
              const checkedList = (pairs) => {
                const sel = pairs.filter(([key]) => form[key]).map(([, lbl]) => lbl);
                return sel.length ? sel.join(', ') : '—';
              };

              // Shared SVG icons
              const IcoHome = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6,verticalAlign:'middle',flexShrink:0}}><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><polyline points="9 21 9 12 15 12 15 21"/></svg>;
              const IcoPeople = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6,verticalAlign:'middle',flexShrink:0}}><circle cx="9" cy="7" r="3"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><path d="M21 21v-2a4 4 0 0 0-3-3.87"/></svg>;
              const IcoDollar = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6,verticalAlign:'middle',flexShrink:0}}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
              const IcoBank = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6,verticalAlign:'middle',flexShrink:0}}><rect x="3" y="9" width="18" height="12" rx="2"/><path d="M3 9l9-6 9 6"/><line x1="12" y1="9" x2="12" y2="21"/></svg>;
              const IcoShield = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6,verticalAlign:'middle',flexShrink:0}}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
              const IcoCal = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6,verticalAlign:'middle',flexShrink:0}}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
              const IcoBriefcase = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6,verticalAlign:'middle',flexShrink:0}}><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>;
              const IcoList = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6,verticalAlign:'middle',flexShrink:0}}><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>;
              const IcoTag = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6,verticalAlign:'middle',flexShrink:0}}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>;
              const IcoFile = <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6,verticalAlign:'middle',flexShrink:0}}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>;

              const possessionLabel = (() => {
                if (form['additionalFields.PossessionAtClosing']) return 'At Close of Escrow';
                if (form['additionalFields.PossessionOther']) {
                  const t = [form['room.offer.PossessionTime'], form['additionalFields.PossessionTime']].filter(Boolean).join(' ');
                  return t || 'Custom time';
                }
                return form['_ui.m1Possession'] || '—';
              })();

              const applianceList = checkedList([
                ['additionalFields.Stove', 'Stove'],
                ['additionalFields.Refrigerator', 'Refrigerator'],
                ['additionalFields.WineRefrigerator', 'Wine Refrigerator'],
                ['additionalFields.Washer', 'Washer'],
                ['additionalFields.Dryer', 'Dryer'],
                ['additionalFields.Dishwasher', 'Dishwasher'],
                ['additionalFields.Microwave', 'Microwave'],
                ['additionalFields.DoorBell', 'Doorbell'],
                ['additionalFields.SecuritySystem', 'Security System'],
                ['additionalFields.SecuritySystem2', 'Security System (2)'],
                ['additionalFields.SmartHomeFeatures', 'Smart Home Features'],
                ['additionalFields.AboveGroundPool', 'Above-Ground Pool'],
                ['additionalFields.ElectricCarCharger', 'EV Charger'],
              ]);

              const addendaList = checkedList([
                ['additionalFields.TenantOccupiedPropertyAddendum2', 'TOPA'],
                ['additionalFields.OtherAddenda', 'PAPA'],
                ['additionalFields.OtherAddenda2', 'MHPA'],
                ['additionalFields.OtherAddenda3', 'TICPA'],
                ['additionalFields.OtherAddenda4', 'COOPPA'],
                ['additionalFields.OtherAddenda5', 'MUPA'],
                ['additionalFields.OtherAddenda12', 'SSA'],
                ['additionalFields.OtherAddenda8', 'BUO'],
                ['additionalFields.OtherAddenda13', 'CCA'],
                ['additionalFields.OtherAddenda9', 'SWPI-C'],
                ['additionalFields.OtherAddenda10', 'BXA'],
                ['additionalFields.OtherAddenda14', 'SXA'],
              ]) + ', AD (fixed)';

              const advisoriesList = checkedList([
                ['additionalField.OtherAdvisories', 'WFDA'],
                ['additionalField.OtherAdvisories5', 'SBSA'],
                ['additionalField.OtherAdvisories2', 'TA'],
                ['additionalField.OtherAdvisories6', 'PA'],
                ['additionalField.OtherAdvisories3', 'REO'],
              ]) + ', BIA, WFA, FHDA, BHIA (fixed)';

              const escrowLicensedBy = [
                form['additionalFields.FinancialProtectionDepartment'] && 'Dept. of Financial Protection',
                form['additionalFields.InsuranceDepartment'] && 'Dept. of Insurance',
                form['additionalFields.RealEstateDepartment'] && 'Dept. of Real Estate',
              ].filter(Boolean).join(', ') || null;

              return (
                <div>
                  <p style={{ fontSize: 14, color: '#666', marginBottom: 20 }}>
                    Review all entered information before submitting.
                  </p>

                  {/* Step 1 — Property */}
                  <ReviewBlock
                    icon={IcoHome}
                    title="Property"
                    rows={[
                      ['Date Prepared',      fmtVal(form['room.offer.presentedofferdate'])],
                      ['Contract Date',      fmtVal(form['room.offer.contractDate'])],
                      ['Address',            fmtVal(form['room.property.location.address1']) + (form['room.property.location.address2'] ? ', ' + form['room.property.location.address2'] : '')],
                      ['City / State / Zip', [form['room.property.location.city'], form['room.property.location.state'], form['room.property.location.postalCode']].filter(Boolean).join(', ')],
                      ['County',             fmtVal(form['room.property.location.county'])],
                      ['APN',                fmtVal(form['room.property.ParcelID'])],
                    ]}
                  />

                  {/* Steps 1-2 — Buyers */}
                  <ReviewBlock
                    icon={IcoPeople}
                    title="Buyers"
                    rows={[
                      ['Buyer 1',              fmtVal(form['room.details.buyer1.name'])],
                      form['room.details.buyer2.name'] ? ['Buyer 2', form['room.details.buyer2.name']] : null,
                      form['additionalFields.EntityBuyers'] ? ['Entity Name', [fmtVal(form['room.offer.PropertyCaseNumber']), form['room.offer.PropertyCaseNumber2']].filter(Boolean).join(' — ')] : null,
                      form['additionalFields.EntityBuyers'] && form['room.details.AuthorizedSignerName'] ? ['Auth Signer 1', form['room.details.AuthorizedSignerName']] : null,
                      form['room.details.BuyerEntityTitle'] ? ['Auth Signer 1 Title', form['room.details.BuyerEntityTitle']] : null,
                      form['additionalFields.EntityBuyers'] && form['room.details.AuthorizedSignerName2'] ? ['Auth Signer 2', form['room.details.AuthorizedSignerName2']] : null,
                      form['room.details.BuyerEntityTitle2'] ? ['Auth Signer 2 Title', form['room.details.BuyerEntityTitle2']] : null,
                      form['additionalFields.AdditionalSignature'] ? ['Additional Signature Addendum (ASA)', 'Attached'] : null,
                    ]}
                  />

                  {/* Step 1 — Sellers */}
                  <ReviewBlock
                    icon={IcoPeople}
                    title="Sellers"
                    rows={[
                      ['Seller 1',             fmtVal(form['room.details.seller1.name'])],
                      form['room.details.seller2.name'] ? ['Seller 2', form['room.details.seller2.name']] : null,
                      form['additionalFields.CounterOffer'] ? ['Seller Counter Offer', 'Attached (SCO/SMCO)'] : null,
                      form['additionalFields.BackUpContractAddendum'] ? ['Back-Up Offer', 'Attached (BUO)'] : null,
                    ]}
                  />

                  {/* Step 2 — Listing Brokerage */}
                  <ReviewBlock
                    icon={IcoBriefcase}
                    title="Listing Brokerage"
                    rows={[
                      ['Brokerage Firm',   fmtVal(form['room.details.listingAgent1.company'])],
                      ['Brokerage DRE #',  fmtVal(form['room.details.sellerBrokerage.LicenseNum'])],
                      ['Listing Agent',    fmtVal(form['room.details.listingAgent1.name'])],
                      ['Agent DRE #',      fmtVal(form['room.details.designatedsellerAgent.licenseNum'])],
                      form['room.details.listingAgent1.phone'] ? ['Phone', form['room.details.listingAgent1.phone']] : null,
                      form['room.details.listingAgent1.email'] ? ['Email', form['room.details.listingAgent1.email']] : null,
                      form['room.details.listingAgent1.address1'] ? ['Address', [form['room.details.listingAgent1.address1'], form['room.details.listingAgent1.city'], form['room.details.listingAgent1.state']].filter(Boolean).join(', ')] : null,
                      form['additionalFields.SellerBrokerageType'] ? ['Brokerage Role', form['additionalFields.SellerBrokerageType']] : null,
                      form['additionalFields.AgentType'] ? ['Agent Role', form['additionalFields.AgentType']] : null,
                    ]}
                  />

                  {/* Step 2 — Buyer Brokerage */}
                  <ReviewBlock
                    icon={IcoBriefcase}
                    title="Buyer's Brokerage"
                    rows={[
                      ['Brokerage Firm',     fmtVal(form['room.details.buyerAgent1.company'])],
                      ['Brokerage DRE #',    fmtVal(form['room.details.buyerBrokerage.licenseNum'])],
                      ['Buyer Agent',        fmtVal(form['room.details.buyerAgent1.name'])],
                      ['Agent DRE #',        fmtVal(form['room.details.designatedbuyerAgent.licenseNum'])],
                      form['room.details.buyerAgent1.phone'] ? ['Phone', form['room.details.buyerAgent1.phone']] : null,
                      form['room.details.buyerAgent1.email'] ? ['Email', form['room.details.buyerAgent1.email']] : null,
                      form['additionalFields.SellerorBuyerRadio'] ? ['Broker Comp Paid By', form['additionalFields.SellerorBuyerRadio']] : null,
                      form['additionalFields.BuyerBrokerageType'] ? ['Brokerage Role', form['additionalFields.BuyerBrokerageType']] : null,
                      form['additionalFields.AgentType2'] ? ['Agent Role', form['additionalFields.AgentType2']] : null,
                      form['additionalFields.MultipleRepresentationWithDesignatedAgency'] ? ['Multiple Representation w/ Designated Agency', 'Yes'] : null,
                    ]}
                  />

                  {/* Step 3 — Financial Terms */}
                  <ReviewBlock
                    icon={IcoDollar}
                    title="Financial Terms"
                    rows={[
                      ['Purchase Price',    fmtAmt(form['room.offer.contractAmount'])],
                      ['All-Cash Offer',    form['additionalFields.MethodofPaymentCash'] ? 'Yes' : 'No'],
                      ['Initial EMD',       fmtAmt(form['room.offer.earnestMoneyAmount'])],
                      form['room.offer.earnestmoneypercentofpurchase'] ? ['EMD % of Price', form['room.offer.earnestmoneypercentofpurchase'] + '%'] : null,
                      form['room.offer.earnestmoneyDueDays'] ? ['EMD Due (Business Days)', form['room.offer.earnestmoneyDueDays']] : null,
                      form['room.offer.ClosingPeriodFromDateOfAcceptance'] ? ['Close of Escrow', form['room.offer.ClosingPeriodFromDateOfAcceptance'] + ' days after acceptance'] : null,
                      form['room.offer.COEDate'] ? ['Specific COE Date', form['room.offer.COEDate']] : null,
                      form['room.offer.offerExpireDate'] ? ['Offer Expiry Date', form['room.offer.offerExpireDate']] : null,
                      (form['room.offer.OfferExpireTime'] || form['additionalFields.OfferExpireTime']) ? ['Offer Expiry Time', [form['room.offer.OfferExpireTime'], form['additionalFields.OfferExpireTime']].filter(Boolean).join(' ')] : null,
                    ]}
                  />

                  {/* Step 4 — Financing (hidden when all-cash) */}
                  {!form['additionalFields.MethodofPaymentCash'] && (
                    <ReviewBlock
                      icon={IcoBank}
                      title="Financing"
                      rows={[
                        ['1st Loan Amount',      fmtAmt(form['room.offer.firstMortgageLoanAmount'])],
                        form['room.offer.loanPercentofPurchasePrice'] ? ['1st Loan %', form['room.offer.loanPercentofPurchasePrice'] + '%'] : null,
                        ['Loan Type',            fmtVal(form['_ui.firstLoanType'])],
                        ['Rate Type',            fmtVal(form['_ui.firstLoanRateType'])],
                        form['room.offer.MaxInitialAdjustableLoanPercent'] ? ['Max Interest Rate', form['room.offer.MaxInitialAdjustableLoanPercent'] + '%'] : null,
                        form['room.offer.LoanApplicationPeriod'] ? ['Loan App. Period', form['room.offer.LoanApplicationPeriod'] + ' days'] : null,
                        form['room.offer.AmountFinanced'] ? ['2nd Loan Amount', fmtAmt(form['room.offer.AmountFinanced'])] : null,
                        form['room.offer.AmountFinanced'] ? ['2nd Loan Type', fmtVal(form['_ui.addLoanType'])] : null,
                        form['room.offer.BalanceOfPurchasePrice'] ? ['Down Payment Balance', fmtAmt(form['room.offer.BalanceOfPurchasePrice'])] : null,
                        form['additionalFields.OccupancyType'] ? ['Occupancy Type', form['additionalFields.OccupancyType']] : null,
                      ]}
                    />
                  )}

                  {/* Step 5 — Seller Credits & Compensation */}
                  <ReviewBlock
                    icon={IcoDollar}
                    title="Seller Credits & Compensation"
                    rows={[
                      form['additionalFields.CreditReport'] ? ['Seller Credit to Buyer', fmtAmt(form['room.offer.SellerEstimatedCreditToBuyer'])] : null,
                      form['room.offer.OtherCreditDocuments'] ? ['Other Credits', form['room.offer.OtherCreditDocuments']] : null,
                      ['Buyer Broker Fee by Seller', form['additionalFields.BuyerBrokerageFeePaidbySeller'] ? 'Yes' : 'No'],
                      form['room.additionalInformation.buyerSideCommission'] ? ['Commission %', form['room.additionalInformation.buyerSideCommission'] + '%'] : null,
                      form['room.offer.salecommissionAmount'] ? ['Commission Amount', fmtAmt(form['room.offer.salecommissionAmount'])] : null,
                      form['room.offer.OtherFinancingTerms'] ? ['Other Financing Terms', form['room.offer.OtherFinancingTerms']] : null,
                      form['additionalFields.CooperativeBrokerCompensation'] && form['room.offer.additionalsalecommissionAmount'] ? ['Cooperative Broker Comp', fmtAmt(form['room.offer.additionalsalecommissionAmount'])] : null,
                    ]}
                  />

                  {/* Step 5 — Verification of Funds & Financing (§H) */}
                  <ReviewBlock
                    icon={IcoBank}
                    title="Verification of Funds & Financing"
                    rows={[
                      ['Cash Funds (H1)',        form['_ui.h1Timing'] === 'days' ? (fmtVal(form['room.offer.UnencomberedFundsEvidenceDays']) + ' days after acceptance') : 'Attached to offer'],
                      ['Down Payment Funds (H2)', form['_ui.h2Timing'] === 'days' ? (fmtVal(form['room.offer.UnencomberedFundsEvidenceDays2']) + ' days after acceptance') : 'Attached to offer'],
                      form['_ui.h3Approval'] ? ['Loan Pre-Approval (H3)', String(form['_ui.h3Approval']).replace(/_/g, ' ')] : null,
                    ]}
                  />

                  {/* Step 6 — Contingencies */}
                  <ReviewBlock
                    icon={IcoShield}
                    title="Contingencies"
                    rows={[
                      ['C.A.R. Form CR-B',      form['additionalFields.RemoveContingency'] ? 'Attached' : 'Not attached'],
                      ['Loan Contingency',       form['additionalFields.FinancingContingencyWaived'] ? 'Waived' : (fmtVal(form['room.offer.FinancingContingencyPeriod']) + ' days')],
                      ['Appraisal Contingency',  form['additionalFields.AppraisalContingency2'] ? 'Waived' : (fmtVal(form['room.offer.AppraisalContingencyPeriod']) + ' days')],
                      form['room.offer.appraisalAmount'] ? ['Custom Appraisal Amount', fmtAmt(form['room.offer.appraisalAmount'])] : null,
                      ['Investigation Period',   fmtVal(form['room.offer.NumberOfDaysAfterAcceptancePeriod2']) + ' days'],
                      form['room.offer.DocumentReviewContingencyDeadline'] ? ['Insurance Contingency', form['room.offer.DocumentReviewContingencyDeadline'] + ' days'] : null,
                      form['room.offer.NumberOfDaysAfterAcceptancePeriod4'] ? ['Seller Docs Review', form['room.offer.NumberOfDaysAfterAcceptancePeriod4'] + ' days'] : null,
                      form['room.offer.NumberOfDaysAfterAcceptancePeriod5'] ? ['Title Report Review', form['room.offer.NumberOfDaysAfterAcceptancePeriod5'] + ' days'] : null,
                      form['additionalFields.BuyerPropertyContingency'] ? ["Buyer's Property Contingency", 'Yes'] : null,
                      form['room.offer.InventoryConditionFormDeliveryPeriod'] ? ['Final Verification Days', form['room.offer.InventoryConditionFormDeliveryPeriod']] : null,
                    ]}
                  />

                  {/* Step 6 — Possession */}
                  <ReviewBlock
                    icon={IcoCal}
                    title="Possession"
                    rows={[
                      ['Possession',         possessionLabel],
                      form['additionalFields.OccupiedByOwner'] ? ['Seller Occupied (Short)', fmtVal(form['room.property.LengthofOccupancy']) + ' days (≤29)'] : null,
                      form['additionalFields.OccupiedByOwner2'] ? ['Seller Occupied (Long)', fmtVal(form['room.property.LengthofOccupancy2']) + ' days (≥30)'] : null,
                      form['additionalFields.TenantOccupiedPropertyAddendum'] ? ['Tenant Occupied', 'TOPA attached'] : null,
                    ]}
                  />

                  {/* Step 6 — Assignment & Document Deadlines (§K, §N) */}
                  <ReviewBlock
                    icon={IcoCal}
                    title="Assignment & Document Deadlines"
                    rows={[
                      form['room.offer.AssignablePeriod'] ? ['Assignment Request (K)', form['room.offer.AssignablePeriod'] + ' days'] : null,
                      form['room.offer.NumberOfDaysAfterAcceptancePeriod6'] ? ['Seller Doc Delivery (N1)', form['room.offer.NumberOfDaysAfterAcceptancePeriod6'] + ' days'] : null,
                      form['room.offer.NumberOfDaysAfterAcceptancePeriod7'] ? ['Escrow Provisions (N2)', form['room.offer.NumberOfDaysAfterAcceptancePeriod7'] + ' days'] : null,
                      form['room.offer.NumberOfDaysAfterAcceptancePeriod8'] ? ['HOA Doc Fees (N3)', form['room.offer.NumberOfDaysAfterAcceptancePeriod8'] + ' days'] : null,
                      form['room.offer.NumberOfDaysAfterAcceptancePeriod9'] ? ['Smoke/CO Compliance (N4)', form['room.offer.NumberOfDaysAfterAcceptancePeriod9'] + ' days'] : null,
                    ]}
                  />

                  {/* Step 7 — Items Included / Excluded */}
                  <ReviewBlock
                    icon={IcoList}
                    title="Items Included / Excluded"
                    rows={[
                      ['Appliances Included',   applianceList],
                      form['room.property.details.fixtureInclusion'] ? ['Additional Included', form['room.property.details.fixtureInclusion']] : null,
                      form['room.property.details.fixtureInclusion2'] ? ['Additional Included (2)', form['room.property.details.fixtureInclusion2']] : null,
                      form['room.property.details.fixtureInclusion3'] ? ['Additional Included (3)', form['room.property.details.fixtureInclusion3']] : null,
                      form['room.property.details.fixtureInclusion4'] ? ['Additional Included (4)', form['room.property.details.fixtureInclusion4']] : null,
                      form['room.property.details.fixtureInclusion5'] ? ['Additional Included (5)', form['room.property.details.fixtureInclusion5']] : null,
                      form['room.property.details.fixtureExclusion'] ? ['Items Excluded', form['room.property.details.fixtureExclusion']] : null,
                      form['room.property.details.fixtureExclusion2'] ? ['Items Excluded (2)', form['room.property.details.fixtureExclusion2']] : null,
                      form['room.property.details.fixtureExclusion3'] ? ['Items Excluded (3)', form['room.property.details.fixtureExclusion3']] : null,
                    ]}
                  />

                  {/* Step 8 — Cost Allocation */}
                  <ReviewBlock
                    icon={IcoTag}
                    title="Cost Allocation"
                    rows={[
                      form['additionalFields.PayableByBuyerOrSeller'] ? ['NHZ / Env. Report (Q1)', fmtVal(form['additionalFields.PayableByBuyerOrSeller'])] : null,
                      form['additionalFields.PayableByBuyerOrSeller2'] ? ['Govt Inspection (Q2)', fmtVal(form['additionalFields.PayableByBuyerOrSeller2'])] : null,
                      form['additionalFields.PayableByBuyerOrSeller3'] ? ['Smoke/CO Detector (Q3A)', fmtVal(form['additionalFields.PayableByBuyerOrSeller3'])] : null,
                      form['additionalFields.PayableByBuyerOrSeller4'] ? ['Smoke/CO Retrofit (Q3B)', fmtVal(form['additionalFields.PayableByBuyerOrSeller4'])] : null,
                      form['additionalFields.PayCostOfPaymentEscrow'] ? ['Govt Corrective Actions (Q6)', fmtVal(form['additionalFields.PayCostOfPaymentEscrow'])] : null,
                      form['additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer'] ? ['Escrow Fee (Q7)', fmtVal(form['additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer'])] : null,
                      form['room.offer.EscrowFees'] ? ['Escrow Fee — Payer/Split (Q7)', form['room.offer.EscrowFees']] : null,
                      form['additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer2'] ? ['Title Insurance (Q8)', fmtVal(form['additionalFields.PolicyOfTitleInsurancePaidBySellerOrBuyer2'])] : null,
                      form['additionalFields.TransferTaxesPaidByBuyerOrSeller'] ? ['County Transfer Tax (Q10)', fmtVal(form['additionalFields.TransferTaxesPaidByBuyerOrSeller'])] : null,
                      form['additionalFields.TransferTaxesPaidByBuyerOrSeller2'] ? ['City Transfer Tax (Q11)', fmtVal(form['additionalFields.TransferTaxesPaidByBuyerOrSeller2'])] : null,
                      form['additionalFields.TransferFeesPaidforBy'] ? ['HOA Transfer Fee (Q14)', fmtVal(form['additionalFields.TransferFeesPaidforBy'])] : null,
                      form['room.property.HomeownerAssociationTransferFee'] ? ['HOA Transfer Amount', fmtAmt(form['room.property.HomeownerAssociationTransferFee'])] : null,
                      form['room.offer.OtherFeesPaidByDescription10'] ? ['Govt Inspections (Q5)', fmtVal(form['room.offer.OtherFeesPaidByDescription10'])] : null,
                      form['room.offer.OtherFeesPaidByDescription11'] ? ['Govt Corrective Actions (Q6)', fmtVal(form['room.offer.OtherFeesPaidByDescription11'])] : null,
                      form['room.offer.PrivateTransferFeeObligation'] ? ['Private Transfer Fees (Q15)', form['room.offer.PrivateTransferFeeObligation']] : null,
                      form['additionalFields.WellSepticSystemEvaluation'] ? ['Septic/Well/Boundaries (Q16)', 'Included — see SWPI-C'] : null,
                      form['room.offer.BrokerServiceOtherFee'] ? ['Custom Fee A', form['room.offer.BrokerServiceOtherFee'] + (form['room.offer.OtherFeesPaidByDescription14'] ? ' — ' + form['room.offer.OtherFeesPaidByDescription14'] : '')] : null,
                      form['room.offer.BrokerServiceOtherFee2'] ? ['Custom Fee B', form['room.offer.BrokerServiceOtherFee2'] + (form['room.offer.OtherFeesPaidByDescription16'] ? ' — ' + form['room.offer.OtherFeesPaidByDescription16'] : '')] : null,
                      !form['additionalFields.DeclineHomeWarrantyProgram'] && form['room.service.HomeWarrantyTypeOfCoverage'] ? ['Home Warranty Plan', form['room.service.HomeWarrantyTypeOfCoverage']] : null,
                      !form['additionalFields.DeclineHomeWarrantyProgram'] && form['additionalFields.PayableByBuyerOrSeller8'] ? ['Home Warranty Paid By', fmtVal(form['additionalFields.PayableByBuyerOrSeller8'])] : null,
                      form['additionalFields.DeclineHomeWarrantyProgram'] ? ['Home Warranty', 'Declined'] : null,
                    ]}
                  />

                  {/* Step 8 — Other Terms */}
                  {(form['room.offer.OtherTerms'] || form['room.offer.OtherTerms2'] || form['room.offer.OtherTerms3'] || form['room.offer.OtherTerms4'] || form['room.offer.OtherTerms5']) && (
                    <ReviewBlock
                      icon={IcoFile}
                      title="Other Terms"
                      rows={[
                        form['room.offer.OtherTerms']  ? ['R1', form['room.offer.OtherTerms']]  : null,
                        form['room.offer.OtherTerms2'] ? ['R2', form['room.offer.OtherTerms2']] : null,
                        form['room.offer.OtherTerms3'] ? ['R3', form['room.offer.OtherTerms3']] : null,
                        form['room.offer.OtherTerms4'] ? ['R4', form['room.offer.OtherTerms4']] : null,
                        form['room.offer.OtherTerms5'] ? ['R5', form['room.offer.OtherTerms5']] : null,
                      ]}
                    />
                  )}

                  {/* Step 9 — Addenda & Advisories */}
                  <ReviewBlock
                    icon={IcoFile}
                    title="Addenda & Advisories"
                    rows={[
                      ['Addenda',      addendaList],
                      form['additionalFields.OtherAddenda6'] ? ['Other Property-Type Addendum', fmtVal(form['room.offer.AddendumsAttached'])] : null,
                      form['room.offer.addendumNumbersCARFormADM'] ? ['ADM Addendum #', form['room.offer.addendumNumbersCARFormADM']] : null,
                      form['additionalFields.OtherAddenda13'] && form['room.offer.AddendumsAttached2'] ? ['Custom Addendum 1', form['room.offer.AddendumsAttached2']] : null,
                      form['additionalFields.OtherAddenda15'] && form['room.offer.AddendumsAttached3'] ? ['Custom Addendum 2', form['room.offer.AddendumsAttached3']] : null,
                      ['Advisories',   advisoriesList],
                      form['room.offer.OtherAdvisories'] ? ['Other Advisory', form['room.offer.OtherAdvisories']] : null,
                      form['room.offer.OtherAdvisories2'] ? ['Other Advisory (2)', form['room.offer.OtherAdvisories2']] : null,
                    ]}
                  />

                  {/* Step 10 — Entity Seller */}
                  {form['additionalFields.EntitySellers'] && (
                    <ReviewBlock
                      icon={IcoPeople}
                      title="Entity Seller"
                      rows={[
                        ['Entity Name',          fmtVal(form['room.offer.PropertyCaseNumber3'])],
                        form['room.details.AuthorizedSignerName3'] ? ['Auth Signer 1', form['room.details.AuthorizedSignerName3']] : null,
                        form['room.details.SellerEntityTitle'] ? ['Auth Signer 1 Title', form['room.details.SellerEntityTitle']] : null,
                        form['room.details.AuthorizedSignerName4'] ? ['Auth Signer 2', form['room.details.AuthorizedSignerName4']] : null,
                        form['room.details.SellerEntityTitle2'] ? ['Auth Signer 2 Title', form['room.details.SellerEntityTitle2']] : null,
                        form['additionalFields.AdditionalSignature2'] ? ['Additional Signature Addendum (ASA)', 'Attached'] : null,
                      ]}
                    />
                  )}

                  {/* Step 10 — Real Estate Brokers: Delivery & Representation */}
                  <ReviewBlock
                    icon={IcoBriefcase}
                    title="Real Estate Brokers — Delivery & Representation"
                    rows={[
                      form['additionalFields.EmailDeliveryOfDocumentMethod'] ? ['Buyer DEDA', fmtVal(form['room.details.DocumentDeliveryMethodLabel'])] : null,
                      form['additionalFields.MultipleRepresentationWithDesignatedAgency2'] ? ['Buyer Multiple Agents', 'Yes'] : null,
                      form['additionalFields.MultipleRepresentationWithDesignatedAgency3'] ? ['Buyer Multiple Brokers', 'Yes'] : null,
                      form['additionalFields.EmailDeliveryOfDocumentMethod2'] ? ['Seller DEDA', fmtVal(form['room.details.DocumentDeliveryMethodLabel2'])] : null,
                      form['additionalFields.MultipleRepresentationWithDesignatedAgency4'] ? ['Seller Multiple Agents', 'Yes'] : null,
                      form['additionalFields.MultipleRepresentationWithDesignatedAgency5'] ? ['Seller Multiple Brokers', 'Yes'] : null,
                    ]}
                  />

                  {/* Step 10 — Escrow Holder */}
                  <ReviewBlock
                    icon={IcoCal}
                    title="Escrow Holder"
                    rows={[
                      ['Escrow Company',  fmtVal(form['room.offer.entityHoldingEarnestMoney'])],
                      form['room.details.escrowCompany.number'] ? ['Escrow Number', form['room.details.escrowCompany.number']] : null,
                      ['Address',         [form['room.details.escrowCompany.address'], form['room.details.escrowCompany.city'], form['room.details.escrowCompany.state'], form['room.details.escrowCompany.postalCode']].filter(Boolean).join(', ') || '—'],
                      form['room.details.escrowOfficer.email'] ? ['Email', form['room.details.escrowOfficer.email']] : null,
                      form['room.details.escrowOfficer.cellPhone'] ? ['Phone', form['room.details.escrowOfficer.cellPhone']] : null,
                      form['room.details.escrowOfficer.LicenseNumber'] ? ['License #', form['room.details.escrowOfficer.LicenseNumber']] : null,
                      escrowLicensedBy ? ['Licensed By', escrowLicensedBy] : null,
                      form['additionalFields.depositEarnestMoney'] && form['room.offer.finalearnestmoneyAmount'] ? ['Deposit Received', fmtVal(form['room.offer.finalearnestmoneyAmount'])] : null,
                      form['room.offer.CounterofferNumber639'] ? ['Counter Offer #1', form['room.offer.CounterofferNumber639']] : null,
                      form['room.offer.CounterofferNumber'] ? ['Counter Offer #2', form['room.offer.CounterofferNumber']] : null,
                      form['room.offer.bindingDate'] ? ['Acceptance Date', form['room.offer.bindingDate']] : null,
                      form['room.offer.EscrowAgentNotifedByName'] ? ['Advised By', form['room.offer.EscrowAgentNotifedByName']] : null,
                    ]}
                  />

                  <div className="rpa-confirm-box" style={{ marginTop: 24 }}>
                    <label className="rpa-confirm-label">
                      <input
                        type="checkbox"
                        className="rpa-confirm-check"
                        checked={confirmed}
                        onChange={e => setConfirmed(e.target.checked)}
                      />
                      <span>
                        I confirm all information above is accurate and authorize submission of the California Residential Purchase Agreement (C.A.R. Form RPA Rev. 12/25) based on these terms.
                      </span>
                    </label>
                  </div>
                </div>
              );
            })()}

            </div>{/* /rpa-step-content */}
          </div>{/* /rpa-content */}
        </div>{/* /rpa-body */}

        {/* ── Footer ── */}
        <div className="rpa-footer">
          <div className="rpa-footer-left">
            {step > 1 && (
              <button className="rpa-btn-ghost" onClick={handleBack} disabled={isSaving}>
                ← Back
              </button>
            )}
          </div>
          <div className="rpa-footer-center">
            {saveError ? (
              <span className="rpa-footer-save-error">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {saveError}
              </span>
            ) : (
              <span className="rpa-footer-count">Step {step} of 11</span>
            )}
          </div>
          <div className="rpa-footer-right">
            {step < 11
              ? <button
                  className={isSaving ? 'rpa-btn-saving' : 'rpa-btn-primary'}
                  onClick={handleContinue}
                  disabled={isSaving}
                >
                  {isSaving ? <><span className="rpa-btn-spinner" /> Saving…</> : 'Continue →'}
                </button>
              : <button
                  className={isSaving ? 'rpa-btn-saving' : confirmed ? 'rpa-btn-primary' : 'rpa-btn-primary'}
                  onClick={handleSubmit}
                  disabled={!confirmed || isSaving}
                  style={!confirmed && !isSaving ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  {isSaving ? <><span className="rpa-btn-spinner" /> Generating…</> : (envelopeId ? 'Update →' : 'Submit →')}
                </button>
            }
          </div>
        </div>

      </div>
    </div>
  );
}

// ── Small helper components ────────────────────────────────────────────────────

function SectionCard({ num, title, note, children }) {
  return (
    <div className="rpa-card">
      {title && (
        <div className="rpa-card-header">
          {title}
          {note && <span className="rpa-card-note">{note}</span>}
        </div>
      )}
      <div className="rpa-card-body">{children}</div>
    </div>
  );
}

function AgencyParty({ title, children }) {
  return (
    <div className="rpa-agency-party">
      <div className="rpa-agency-party-header">{title}</div>
      <div className="rpa-agency-party-body">{children}</div>
    </div>
  );
}

function Fld({ label, hint, children }) {
  return (
    <div className="rpa-field">
      {label && <label className="rpa-field-label">{label}</label>}
      {children}
    </div>
  );
}

function Hr() {
  return <hr style={{ border: 'none', borderTop: '1px solid #e8e8ed', margin: '12px 0' }} />;
}

function StateSelect({ stateKey, cityKey, form, set }) {
  return (
    <select
      className="rpa-input"
      value={form[stateKey] || ''}
      onChange={e => {
        set(stateKey, e.target.value);
        if (cityKey) set(cityKey, '');
      }}
    >
      <option value="">State</option>
      {stateCityData.map(s => (
        <option key={s.stateCode} value={s.stateCode}>{s.stateCode} — {s.state}</option>
      ))}
    </select>
  );
}

const WIZARD_CITY_STYLES = {
  control: (base, state) => ({
    ...base,
    border: state.isFocused ? '1px solid #5B47D6' : '1px solid #E8E8ED',
    borderRadius: 8,
    boxShadow: state.isFocused ? '0 0 0 3px rgba(91,71,214,0.10)' : 'none',
    minHeight: 40,
    fontSize: 14,
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    color: '#2B1055',
    background: '#ffffff',
    cursor: 'pointer',
    '&:hover': { borderColor: '#C7C7CC' },
  }),
  valueContainer: (base) => ({ ...base, padding: '0 14px' }),
  singleValue: (base) => ({ ...base, color: '#2B1055', fontSize: 14, fontFamily: "'Inter', sans-serif" }),
  placeholder: (base) => ({ ...base, color: '#999999', fontSize: 14 }),
  input: (base) => ({ ...base, color: '#2B1055', fontSize: 14 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 8px', color: '#999' }),
  clearIndicator: (base) => ({ ...base, padding: '0 4px', color: '#999', '&:hover': { color: '#ef4444' } }),
  menu: (base) => ({
    ...base, borderRadius: 8, border: '1px solid #E8E8ED',
    boxShadow: '0 4px 16px rgba(45,34,112,0.10)', zIndex: 100,
  }),
  menuList: (base) => ({ ...base, padding: 4 }),
  option: (base, state) => ({
    ...base,
    background: state.isSelected ? '#5B47D6' : state.isFocused ? '#EDE9FE' : 'transparent',
    color: state.isSelected ? '#fff' : '#2B1055',
    fontSize: 14, padding: '8px 14px', borderRadius: 6, cursor: 'pointer',
  }),
};

function WizardCitySelect({ cityKey, stateKey, form, set }) {
  const stateCode = form[stateKey] || '';
  const stateEntry = stateCityData.find(s => s.stateCode === stateCode);
  const options = stateEntry
    ? stateEntry.cities.split(',').map(c => ({ value: c.trim(), label: c.trim() }))
    : [];
  const value = form[cityKey] ? { value: form[cityKey], label: form[cityKey] } : null;
  return (
    <CreatableSelect
      styles={WIZARD_CITY_STYLES}
      value={value}
      onChange={opt => set(cityKey, opt ? opt.value : '')}
      options={options}
      isDisabled={!stateCode}
      isClearable
      placeholder={stateCode ? 'Search or type city…' : 'Select state first'}
      formatCreateLabel={v => `Add "${v}"`}
      noOptionsMessage={() => 'No cities found — type to add'}
    />
  );
}

function DivLabel({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0 10px' }}>
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', whiteSpace: 'nowrap' }}>{children}</span>
      <div style={{ flex: 1, borderTop: '1px solid #e8e8ed' }} />
    </div>
  );
}

function RadioGroup({ value, onChange, options, inline }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flexDirection: inline ? 'row' : 'column' }}>
      {options.map(opt => (
        <label key={String(opt.value)} style={{
          display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
          padding: '7px 11px',
          border: `1.5px solid ${value === opt.value ? '#5B47D6' : '#E8E8ED'}`,
          borderRadius: 7,
          background: value === opt.value ? '#EDE9FE' : '#FAFAF8',
          fontSize: 13,
          flex: inline ? '1 1 auto' : undefined,
          transition: 'border-color 0.12s, background 0.12s',
        }}>
          <input type="radio" checked={value === opt.value} onChange={() => onChange(opt.value)}
            style={{ accentColor: '#5B47D6', width: 15, height: 15, margin: 0, padding: 0, cursor: 'pointer' }} />
          <span>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function CheckRow({ checked, onChange, label, hint }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer',
      padding: '8px 11px',
      border: `1.5px solid ${checked ? '#5B47D6' : '#E8E8ED'}`,
      borderRadius: 7,
      background: checked ? '#EDE9FE' : '#FAFAF8',
      fontSize: 13,
      marginBottom: 8,
      transition: 'border-color 0.12s, background 0.12s',
    }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ accentColor: '#5B47D6', width: 15, height: 15, margin: 0, padding: 0, marginTop: 2, cursor: 'pointer' }} />
      <div>
        <span>{label}</span>
      </div>
    </label>
  );
}
